import type { BonDruck } from '../bonweg';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';

import {
  ApiFehler,
  PLATTFORM_NAME,
  type UmsatzPlattform,
  type BelegAntwort,
  type BedienerKurz,
  type BelegAuftrag,
  type KassenApi,
  type KundeKurz,
  type OffenerBon,
  type Zahlung,
} from '../api';
import {
  aktuelleKennung,
  markiereZaehlerUnsicher,
  vergebeBelegnummer,
  ZaehlerstandUnsicher,
} from '../belegnummern';
import { SchluesselBelegt, type GeraeteSpeicher } from '../geraetespeicher';
import {
  brauchtBezahlentscheidung,
  gibReservierungFrei,
  halteBezahlungFest,
  klaereAltbestandsBon,
  klaereOffenenVorgang,
  klaereVorgangOhneDatensatz,
  ladeReservierung,
  ladeReservierungsAusgang,
  ladeVorgang,
  mitKassiersperre,
  offeneVorgaenge,
  offlineBelegAusVorgang,
  pruefeKeinOffenerVorgang,
  reserviereKassieren,
  schliesseVorgangAb,
  sichereVorgang,
  VerkaufUngeklaert,
  type Bezahlentscheidung,
  type Kassierreservierung,
  type ReservierungsAusgang,
  type VorgangsDatensatz,
  type VorgangsKlaerung,
} from '../vorgang';
import {
  feldEingabeDaten,
  FeldScanErkennung,
  istNachlauf,
  kurzcodeHinweis,
  ladeScannerPraefix,
  PRAEFIX_ABSTAND_MS,
  ScanErkennung,
  type FeldVerworfen,
  type GehalteneTaste,
} from '../scanner';
import { istArtikelcodeImStamm, stammCodeSuche } from '../artikelcode';
import { DoppelScanFilter } from '../barcode';
import { feldScanSperre, gesperrterScan, scanSperre } from '../scan-verkauf';
import {
  KEIN_EMPFAENGER,
  KLEINBETRAG_GRENZE_CENT,
  UID_EMPFAENGER_GRENZE_CENT,
  empfaengerFuer,
} from '../rechnung';
import {
  istNetzfehler,
  istPuffergeeignet,
  offlineBelegText,
  offlinePufferBereit,
  type OfflineBeleg,
  type OfflinePuffer,
} from '../offline';
import { OFFLINE_ERWEITERT } from '../offline-schalter';
import { meldeSpeicherfehler, speicherJetztDauerhaft } from '../speicher-persistenz';
import type { SyncDienst } from '../sync';
import {
  baueCodeIndex,
  einzelpreis,
  inBon,
  konfigAusStamm,
  aktuelleSchnellwahl,
  ladeStammCacheAsync,
  mengeImBon,
  mengeSetzen,
  positionMitMenge,
  speichereStammCache,
  vorgabeMenge,
  type GesperrteKachel,
  type Schnellwahl,
  type SchnellwahlKonfig,
  sucheKacheln,
} from '../schnellwahl';
import {
  gewichtsText,
  istGewichtsPosition,
  waehleArtikel,
  type WiegeAnfrage,
} from '../gewichtsverkauf';
import { stammAblage } from '../stamm-speicher';
import {
  aendereMitRabatt,
  istRabatt,
  mitRabatt,
  nachKundenwechsel,
  ohneRabatt,
  RABATT_STUFEN,
  rabattFuerNeuePosition,
  rabattProzent,
} from '../rabatt';
import { holeBon, ladeTische, naechsterTischname, parkeBon, type GeparkterBon } from '../tische';
import { KundenanzeigeSender, type Zahlungsanzeige } from '../kundenanzeige';
import { ALLE_MODULE, type Module } from '../module';
import {
  bonierungAbgelehnt,
  abziehen,
  bonierDiff,
  ladeNachsendeListe,
  nachsenden,
  neueVorgangsId,
  verbuche,
  vormerken,
  type BonierStand,
  type OffeneBonierung,
} from '../bonierung';
import {
  eigenerVorgangsteil,
  ladeBon,
  ladeKartenstart,
  ladeKartenstartVorgang,
  merkeKartenstart,
  sitzungMitDauerhaft,
  speichereBon,
  type GesicherterBon,
} from '../bon-sicherung';
import { TischplanAnsicht, type TischBelegung } from './TischplanAnsicht';
import { passePositionAn } from '../preisanpassung';
import { zahlungsKandidaten } from '../zahlung-kandidaten';
import { anteilCent, auswahlSetzen, restNachZahlung, teilbar, teileBon } from '../teilen';
import { trinkgeldVorschlaege } from '../trinkgeld';
import { formatiereEuro, summeBrutto, type Position } from '../warenkorb';
import {
  bereinigeGutscheine,
  einzweckEinloesungsposition,
  gutscheinZahlungCent,
  restNachGutschein,
  verkaufsposition,
  type GutscheinAusgabe,
  type GutscheinEinloesung,
} from '../gutschein';
import { GutscheinEinloesenDialog, GutscheinVerkaufDialog } from './GutscheinDialog';
import {
  Bildschirmtastatur,
  TastaturKnopf,
  TastaturWarnzeile,
  VerkaufsTastatur,
  istTastaturEsc,
  useBildschirmtastatur,
} from './Bildschirmtastatur';
import { verdichteWarnungen } from '../bildschirmtastatur';
import { BarDialog } from './BarDialog';
import { rueckgeldCent } from '../rueckgeld';
import {
  ABMELDUNG_EREIGNIS,
  aktuelleRechte,
  initialen,
  ladeAnmeldung,
  ladeBediener,
  setzeAktivenBediener,
  speichereAnmeldung,
  speichereBediener,
  speichereVerwaltet,
  istVerwaltet,
  type AngemeldeterBediener,
} from '../bediener';
import { BedienerAnmeldung } from './BedienerAnmeldung';
import { Belegansicht } from './Belegansicht';
import { SchnellwahlKacheln, WarengruppenLeiste } from './Schnellwahl';
import { ZahlungDialog } from './ZahlungDialog';
import { KartenStatusV2 } from './zahlung-v2/KartenStatusV2';
import type { KartenStatusArt } from './zahlung-v2/logik';
import { zahlungsdialogV2Aktiv } from './zahlung-v2/schalter';
import { useFokusfalleAmFenster } from './zahlung-v2/fokus';
import { FunktionsSymbol, FunktionsTaste, type FunktionsIcon } from './FunktionsTaste';
import { Rueckfrage } from './Rueckfrage';
import { PlattformDialog } from './PlattformDialog';
import { Gewichtsdialog } from './Gewichtsdialog';
import { PfandDialog } from './PfandDialog';
import { ScanAuswahlDialog } from './ScanAuswahlDialog';
import { Ziffernblock, ZiffernTasten } from './Ziffernblock';
import { tastaturZuZiffer, ziffernTaste } from '../ziffern';
import { ladeAutoDruck, oeffneLade, type GeraeteRueckmeldung } from '../bondruck';
import { schluesselLadeBeleg, schluesselLadeManuell } from '../geraet';
import { istUngeklaertMeldung, ungeklaertErledigt } from '../abgleich-lauf';

/** Vom Scanner-Handler selbst ausgelöste Tastendrücke (Freigabe) – nicht noch einmal prüfen. */
const WEITERGELEITET = new WeakSet<Event>();

/**
 * Zeitpunkt des Tastendrucks (PB-1, Prüfung R1 M4): der Zeitstempel des Ereignisses, nicht der
 * Verarbeitung – hängt die Seite kurz, kommen getippte Tasten sonst als „schneller Schwall“
 * an und sähen aus wie ein Scan. Unplausible Zeitstempel → Verarbeitungszeit.
 */
function tastenZeit(e: Event): number {
  const jetzt = performance.now();
  return e.timeStamp > 0 && e.timeStamp <= jetzt + 1 ? e.timeStamp : jetzt;
}

/** Feldinhalt setzen, sodass React die Änderung sieht (nativer Setter + input-Ereignis). */
function setzeFeldwert(feld: HTMLInputElement | HTMLTextAreaElement, wert: string): void {
  const prototyp =
    feld instanceof HTMLInputElement ? HTMLInputElement.prototype : HTMLTextAreaElement.prototype;
  Object.getOwnPropertyDescriptor(prototyp, 'value')?.set?.call(feld, wert);
  feld.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * Artikelkennung einer Bonzeile (ohne Preis und Menge): sichert die per Zeilennummer
 * gewählte Zeile ab – verschiebt sich die Liste, passt die Kennung nicht mehr und die
 * Auswahl fällt weg, statt eine andere Position zu bearbeiten.
 */
function zeilenKennung(p: Position): string {
  return `${p.bezeichnung}|${p.steuersatz}|${String(p.artikelId ?? '')}`;
}

/** Zeile wiedererkennen (Hervorhebung) – gleiche Merkmale wie beim Zusammenfassen. */
function zeilenSchluessel(p: Position): string {
  // Mit Zeilenrabatt den Preis vor dem Nachlass nehmen: der gerundete Einzelpreis
  // kann sich mit der Menge um einen Cent verschieben.
  const preis =
    p.zeilenNachlass === undefined
      ? String(Math.round(einzelpreis(p)))
      : `${String(p.zeilenNachlass.basisCent)}-${String(p.zeilenNachlass.prozent)}`;
  // Gewichtszeilen werden nie zusammengefasst: das Gewicht gehört zur Kennung
  const gewicht = p.einheit === undefined ? '' : `|${String(p.menge)}${p.einheit}`;
  return `${p.bezeichnung}|${p.steuersatz}|${String(p.artikelId ?? '')}|${preis}${gewicht}`;
}

/** Wie lange die zuletzt hinzugefügte Zeile hervorgehoben bleibt (POS V2, N11). */
const HERVORHEBUNG_MS = 2000;

/**
 * Verkauf für den Touchscreen (POS V2, freigegebener Rahmen D1 v2): links die
 * Funktionsleiste, daneben der Bon mit Kundenzeile, Positionen und Summenbereich,
 * rechts Suche, Warengruppen, Kacheln, Ziffernblock auf Abruf und die Zahlleiste
 * (Bar links, Karte rechts). Der Beleg entsteht erst, wenn die Zahlung bestätigt
 * ist (Regel 3).
 */
/** P1-13: Kartenstart-Merker in Sitzung und dauerhaft (überlebt eine neue Browsersitzung). */
function kartenstartSpeicher() {
  return sitzungMitDauerhaft(sessionStorage, localStorage, (ok) => {
    meldeSpeicherfehler('vorgang', !ok);
  });
}

export function Verkauf(props: {
  api: KassenApi;
  kassenId: string;
  puffer: OfflinePuffer;
  /** N1: Gerätespeicher (gemeinsamer Belegnummernzähler, Vorgangsdatensätze). */
  geraeteSpeicher: GeraeteSpeicher;
  /**
   * N1: Verkaufsstopp (z. B. blockiertes Speicher-Upgrade, veraltetes Fenster): Text der
   * Meldung; solange gesetzt, entsteht kein Beleg.
   */
  verkaufsstopp?: string | null;
  /** N1.11: Puffern nur bei gewährtem dauerhaftem Speicher (navigator.storage.persist). */
  speicherDauerhaft?: boolean;
  unternehmer: { name: string; anschrift: string; uid?: string } | null;
  onOffline: () => void;
  bridgeDruck?: BonDruck;
  /** Kassenlade über die native Bridge der Android-Hülle (Schlüssel je Vorgang); sonst Server-Weg. */
  ladeAmGeraet?: (schluessel: string) => Promise<GeraeteRueckmeldung>;
  emailSenden?: (belegnummer: string, an: string, form?: 'bon' | 'rechnung') => Promise<string>;
  /** Gebuchte Module; nicht gebuchte Funktionen sind ausgeblendet (Standard: alle). */
  module?: Module;
  /** Positionen aus dem Terminbuch, die in den Bon sollen. */
  vorbelegung?: Position[];
  onVorbelegungUebernommen?: () => void;
  /** Bediener-Chip im Kopf wurde angetippt: Bedienerauswahl öffnen. */
  bedienerWahl?: boolean;
  onBedienerWahlGezeigt?: () => void;
  /**
   * App-weite Synchronisierung (P8): Stamm und Küchenbon-Nachsendung laufen im Hintergrund;
   * der Verkauf übernimmt nur noch deren Stand. Ohne: bisheriges Laden im Verkauf.
   */
  sync?: SyncDienst;
  /**
   * A5-3: steigt nach einem Abgleich, der Belege nachsigniert bzw. keine ungeklärten Verkäufe
   * mehr gefunden hat – eine angezeigte „noch ungeklärt“-Meldung wird dann neu geprüft.
   */
  abgleichNr?: number;
}) {
  const { api, kassenId, puffer, geraeteSpeicher } = props;
  const modul = props.module ?? ALLE_MODULE;
  const [offlineBeleg, setOfflineBeleg] = useState<OfflineBeleg | null>(null);
  // Offener Bon aus der Sitzung (Reiterwechsel baut den Verkauf ab, der Bon bleibt).
  // P1-13: ohne Sitzungsstand der dauerhaft gesicherte ungeklärte Vorgang (neue Sitzung)
  const [gesichert] = useState(() => ladeBon(sessionStorage, kassenId, localStorage));
  const [positionen, setPositionen] = useState<Position[]>(
    () => (gesichert?.positionen ?? []) as Position[],
  );
  /** Rechnung teilen: nur die ausgewählten Mengen werden jetzt kassiert, der Rest bleibt im Bon. */
  const [teilen, setTeilen] = useState(false);
  const [auswahl, setAuswahl] = useState<Map<number, number>>(new Map());
  // Schnellwahl: Serverstamm (Cache für offline), sonst die Konfiguration dieses Geräts.
  const [konfig, setKonfig] = useState<SchnellwahlKonfig>(() =>
    aktuelleSchnellwahl(localStorage, kassenId),
  );
  const [gruppe, setGruppe] = useState<string | null>(() => konfig.gruppen[0]?.name ?? null);
  // Sicherheitspaket S3: Der Server prüft Preise gegen den Artikelstamm – die Kasse lädt ihn
  // deshalb alle fünf Minuten und nach einer gemeldeten Preisabweichung neu.
  const [stammStand, setStammStand] = useState(0);
  const sync = props.sync;
  /** Stamm neu laden: über die Synchronisierung, sonst selbst. */
  const stammNeuLaden = useCallback(() => {
    if (sync === undefined) {
      setStammStand((n) => n + 1);
    } else {
      void sync.anstossen('preisabweichung');
    }
  }, [sync]);
  useEffect(() => {
    if (sync === undefined) {
      return;
    }
    // Neuer Stand aus der Synchronisierung: Schnellwahl übernehmen. Der offene Bon bleibt
    // unverändert; veraltete Preise weist der Server vor dem Zahlungsstart ab (S-5).
    const neu = (e: Event): void => {
      if ((e as CustomEvent<{ kassenId?: string }>).detail.kassenId !== kassenId) {
        return;
      }
      stammVomServer.current = true;
      const k = aktuelleSchnellwahl(localStorage, kassenId);
      setKonfig(k);
      setGruppe((g) =>
        g !== null && k.gruppen.some((x) => x.name === g) ? g : (k.gruppen[0]?.name ?? null),
      );
    };
    window.addEventListener('bilanx:stamm', neu);
    return () => {
      window.removeEventListener('bilanx:stamm', neu);
    };
  }, [sync, kassenId]);
  useEffect(() => {
    if (sync !== undefined) {
      return; // die Synchronisierung prüft alle 60 s und auf Ereignis
    }
    const timer = setInterval(() => {
      setStammStand((n) => n + 1);
    }, 5 * 60_000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  // Großer Stamm (Artikelimport): liegt nur in IndexedDB – beim Start (auch offline) von dort
  // laden, solange der Server noch nicht geantwortet hat.
  const stammVomServer = useRef(false);
  useEffect(() => {
    let aktiv = true;
    void ladeStammCacheAsync(stammAblage(), kassenId, localStorage).then((c) => {
      if (aktiv && c !== null && !stammVomServer.current) {
        setKonfig(c.konfig);
        setGruppe((g) =>
          g !== null && c.konfig.gruppen.some((x) => x.name === g)
            ? g
            : (c.konfig.gruppen[0]?.name ?? null),
        );
      }
    });
    return () => {
      aktiv = false;
    };
  }, [kassenId]);
  useEffect(() => {
    if (sync !== undefined) {
      return;
    }
    let aktiv = true;
    api
      .artikelstamm(kassenId)
      .then((stamm) => {
        const neu = konfigAusStamm(stamm);
        stammVomServer.current = true;
        speichereStammCache(
          localStorage,
          kassenId,
          { version: stamm.version, konfig: neu },
          stammAblage(),
        );
        if (aktiv) {
          setKonfig(neu);
          setGruppe((g) =>
            g !== null && neu.gruppen.some((x) => x.name === g)
              ? g
              : (neu.gruppen[0]?.name ?? null),
          );
        }
      })
      .catch(() => {
        // 404 (kein Stamm) oder offline: Cache bzw. Gerätekonfiguration bleiben.
      });
    return () => {
      aktiv = false;
    };
  }, [api, kassenId, stammStand, sync]);
  // PB-1: Code-Index nach jedem Stammwechsel im Leerlauf bauen, nicht beim ersten Scan (R1, H2)
  useEffect(() => {
    const timer = setTimeout(() => {
      baueCodeIndex(konfig.kacheln, konfig.gesperrt);
    }, 0);
    return () => {
      clearTimeout(timer);
    };
  }, [konfig]);
  /** Artikel-/EAN-Suche über den Kacheln (leer = Gruppenansicht). */
  const [suche, setSuche] = useState('');
  /** BT v3: Bildschirmtastatur der Verkaufsfläche (Artikelsuche, Belegnotiz); Standardziel Suche. */
  const bt = useBildschirmtastatur({ standardZiel: 'suche' });
  const verkaufRaster = useRef<HTMLDivElement | null>(null);
  /** Aufklappbare Bereiche im Bon (Funktionsleiste links). */
  const [notizOffen, setNotizOffen] = useState(false);
  const [bedienerOffen, setBedienerOffen] = useState(false);
  const { bedienerWahl, onBedienerWahlGezeigt } = props;
  useEffect(() => {
    if (bedienerWahl === true) {
      setBedienerOffen(true);
      onBedienerWahlGezeigt?.();
    }
  }, [bedienerWahl, onBedienerWahlGezeigt]);
  /** Menü „Mehr“ der Funktionsleiste und „Weitere“ (⋯) der Zahlleiste. */
  const [mehrOffen, setMehrOffen] = useState(false);
  const [weitereOffen, setWeitereOffen] = useState(false);
  useEffect(() => {
    if (!mehrOffen && !weitereOffen) {
      return;
    }
    // Antippen außerhalb schließt das Menü
    const beiZeiger = (e: PointerEvent): void => {
      const ziel = e.target instanceof Element ? e.target : null;
      if (ziel?.closest('.mehr-bereich') == null) {
        setMehrOffen(false);
      }
      if (ziel?.closest('.weitere-bereich') == null) {
        setWeitereOffen(false);
      }
    };
    document.addEventListener('pointerdown', beiZeiger);
    return () => {
      document.removeEventListener('pointerdown', beiZeiger);
    };
  }, [mehrOffen, weitereOffen]);
  /** Ziffernblock („Menge / Betrag“) auf Abruf im Artikelbereich. */
  const [ziffernOffen, setZiffernOffen] = useState(false);
  /** Gewählte Bonzeile (zeigt Menge ±, Preis ändern, Entfernen): Zeilennummer + Kennung. */
  const [gewaehlt, setGewaehlt] = useState<{ index: number; kennung: string } | null>(null);
  /**
   * Die Auswahl überlebt nur Änderungen über die Zeilenaktionen selbst (Menge ±, Preis).
   * Jede andere Änderung am Bon (Artikel, Scanner, Rabatt, Kunde, Tisch, Leeren, Beleg)
   * hebt sie auf – so kann nie eine nachgerückte Zeile bearbeitet werden.
   */
  const zeilenAktion = useRef(false);
  useEffect(() => {
    if (zeilenAktion.current) {
      zeilenAktion.current = false;
      return;
    }
    setGewaehlt(null);
  }, [positionen]);
  /** Zuletzt hinzugefügte Zeile: kurz hervorgehoben und in Sicht gescrollt. */
  const [zuletzt, setZuletzt] = useState<{ schluessel: string; nr: number } | null>(null);
  const bonInhalt = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (zuletzt === null) {
      return;
    }
    // Nur den Bon scrollen, nie die Seite (gestapelte Ansicht: Artikel bleiben im Bild)
    const box = bonInhalt.current;
    const zeile = box?.querySelector('li.zuletzt');
    if (box != null && zeile != null) {
      const b = box.getBoundingClientRect();
      const z = zeile.getBoundingClientRect();
      if (z.top < b.top) {
        box.scrollTop -= b.top - z.top;
      } else if (z.bottom > b.bottom) {
        box.scrollTop += z.bottom - b.bottom;
      }
    }
    const timer = setTimeout(() => {
      setZuletzt(null);
    }, HERVORHEBUNG_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [zuletzt]);
  /** Pfand- bzw. Leergut-Dialog (Modul pfand). */
  const [pfandDialog, setPfandDialog] = useState<'pfand' | 'leergut' | null>(null);
  /**
   * Offene Gewichtsabfrage eines Wiegeartikels. Der Ref wird synchron gesetzt, damit ein
   * zweiter Scan bzw. Doppeltipp vor dem nächsten Rendern keine zweite Abfrage öffnet.
   */
  const [wiegeAnfrage, setWiegeAnfrage] = useState<WiegeAnfrage | null>(null);
  const wiegeAnfrageRef = useRef<WiegeAnfrage | null>(null);
  /** Fester Rabatt des übernommenen Kunden – gilt auch für später hinzugefügte Artikel. */
  const [kundenRabatt, setKundenRabatt] = useState<number | null>(
    () => gesichert?.kunde?.rabattProzent ?? null,
  );
  /** Kunde aus dem Kundenstamm – geht mit dem Beleg, damit der Server den Kundenrabatt prüft (S3). */
  const [kundeId, setKundeId] = useState<number | null>(() => gesichert?.kunde?.id ?? null);
  /** Außer Haus (Mitnahme/Lieferung): Kacheln mit abweichendem Satz buchen diesen. */
  const [ausserHaus, setAusserHaus] = useState(false);
  const [rabattOffen, setRabattOffen] = useState(false);
  const rabatt = rabattProzent(positionen);
  /** Notiz auf dem Beleg (Tisch, Kundenwunsch). */
  const [notiz, setNotiz] = useState(gesichert?.notiz ?? '');
  /** Kassierkraft dieses Geräts (Liste in der Einrichtung gepflegt); geht mit jedem Beleg mit. */
  const [bediener, setBediener] = useState(() => ladeBediener(localStorage, kassenId));
  function bedienerWechseln(name: string): void {
    const neu = setzeAktivenBediener(bediener, name === '' ? null : name);
    speichereBediener(localStorage, kassenId, neu);
    setBediener(neu);
  }
  /** Bediener vom Server (Backoffice-Verwaltung mit PIN); leer = lokale Namen gelten. */
  // Vorbelegt aus der Sitzung: war die Verwaltung schon aktiv, gilt die Anmeldepflicht
  // sofort – sonst wäre der Verkauf bis zur Antwort des Servers kurz offen.
  const [verwaltung, setVerwaltung] = useState<{
    bediener: BedienerKurz[];
    verwaltet: boolean;
    geladen: boolean;
  }>(() => ({ bediener: [], verwaltet: istVerwaltet(sessionStorage, kassenId), geladen: false }));
  const [anmeldung, setAnmeldung] = useState<AngemeldeterBediener | null>(() =>
    ladeAnmeldung(sessionStorage, kassenId),
  );
  const [anmeldeDialog, setAnmeldeDialog] = useState(false);
  const [anmeldeFehler, setAnmeldeFehler] = useState<string | null>(null);
  const [anmeldeLaeuft, setAnmeldeLaeuft] = useState(false);
  useEffect(() => {
    let aktuell = true;
    api
      .bedienerListe(kassenId)
      .then((v) => {
        if (aktuell) {
          setVerwaltung({ ...v, geladen: true });
          speichereVerwaltet(sessionStorage, kassenId, v.verwaltet && v.bediener.length > 0);
        }
      })
      .catch(() => undefined);
    return () => {
      aktuell = false;
    };
  }, [api, kassenId]);
  /** Mit Verwaltung am Server muss ein Bediener angemeldet sein, bevor kassiert wird. */
  const bedienerPflicht =
    verwaltung.verwaltet && (verwaltung.bediener.length > 0 || !verwaltung.geladen);
  const rechte = aktuelleRechte(sessionStorage, kassenId);
  /** Name am Beleg: angemeldeter Bediener, sonst der lokal gewählte. */
  const bedienerName = anmeldung?.name ?? bediener.aktiv;
  function anmelden(b: BedienerKurz, pin: string | undefined): void {
    setAnmeldeLaeuft(true);
    setAnmeldeFehler(null);
    api
      .bedienerAnmelden(kassenId, b.id, pin)
      .then((k) => {
        const neu = {
          id: k.id,
          name: k.name,
          rechte: k.rechte,
          ...(k.sitzung === undefined ? {} : { sitzung: k.sitzung }),
        };
        speichereAnmeldung(sessionStorage, kassenId, neu);
        setAnmeldung(neu);
        setAnmeldeDialog(false);
      })
      .catch((e: unknown) => {
        setAnmeldeFehler(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setAnmeldeLaeuft(false);
      });
  }
  function abmelden(): void {
    speichereAnmeldung(sessionStorage, kassenId, null);
    setAnmeldung(null);
  }
  // Der Wächter in der App meldet nach Inaktivität ab – Anzeige nachziehen.
  useEffect(() => {
    const beiAbmeldung = (): void => {
      setAnmeldung(ladeAnmeldung(sessionStorage, kassenId));
    };
    window.addEventListener(ABMELDUNG_EREIGNIS, beiAbmeldung);
    return () => {
      window.removeEventListener(ABMELDUNG_EREIGNIS, beiAbmeldung);
    };
  }, [kassenId]);
  /** Offene Tische (geparkte Bons) dieses Geräts. */
  const [tische, setTische] = useState<GeparkterBon[]>(() => ladeTische(localStorage, kassenId));
  /** Offene Tische am Server (alle Geräte des Lokals); leer, wenn offline oder nicht konfiguriert. */
  const [serverTische, setServerTische] = useState<OffenerBon[]>([]);
  const serverTischeLaden = useCallback(async () => {
    if (!navigator.onLine || !modul.tische) {
      return;
    }
    try {
      setServerTische(await api.tische(kassenId));
    } catch {
      // offline oder 501 – lokale Tische bleiben
    }
  }, [api, kassenId, modul.tische]);
  useEffect(() => {
    void serverTischeLaden();
  }, [serverTischeLaden]);
  /** Alle offenen Tische für Anzeige und Namensvergabe (Server + lokal). */
  const alleTische: GeparkterBon[] = [
    ...serverTische.map((t) => ({
      name: t.name,
      positionen: t.positionen,
      ...(t.empfaenger === null ? {} : { empfaenger: t.empfaenger }),
      ...(t.notiz === null ? {} : { notiz: t.notiz }),
      geparktAm: t.geparktAm,
    })),
    ...tische,
  ];
  const [tischeOffen, setTischeOffen] = useState(false);
  const [aktiverTisch, setAktiverTisch] = useState<string | null>(gesichert?.aktiverTisch ?? null);

  /** Gutscheine am Bon: Verkäufe (Positionen mit Code) und Einlösungen (Zahlungsmittel bzw. Einzweck-Abzug). */
  const [gutscheinAusgaben, setGutscheinAusgaben] = useState<GutscheinAusgabe[]>(
    () => (gesichert?.gutscheinAusgaben ?? []) as GutscheinAusgabe[],
  );
  const [einloesungen, setEinloesungen] = useState<GutscheinEinloesung[]>(
    () => (gesichert?.einloesungen ?? []) as GutscheinEinloesung[],
  );
  const [gutscheinDialog, setGutscheinDialog] = useState<'verkauf' | 'einloesen' | null>(null);
  /** POS V2 Phase 2, D11: offene Rückfrage (Entfernen, Leeren); `aktion` läuft erst nach Bestätigung. */
  const [rueckfrage, setRueckfrage] = useState<{
    titel: string;
    icon: FunktionsIcon;
    text: string;
    zurueck: string;
    bestaetigen: string;
    aktion: () => void;
  } | null>(null);
  /** Barkassieren: Dialog mit Rückgeld; das Ergebnis wird zur Belegansicht angezeigt. */
  const [barDialog, setBarDialog] = useState(false);
  const [rueckgeld, setRueckgeld] = useState<{ gegebenCent: number; rueckgeldCent: number } | null>(
    null,
  );
  /** Kassenlade nach Barzahlung nicht geöffnet (Bridge/Drucker) – zur Belegansicht anzeigen. */
  const [ladeHinweis, setLadeHinweis] = useState<string | null>(null);
  /** Vom Scanner gelesener Gutscheincode – öffnet den Einlöse-Dialog mit Vorbelegung. */
  const [scanCode, setScanCode] = useState<string | null>(null);

  /** Bereits an Küche/Schank gesendete Mengen dieses Bons (Modul Küchenbons). */
  const [boniert, setBoniert] = useState<BonierStand[]>(
    () => (gesichert?.boniert ?? []) as BonierStand[],
  );
  const [bonierMeldung, setBonierMeldung] = useState<string | null>(null);
  /** Läuft gerade eine Bonierung? Solange gesperrt, damit nichts doppelt an die Küche geht. */
  const [bonierLaeuft, setBonierLaeuft] = useState(false);
  const bonierSperre = useRef(false);
  const zuBonieren = modul.kuechenbons ? bonierDiff(positionen, boniert) : [];

  /**
   * Unterschied an Küche und Schank senden. Liefert true, wenn nichts zu tun war
   * oder der Server angenommen hat; Druckfehler einzelner Stellen werden gemeldet.
   */
  async function bonierenSenden(
    diff: readonly BonierStand[],
    tisch: string | null = aktiverTisch,
  ): Promise<boolean> {
    if (!modul.kuechenbons || training || diff.length === 0) {
      return true;
    }
    const auftrag: OffeneBonierung = {
      vorgangId: neueVorgangsId(),
      positionen: [...diff],
      ...(tisch === null ? {} : { tisch }),
      ...(bedienerName === null ? {} : { bediener: bedienerName }),
      ...(notiz.trim() === '' ? {} : { notiz: notiz.trim() }),
    };
    /** Netzfehler: dauerhaft vormerken und später mit derselben Kennung nachsenden. */
    const vormerkenUndMelden = (): true => {
      vormerken(localStorage, kassenId, auftrag);
      setNachsendeOffen(ladeNachsendeListe(localStorage, kassenId).length);
      setFehler(
        'Küchenbon nicht erreicht – wird automatisch nachgesendet, sobald die Verbindung steht',
      );
      return true;
    };
    if (!navigator.onLine) {
      return vormerkenUndMelden();
    }
    bonierSperre.current = true;
    setBonierLaeuft(true);
    try {
      const r = await api.bonieren(kassenId, auftrag);
      const fehlerText = r.bonierungen
        .filter((b) => b.druckFehler !== null)
        .map((b) => `${b.ziel}: ${b.druckFehler ?? ''}`);
      setBonierMeldung(
        r.bonierungen.length === 0
          ? null
          : `Boniert: ${r.bonierungen.map((b) => `${b.ziel}${b.gedruckt ? ' (Druck)' : ' (Monitor)'}`).join(', ')}` +
              (fehlerText.length > 0 ? ` – Druck fehlgeschlagen: ${fehlerText.join('; ')}` : ''),
      );
      return true;
    } catch (e: unknown) {
      if (istNetzfehler(e)) {
        return vormerkenUndMelden();
      }
      setFehler(`Küchenbon nicht gesendet: ${e instanceof Error ? e.message : String(e)}`);
      return false;
    } finally {
      bonierSperre.current = false;
      setBonierLaeuft(false);
    }
  }

  /** Vorgemerkte Küchenbons (Netzfehler) – Anzahl für die Anzeige. */
  const [nachsendeOffen, setNachsendeOffen] = useState(
    () => ladeNachsendeListe(localStorage, kassenId).length,
  );
  useEffect(() => {
    if (!modul.kuechenbons || sync === undefined) {
      return;
    }
    // Nachsendung läuft app-weit (hintergrund.ts) – hier nur Anzahl und Ablehnungen anzeigen
    const ergebnis = (e: Event): void => {
      const d = (e as CustomEvent<{ kassenId?: string; offen?: number; verworfen?: number }>)
        .detail;
      if (d.kassenId !== kassenId) {
        return;
      }
      setNachsendeOffen(d.offen ?? 0);
      if ((d.verworfen ?? 0) > 0) {
        setFehler(
          `${String(d.verworfen)} Küchenbon(s) vom Server abgelehnt – bitte in Küche/Schank Bescheid geben`,
        );
      }
    };
    window.addEventListener('bilanx:kuechenbons', ergebnis);
    return () => {
      window.removeEventListener('bilanx:kuechenbons', ergebnis);
    };
  }, [kassenId, modul.kuechenbons, sync]);
  useEffect(() => {
    if (!modul.kuechenbons || sync !== undefined) {
      return;
    }
    let aktiv = true;
    const lauf = (): void => {
      if (!navigator.onLine || ladeNachsendeListe(localStorage, kassenId).length === 0) {
        return;
      }
      void nachsenden(
        (b) => api.bonieren(kassenId, b),
        localStorage,
        kassenId,
        bonierungAbgelehnt,
      ).then((r) => {
        if (aktiv) {
          setNachsendeOffen(r.offen);
          if (r.verworfen > 0) {
            setFehler(
              `${String(r.verworfen)} Küchenbon(s) vom Server abgelehnt – bitte in Küche/Schank Bescheid geben`,
            );
          }
        }
      });
    };
    lauf();
    const timer = setInterval(lauf, 20_000);
    window.addEventListener('online', lauf);
    return () => {
      aktiv = false;
      clearInterval(timer);
      window.removeEventListener('online', lauf);
    };
  }, [api, kassenId, modul.kuechenbons, sync]);

  /** Knopf „Bonieren“: Neues senden, Abbestelltes stornieren. */
  async function bonierenJetzt(): Promise<void> {
    if (bonierSperre.current) {
      return; // Doppeltipp: die laufende Bonierung abwarten
    }
    const diff = bonierDiff(positionen, boniert);
    if (await bonierenSenden(diff)) {
      setBoniert(verbuche(boniert, diff));
    }
  }

  /**
   * POS V2 Phase 2, D11: „Leeren“ fragt immer nach (Folgen im Satz, bonierte Positionen bekommen
   * einen Storno-Bon). Erst „Bon leeren“ führt den bisherigen Ablauf aus.
   */
  function bonLeerenFragen(): void {
    const boniertStueck = modul.kuechenbons ? boniert.reduce((n, b) => n + b.menge, 0) : 0;
    setRueckfrage({
      titel: 'Ganzen Bon leeren?',
      icon: 'leeren',
      text:
        `${String(ohneRabatt(positionen).length)} Position(en) (${formatiereEuro(summeBrutto(positionen))} €) werden verworfen.` +
        (boniertStueck > 0
          ? ` ${String(boniertStueck)} bereits bonierte Position(en): Küche/Schank erhält einen Storno-Bon.`
          : ' Kein Beleg betroffen.'),
      zurueck: 'Abbrechen',
      bestaetigen: 'Bon leeren',
      aktion: () => void bonLeeren(),
    });
  }

  /** Bon leeren; bereits Boniertes wird in Küche/Schank storniert (nach der Rückfrage D11). */
  async function bonLeeren(): Promise<void> {
    if (modul.kuechenbons && boniert.length > 0) {
      if (bonierSperre.current) {
        return;
      }
      const gesendet = await bonierenSenden(bonierDiff([], boniert));
      // Ohne gesendeten Storno würde die Küche weiterkochen – nur auf ausdrücklichen Wunsch leeren.
      if (
        !gesendet &&
        !window.confirm(
          'Der Storno ist NICHT bei Küche/Schank angekommen. Bon trotzdem leeren? (Dann bitte persönlich Bescheid geben.)',
        )
      ) {
        return;
      }
    }
    bonZuruecksetzen();
  }

  /**
   * Nach einem Beleg: der volle Unterschied geht jetzt an Küche/Schank – noch
   * nicht Boniertes (Imbiss, Theke) ebenso wie vor dem Kassieren Abbestelltes
   * (Storno). Beim Teilen bleibt der Rest als Bon; die bezahlten Mengen verlassen
   * den Bonierstand ohne Storno.
   */
  function teilungAbschliessen(bezahlt: readonly Position[]): void {
    const diff = bonierDiff(positionen, boniert);
    void bonierenSenden(diff);
    const rest = restNachZahlung(positionen, bezahlt);
    if (rest.length > 0) {
      setBoniert(abziehen(verbuche(boniert, diff), bezahlt));
      setPositionen(rest);
      setAuswahl(new Map());
      return;
    }
    bonZuruecksetzen();
  }

  function bonZuruecksetzen(): void {
    // Codex-Prüfung 4: ein leerer Bon trägt keine Reservierung mehr
    setReservierungMarker(null);
    besitzFreigabe.current?.();
    besitzFreigabe.current = null;
    besitzId.current = null;
    setKundenRabatt(null);
    setKundeId(null);
    setPositionen([]);
    setTeilen(false);
    setAuswahl(new Map());
    setEmpfaengerEingabe(KEIN_EMPFAENGER);
    setKundeEmail(null);
    setNotiz('');
    setAktiverTisch(null);
    setGutscheinAusgaben([]);
    setEinloesungen([]);
    setBoniert([]);
  }

  function gutscheinVerkaufen(a: GutscheinAusgabe | null): void {
    setGutscheinDialog(null);
    if (a === null) {
      return;
    }
    // Ein Rabatt würde den Gutschein kürzen – Bon ohne Rabatt weiterführen.
    setPositionen((alt) => [...ohneRabatt(alt), verkaufsposition(a)]);
    setGutscheinAusgaben((alt) => [...alt, a]);
  }

  function gutscheinEinloesen(e: GutscheinEinloesung | null): void {
    setGutscheinDialog(null);
    setScanCode(null);
    if (e === null) {
      return;
    }
    if (e.art === 'einzweck' && e.steuersatz !== null) {
      const satz = e.steuersatz;
      // Derselbe Code erneut übernommen: alte Abzugszeile ersetzen, nicht zusätzlich abziehen.
      setPositionen((alt) => [
        ...ohneRabatt(alt).filter((p) => !p.bezeichnung.includes(`Einzweck-Gutschein ${e.code}`)),
        einzweckEinloesungsposition(e.code, e.betragCent, satz),
      ]);
    }
    setEinloesungen((alt) => [...alt.filter((x) => x.code !== e.code), e]);
  }

  function einloesungEntfernen(code: string): void {
    setEinloesungen((alt) => alt.filter((x) => x.code !== code));
    setPositionen((alt) =>
      alt.filter((p) => !p.bezeichnung.includes(`Einzweck-Gutschein ${code}`)),
    );
  }

  /** Bon als Tisch parken; true, wenn er gesichert ist (Server oder Gerät). */
  /**
   * Codex-Prüfung 10: Kennung der Browsersitzung (sessionStorage – übersteht Neuladen und
   * Duplizieren, nicht das Schließen). Ein Vorgang dieser Sitzung gehört zum Bon hier.
   */
  const [sitzungsId] = useState(() => {
    try {
      const vorhanden = sessionStorage.getItem('bilanx.sitzung');
      if (vorhanden !== null && vorhanden !== '') {
        return vorhanden;
      }
      const neu = crypto.randomUUID();
      sessionStorage.setItem('bilanx.sitzung', neu);
      return neu;
    } catch {
      return crypto.randomUUID();
    }
  });
  /** Codex-Prüfung 9: Parken läuft (Bonierung, Serveraufruf) – solange kein Kassieren. */
  const parkLaeuft = useRef(false);
  async function tischParken(): Promise<boolean> {
    // Codex-Prüfung 8: Ein ungeklärter Einkauf (offener Beleg, Kartenzahlung, Lieferdienst,
    // Reservierung) wird nie geparkt – die geparkte Kopie trüge keine Vorgangskennung und ließe
    // sich nach der Klärung ein zweites Mal kassieren
    if (vorgangOffen || reservierungMarker !== null) {
      setFehler(
        'Verkauf noch ungeklärt. Bitte diesen Einkauf nicht erneut kassieren. Zuerst Belegstatus prüfen – erst danach parken.',
      );
      return false;
    }
    // Ein geparkter Bon speichert nur Positionen – ein verkaufter Gutschein (Code, Art,
    // Steuersatz) ginge verloren und würde später als normaler Artikel kassiert.
    if (gutscheinAusgaben.length > 0 || einloesungen.length > 0) {
      setFehler('Bons mit Gutscheinen können nicht geparkt werden – zuerst kassieren');
      return false;
    }
    const name =
      aktiverTisch ??
      window.prompt('Name des Tisches / Bons', naechsterTischname(alleTische))?.trim() ??
      '';
    if (name === '' || positionen.length === 0) {
      return false;
    }
    // Parken heißt in der Gastro: Bestellung an Küche/Schank. Nur bei Erfolg gilt sie als boniert.
    if (bonierSperre.current) {
      return false;
    }
    // Codex-Prüfung 9: Parken und Kassieren schließen sich aus – kein Kassieren, solange das
    // Parken läuft, und kein Parken während eines Kassiervorgangs
    if (
      parkLaeuft.current ||
      laeuftRef.current ||
      barSperre.current ||
      plattformLaeuft.current ||
      direktBelegSperre.current
    ) {
      return false;
    }
    parkLaeuft.current = true;
    setLaeuft(true);
    try {
      return await tischParkenEinmal(name);
    } finally {
      parkLaeuft.current = false;
      setLaeuft(false);
    }
  }

  async function tischParkenEinmal(name: string): Promise<boolean> {
    const diff = bonierDiff(positionen, boniert);
    const gesendet = await bonierenSenden(diff, name);
    const neuBoniert = gesendet ? verbuche(boniert, diff) : boniert;
    const empfaenger = empfaengerFuer(summe, empfaengerEingabe).empfaenger;
    const bon = {
      name,
      positionen,
      boniert: neuBoniert,
      ...(empfaenger === undefined ? {} : { empfaenger }),
      ...(notiz.trim() === '' ? {} : { notiz: notiz.trim() }),
    };
    // Online am Server (alle Geräte sehen den Tisch), sonst lokal auf diesem Gerät.
    let amServer = false;
    if (navigator.onLine) {
      try {
        await api.tischParken(kassenId, name, {
          ...bon,
          ...(bedienerName === null ? {} : { bediener: bedienerName }),
        });
        amServer = true;
        await serverTischeLaden();
      } catch (e: unknown) {
        if (!istNetzfehler(e) && !(e instanceof ApiFehler && e.status === 501)) {
          setFehler(e instanceof Error ? e.message : String(e));
          return false;
        }
      }
    }
    if (!amServer) {
      setTische(parkeBon(localStorage, kassenId, bon));
    }
    bonZuruecksetzen();
    setTischeOffen(false);
    return true;
  }

  function bonUebernehmen(bon: GeparkterBon): void {
    setPositionen(bon.positionen);
    setEmpfaengerEingabe(
      bon.empfaenger === undefined
        ? KEIN_EMPFAENGER
        : {
            name: bon.empfaenger.name,
            anschrift: bon.empfaenger.anschrift,
            uid: bon.empfaenger.uid ?? '',
          },
    );
    setNotiz(bon.notiz ?? '');
    setAktiverTisch(bon.name);
    setBoniert(bon.boniert ?? []);
    setTischeOffen(false);
  }

  /** Tischplan des Betriebs (leer = Tische nur als Liste). */
  const [tischplan, setTischplan] = useState<Awaited<ReturnType<KassenApi['tischplan']>> | null>(
    null,
  );
  const [tischplanOffen, setTischplanOffen] = useState(false);
  useEffect(() => {
    if (!modul.tische || !navigator.onLine) {
      return;
    }
    let aktuell = true;
    api
      .tischplan(kassenId)
      .then((t) => {
        if (aktuell) {
          setTischplan(t);
        }
      })
      .catch(() => undefined);
    return () => {
      aktuell = false;
    };
  }, [api, kassenId, modul.tische]);
  const hatTischplan = tischplan?.plan.bereiche.some((b) => b.tische.length > 0) ?? false;
  const belegung = new Map<string, TischBelegung>([
    ...tische.map((t): [string, TischBelegung] => [
      t.name,
      { summeCent: summeBrutto(t.positionen), seit: t.geparktAm, bediener: null, lokal: true },
    ]),
    ...serverTische.map((t): [string, TischBelegung] => [
      t.name,
      {
        summeCent: summeBrutto(t.positionen),
        seit: t.geparktAm,
        bediener: t.bediener,
        lokal: false,
      },
    ]),
  ]);

  /**
   * Tisch im Plan angetippt: belegt → aufrufen (ein offener anderer Tisch wird vorher
   * geparkt), frei → neuer Bon für diesen Tisch; ein noch namenloser Bon wird ihm zugeordnet.
   */
  async function tischGewaehlt(name: string): Promise<void> {
    setTischplanOffen(false);
    if (name === aktiverTisch) {
      return;
    }
    const belegtAmServer = serverTische.some((t) => t.name === name);
    const belegtLokal = tische.some((t) => t.name === name);
    if (positionen.length > 0 && aktiverTisch !== null) {
      // Scheitert das Parken, bleibt der aktuelle Bon – nicht durch den anderen Tisch ersetzen.
      if (!(await tischParken())) {
        return;
      }
    } else if (positionen.length > 0 && !belegtAmServer && !belegtLokal) {
      setAktiverTisch(name);
      return;
    }
    if (belegtAmServer || belegtLokal) {
      await tischAufrufen(name, belegtAmServer ? 'server' : 'lokal', aktiverTisch !== null);
      return;
    }
    setAktiverTisch(name);
  }

  async function tischAufrufen(
    name: string,
    quelle: 'server' | 'lokal',
    ohneRueckfrage = false,
  ): Promise<void> {
    if (
      !ohneRueckfrage &&
      positionen.length > 0 &&
      !window.confirm('Aktuellen Bon verwerfen und Tisch aufrufen?')
    ) {
      return;
    }
    if (quelle === 'lokal') {
      const bon = holeBon(localStorage, kassenId, name);
      setTische(ladeTische(localStorage, kassenId));
      if (bon !== null) {
        bonUebernehmen(bon);
      }
      return;
    }
    await tischVomServerHolen({ name, vorgangId: neueVorgangsId() });
  }

  /**
   * Tisch vom Server holen. Der Server löscht ihn dabei; geht die Antwort verloren,
   * liefert derselbe Vorgang (Kennung) ihn noch einmal. Bleibt die Verbindung weg,
   * wird der Vorgang in der Sitzung gemerkt und beim nächsten Öffnen nachgeholt.
   */
  async function tischVomServerHolen(v: { name: string; vorgangId: string }): Promise<void> {
    const merker = `bilanx.tischaufruf.${kassenId}`;
    sessionStorage.setItem(merker, JSON.stringify(v));
    try {
      for (let versuch = 0; ; versuch++) {
        try {
          const t = await api.tischAufrufen(kassenId, v.name, v.vorgangId);
          sessionStorage.removeItem(merker);
          bonUebernehmen({
            name: t.name,
            positionen: t.positionen,
            ...(t.empfaenger === null ? {} : { empfaenger: t.empfaenger }),
            ...(t.notiz === null ? {} : { notiz: t.notiz }),
            ...(t.boniert === undefined ? {} : { boniert: t.boniert }),
            geparktAm: t.geparktAm,
          });
          return;
        } catch (e: unknown) {
          if (istNetzfehler(e) && versuch < 2) {
            await new Promise((r) => setTimeout(r, 1000));
            continue;
          }
          if (!istNetzfehler(e)) {
            sessionStorage.removeItem(merker);
          }
          setFehler(
            e instanceof ApiFehler && e.status === 404
              ? `Tisch „${v.name}“ wurde inzwischen an einem anderen Gerät aufgerufen`
              : istNetzfehler(e)
                ? `Tisch „${v.name}“: Verbindung unterbrochen – der Aufruf wird beim nächsten Öffnen des Verkaufs nachgeholt`
                : e instanceof Error
                  ? e.message
                  : String(e),
          );
          return;
        }
      }
    } finally {
      await serverTischeLaden();
    }
  }
  // Ein unterbrochener Tischaufruf aus der Sitzung: nachholen, solange der Bon leer ist.
  const tischaufrufGeprueft = useRef(false);
  useEffect(() => {
    if (tischaufrufGeprueft.current) {
      return;
    }
    tischaufrufGeprueft.current = true;
    const roh = sessionStorage.getItem(`bilanx.tischaufruf.${kassenId}`);
    if (roh === null || positionen.length > 0) {
      return;
    }
    try {
      const v = JSON.parse(roh) as { name?: unknown; vorgangId?: unknown };
      if (typeof v.name === 'string' && typeof v.vorgangId === 'string') {
        void tischVomServerHolen({ name: v.name, vorgangId: v.vorgangId });
      }
    } catch {
      sessionStorage.removeItem(`bilanx.tischaufruf.${kassenId}`);
    }
    // Nur beim ersten Rendern.
  }, []);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  /** Barzahlung läuft (synchron, unabhängig vom Rendern). */
  const barSperre = useRef(false);
  /**
   * W-7 (Welle 2): Trainingsbeleg bzw. Gutschein-Abschluss läuft (synchron, unabhängig vom
   * Rendern) – Doppeltipp, Doppel-Enter oder gehaltenes F2 lösen nur einen Belegaufruf aus.
   */
  const direktBelegSperre = useRef(false);

  // Barcode-Scanner im Tastaturmodus (Datalogic, Honeywell u. a.): Code + Enter → Code-Suche
  // (artikelcode.ts) → Bon, Auswahl, Gutschein oder Hinweis. Der eine Fenster-Listener ruft
  // über eine Ref immer die aktuelle Verarbeitung auf (Sperre, Menge, Konfiguration).
  const scanVerarbeitenRef = useRef<(code: string) => void>(() => undefined);
  const feldScanRef = useRef<
    (e: KeyboardEvent, feld: HTMLInputElement | HTMLTextAreaElement) => void
  >(() => undefined);
  /** Android-WebView: Zeichen, die nur als beforeinput kommen (keydown „Unidentified“). */
  const feldEingabeRef = useRef<
    (e: InputEvent, feld: HTMLInputElement | HTMLTextAreaElement) => void
  >(() => undefined);
  const feldScan = useRef(new FeldScanErkennung());
  /**
   * wa3/wa2: eine gemeinsame Instanz – globaler HID-Pfad und Suchfeld verwerfen dieselbe Meldung
   * desselben physischen Scans binnen DOPPELSCAN_MS (120 ms).
   */
  const doppelFilterRef = useRef(new DoppelScanFilter());
  /** Timer für einen Präfix-Scan im Feld ohne Abschluss (Codex-Nachprüfung PB-1). */
  const feldPraefixTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Enter, das im Suchfeld eine schnelle (scannerartige) Folge abschließt: Es darf keinen
   * einzelnen Bezeichnungs- bzw. Anfangstreffer automatisch übernehmen (Codex-Nachprüfung PB-1).
   */
  const schnellesSuchEnter = useRef<KeyboardEvent | null>(null);
  useEffect(
    () => () => {
      if (feldPraefixTimer.current !== null) {
        clearTimeout(feldPraefixTimer.current);
      }
    },
    [],
  );
  /** Zeitpunkt des letzten erkannten Scans (für den Nachlauf nach einem Fokuswechsel). */
  const letzterScanZeit = useRef(Number.NEGATIVE_INFINITY);
  /** Kundenanzeige: Kartenzahlung bezahlt und Beleg in Arbeit bzw. ohne Erfolg beendet. */
  const [kartenStand, setKartenStand] = useState<'beleg' | 'abgelehnt' | 'abgebrochen' | null>(
    null,
  );
  /**
   * Mehrere Artikel zum gescannten Code (bzw. ein verkaufbarer und gesperrte): Auswahl durch
   * die Kassierkraft, gesperrte mit Grund und nicht wählbar (PB-1).
   */
  const [scanAuswahl, setScanAuswahl] = useState<{
    code: string;
    treffer: Schnellwahl[];
    gesperrt: GesperrteKachel[];
  } | null>(null);
  /** PB-1: schnelle Folge mit 1–3 Zeichen ohne Scannerpräfix – verworfen, nur Hinweis. */
  const kurzcodeRef = useRef<(code: string, grund: 'kurz' | 'unvollstaendig') => void>(
    () => undefined,
  );
  useEffect(() => {
    const erkennung = new ScanErkennung({
      doppelFilter: doppelFilterRef.current,
      onScan: (code) => {
        scanVerarbeitenRef.current(code);
      },
      onVerworfen: (code, grund) => {
        kurzcodeRef.current(code, grund);
      },
      // je Gerät eingerichtet (Einrichtung → Barcode-Scanner); bei jeder Taste aktuell gelesen
      praefix: () => ladeScannerPraefix(localStorage),
      onFreigabe: (tasten: readonly GehalteneTaste[]) => {
        for (const t of tasten) {
          const ev = new KeyboardEvent('keydown', { key: t.key, bubbles: true, cancelable: true });
          WEITERGELEITET.add(ev);
          // Ziel per Fähigkeit statt `instanceof` (Testumgebungen mit eigener EventTarget-Klasse);
          // ein inzwischen entferntes Ziel (Dialog zu) bekommt nichts mehr – dann wie bisher body
          const ziel = t.ziel as { dispatchEvent?: unknown; isConnected?: unknown } | null;
          (typeof ziel?.dispatchEvent === 'function' && ziel.isConnected !== false
            ? (ziel as EventTarget)
            : document.body
          ).dispatchEvent(ev);
        }
      },
    });
    const handler = (e: KeyboardEvent): void => {
      if (
        WEITERGELEITET.has(e) ||
        e.isComposing ||
        e.altKey ||
        e.ctrlKey ||
        e.metaKey ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }
      // Eingabefelder gehören der Tastatur; nur bei gesperrtem Scan (z. B. „Gegeben“ im
      // Bar-Dialog) wird ein Scan dort erkannt und verworfen.
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        // Felder mit eigener Scanerkennung (PIN-Feld der Bedieneranmeldung, W5-A4) – sonst
        // schluckte dieser Listener das Präfix, und das Feld sähe nur den Rest des Scans
        if (e.target.dataset.eigeneScanerkennung === undefined) {
          feldScanRef.current(e, e.target);
        }
        return;
      }
      if (erkennung.taste(e.key, tastenZeit(e), e.target) === 'gehalten') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    // Fokus im Feld (auch inputmode=none bei BT v3): Zeichen ohne verwertbares keydown kommen
    // nur als beforeinput – dieselbe Felderkennung wie über keydown (A4, Go-Live SUNMI)
    const eingabe = (e: Event): void => {
      const ziel = e.target;
      if (
        (ziel instanceof HTMLInputElement || ziel instanceof HTMLTextAreaElement) &&
        ziel.dataset.eigeneScanerkennung === undefined &&
        e instanceof InputEvent
      ) {
        feldEingabeRef.current(e, ziel);
      }
    };
    window.addEventListener('keydown', handler, true);
    window.addEventListener('beforeinput', eingabe, true);
    return () => {
      window.removeEventListener('keydown', handler, true);
      window.removeEventListener('beforeinput', eingabe, true);
      erkennung.beenden();
    };
  }, []);
  /** Trainingsmodus: Belege werden als TRAININGSBELEG signiert, ohne Zahlung und ohne Umsatz. */
  const [training, setTraining] = useState(false);
  const [zahlung, setZahlung] = useState<Zahlung | null>(null);
  /** Vor der Kartenzahlung: Trinkgeld abfragen (Gastro). */
  const [trinkgeldFrage, setTrinkgeldFrage] = useState(false);
  /** D3 v2 / N9: „Anderer Betrag“ – Trinkgeld in Cent über den Ziffernblock (null = Auswahl). */
  const [trinkgeldFrei, setTrinkgeldFrei] = useState<number | null>(null);
  useEffect(() => {
    if (!trinkgeldFrage) {
      setTrinkgeldFrei(null);
    }
  }, [trinkgeldFrage]);
  const [beleg, setBeleg] = useState<{ beleg: BelegAntwort; text: string | null } | null>(null);
  const [empfaengerEingabe, setEmpfaengerEingabe] = useState(
    gesichert?.empfaenger ?? KEIN_EMPFAENGER,
  );
  /** Empfängerfelder zeigen: nur ausdrücklich (Rechnung gewünscht), nie wegen des Betrags. */
  const [empfaengerOffen, setEmpfaengerOffen] = useState(false);
  /** Kundensuche: null = zu; sonst der Suchtext. */
  const [kundenSuche, setKundenSuche] = useState<string | null>(null);
  const [kundenTreffer, setKundenTreffer] = useState<KundeKurz[]>([]);
  /** E-Mail des übernommenen Kunden – Vorschlag für den Belegversand. */
  const [kundeEmail, setKundeEmail] = useState<string | null>(gesichert?.kundeEmail ?? null);
  /** Je Zahlungsreferenz genau ein Belegversuch – schützt vor Doppelmeldungen des Dialogs. */
  const verarbeitet = useRef(new Set<string>());

  const teilung = useMemo(
    () => (teilen ? teileBon(positionen, auswahl) : null),
    [teilen, positionen, auswahl],
  );
  /** Positionen des nächsten Belegs: beim Teilen die Auswahl, sonst der ganze Bon. */
  const positionenZumZahlen = teilung === null ? positionen : teilung.jetzt;
  const summe = summeBrutto(positionenZumZahlen);
  const rechnungsempfaenger = empfaengerFuer(summe, empfaengerEingabe);
  // Empfänger nur ausdrücklich (Rechnung) – der Betrag allein verlangt keinen (§ 132a BAO).
  const empfaengerSichtbar = empfaengerOffen;

  // Kundenstamm durchsuchen, leicht verzögert, damit nicht jeder Tastendruck eine Anfrage auslöst.
  useEffect(() => {
    if (kundenSuche === null) {
      return;
    }
    const text = kundenSuche;
    let aktuell = true;
    const timer = setTimeout(() => {
      api
        .kunden(kassenId, text)
        .then((k) => {
          if (aktuell) {
            setKundenTreffer(k);
          }
        })
        .catch(() => {
          if (aktuell) {
            setKundenTreffer([]);
          }
        });
    }, 200);
    return () => {
      aktuell = false;
      clearTimeout(timer);
    };
  }, [api, kassenId, kundenSuche]);

  function kundeUebernehmen(k: KundeKurz): void {
    setEmpfaengerEingabe({ name: k.name, anschrift: k.anschrift, uid: k.uid ?? '' });
    setKundeEmail(k.email);
    setKundeId(k.id);
    // Felder zuklappen: der Kunde erscheint als Kundenzeile im Bon
    setEmpfaengerOffen(false);
    setKundenSuche(null);
    setKundenTreffer([]);
    // Kundenrabatt: vom Betrieb am Kunden hinterlegt, gilt ohne Rabattrecht des Bedieners.
    // Beim Wechsel fällt der Rabatt des vorigen Kunden; bei Gutscheinen im Bon keiner.
    const roh = k.rabattProzent ?? null;
    const prozent = roh !== null && roh > 0 ? roh : null;
    setPositionen((alt) => [...nachKundenwechsel(alt, kundenRabatt, prozent, gutscheineImBon)]);
    setKundenRabatt(prozent);
    setRabattOffen(false);
  }
  // Nur Gutscheine, deren Positionen noch im Bon liegen (Mehrzweck-Einlösungen sind Zahlungsmittel).
  const gutscheinStand = bereinigeGutscheine(positionen, gutscheinAusgaben, einloesungen);
  const gutscheinCent = gutscheinZahlungCent(gutscheinStand.einloesungen);
  /** Offener Rest nach Gutscheinen – das zahlt der Kunde bar oder mit Karte. */
  const rest = restNachGutschein(summe, gutscheinStand.einloesungen);
  /** Kundenanzeige (zweites Fenster): laufender Bon und Abschluss. */
  const anzeige = useRef<KundenanzeigeSender | null>(null);
  useEffect(() => {
    const sender = new KundenanzeigeSender(kassenId);
    anzeige.current = sender;
    return () => {
      sender.schliesse();
      anzeige.current = null;
    };
  }, [kassenId]);
  useEffect(() => {
    anzeige.current?.sende({
      typ: 'bon',
      positionen: positionenZumZahlen,
      summeCent: rest,
      gutscheinCent: summe - rest,
    });
  }, [positionenZumZahlen, rest, summe]);
  const mitGutschein = gutscheinStand.einloesungen.length > 0;
  /** Gutschein im Bon (Verkauf oder Einlösung): dann kein Bon- und kein Kundenrabatt. */
  const gutscheineImBon = mitGutschein || gutscheinStand.ausgaben.length > 0;
  const kassierbar =
    positionenZumZahlen.length > 0 &&
    !laeuft &&
    // N1 (Codex N1-8): Verkaufsstopp sperrt jeden Kassierweg, auch den Kartenstart
    props.verkaufsstopp == null &&
    (!bedienerPflicht || anmeldung !== null) &&
    (summe > 0 || (summe === 0 && mitGutschein)) &&
    rest >= 0 &&
    rechnungsempfaenger.fehler === null;
  /** Gutscheine decken alles: Beleg mit Zahlungsart „gutschein" abschließen. */
  const nurGutschein = mitGutschein && rest === 0;

  /** Tastaturkürzel für PC-Kassen: F2 Bar, F3 Karte, F4 Parken, F6 Teilen, Esc schließt Dialoge. */
  const kuerzel = useRef<() => Record<string, () => void>>(() => ({}));
  kuerzel.current = () => {
    /**
     * ZD-V2-F4: Solange ein Zahlungsdialog offen ist (Bar, Trinkgeldfrage, Karte, Rückerstattung,
     * Bezahlentscheidung) oder eine Barzahlung schon synchron gesperrt ist, wirken F2/F3/F4/F6
     * nicht – sonst startete F3 die Karte neben dem Bar-Dialog, F2 öffnete den Bar-Dialog über
     * der Trinkgeldfrage, F4 parkte und F6 teilte den Bon hinter dem offenen Dialog. Die
     * Funktionen selbst (Kassieren, Parken, Teilen) bleiben unverändert. Wird erst beim
     * Tastendruck ausgewertet (aktueller Stand dieses Renderns).
     */
    const zahlungsdialogOffen =
      barDialog ||
      trinkgeldFrage ||
      // D11: offene Rückfrage (Entfernen, Leeren) – keine Kürzel dahinter
      rueckfrage !== null ||
      // D8: Fenster „Menge oder Betrag“ ist modal
      ziffernOffen ||
      // Wiegeartikel: Gewichtsdialog ist modal
      wiegeAnfrage !== null ||
      // D6: Empfängerdialog ist modal – keine Zahlung/Parken/Teilen dahinter (Codex Sprint 01.11.)
      empfaengerSichtbar ||
      // BT (Codex-Prüfung BT v1, Auflage 3): Gutscheindialog ist modal – kein F2/F3/F4/F6 dahinter
      gutscheinDialog !== null ||
      zahlung !== null ||
      erstattung !== null ||
      bezahlFrage !== null ||
      barSperre.current ||
      // Codex-Prüfung A2 (Runde 1): Kartenstart schon synchron gesperrt (Reservierung läuft,
      // `laeuft` folgt erst danach) bzw. Parken läuft
      laeuftRef.current ||
      parkLaeuft.current ||
      // W-7: Trainingsbeleg bzw. Gutschein-Abschluss läuft schon
      direktBelegSperre.current;
    return {
      // Im Training nur Trainingsbelege – F2/F3 dürfen keinen echten Umsatz auslösen.
      F2: () => {
        if (!kassierbar || vorgangOffen || zahlungsdialogOffen) {
          return;
        }
        if (training) {
          void trainingsbeleg();
        } else if (!nurGutschein) {
          setBarDialog(true);
        }
      },
      F3: () => {
        if (
          kassierbar &&
          !vorgangOffen &&
          !zahlungsdialogOffen &&
          !training &&
          !nurGutschein &&
          navigator.onLine &&
          modul.kartenzahlung
        ) {
          karteWaehlen();
        }
      },
      F4: () => {
        if (positionen.length > 0 && !laeuft && !zahlungsdialogOffen && modul.tische) {
          void tischParken();
        }
      },
      F6: () => {
        if (
          !laeuft &&
          !zahlungsdialogOffen &&
          !mitGutschein &&
          modul.rechnungTeilen &&
          (teilen || teilbar(positionen))
        ) {
          setTeilen((t) => !t);
          setAuswahl(new Map());
        }
      },
      Escape: () => {
        // ZD-V2-F4: Esc schließt den Bar-Dialog nicht, während die Barzahlung verarbeitet wird
        // (die Rückmeldung bliebe sonst unsichtbar), und ist während einer Kartenzahlung bzw.
        // Rückerstattung ohne Wirkung.
        if (
          (barDialog && (laeuft || barSperre.current)) ||
          zahlung !== null ||
          erstattung !== null ||
          // Kartenstart unterwegs: Esc bräche ihn nicht ab (die Trinkgeldfrage verschwände nur)
          laeuftRef.current
        ) {
          return;
        }
        setBarDialog(false);
        setTrinkgeldFrage(false);
        setGutscheinDialog(null);
        setTischeOffen(false);
        setRabattOffen(false);
        setNotizOffen(false);
        setBedienerOffen(false);
        setMehrOffen(false);
        setWeitereOffen(false);
        setSuche('');
      },
    };
  };
  useEffect(() => {
    const beiTaste = (e: KeyboardEvent): void => {
      if (e.altKey || e.ctrlKey || e.metaKey) {
        return;
      }
      // R5: ein Esc, das nur die Bildschirmtastatur geschlossen hat, wirkt hier nicht
      if (e.key === 'Escape' && istTastaturEsc(e)) {
        return;
      }
      const aktion = kuerzel.current()[e.key];
      if (aktion !== undefined) {
        e.preventDefault();
        aktion();
      }
    };
    window.addEventListener('keydown', beiTaste);
    return () => {
      window.removeEventListener('keydown', beiTaste);
    };
  }, []);

  /** Eingabe des Ziffernblocks (Cent) und Mengenzähler – auch Stückzahl für die nächste Kachel. */
  const [zifferCent, setZifferCent] = useState(0);
  const [zifferMenge, setZifferMenge] = useState(1);

  function hinzufuegen(p: Position): void {
    // Ohne Anmeldung nimmt der Verkauf nichts an (auch nicht vom Scanner).
    if (bedienerPflicht && anmeldung === null) {
      return;
    }
    // Funktionales Update: schnelle Doppeltipps dürfen sich nicht überschreiben.
    // Ein bestehender Bon-Rabatt wird auf die neue Summe nachgezogen.
    setPositionen((alt) => {
      const prozent = rabattFuerNeuePosition(alt, kundenRabatt, gutscheineImBon);
      const neu = inBon(ohneRabatt(alt), p);
      return prozent === null ? neu : mitRabatt(neu, prozent);
    });
    setFehler(null);
    setZuletzt((z) => ({ schluessel: zeilenSchluessel(p), nr: (z?.nr ?? 0) + 1 }));
  }

  /**
   * Artikel aus Kachel, Suche oder Scan: Stückartikel mit Menge in den Bon, Wiegeartikel öffnen
   * den Gewichtsdialog (die vorgemerkte Stückzahl gilt dort nicht). Bei offenem Gewichtsdialog
   * wird jede weitere Auswahl verworfen – keine Doppelposition durch einen wiederholten Scan.
   */
  function artikelWaehlen(k: Schnellwahl, menge: number): void {
    if (bedienerPflicht && anmeldung === null) {
      return;
    }
    const auswahl = waehleArtikel(
      wiegeAnfrageRef.current,
      k,
      () => positionMitMenge(k, menge, ausserHaus),
      ausserHaus,
    );
    switch (auswahl.art) {
      case 'bonieren':
        hinzufuegen(auswahl.position);
        return;
      case 'gewicht':
        wiegeAnfrageRef.current = auswahl.anfrage;
        setWiegeAnfrage(auswahl.anfrage);
        setFehler(null);
        return;
      case 'ignoriert':
        setFehler(auswahl.grund);
    }
  }

  function wiegeAnfrageSchliessen(): void {
    wiegeAnfrageRef.current = null;
    setWiegeAnfrage(null);
  }

  // Aus dem Terminbuch: Leistung in den Bon legen, sobald kassiert werden darf (Anmeldung).
  const { vorbelegung, onVorbelegungUebernommen } = props;
  // Jede Vorbelegung genau einmal übernehmen (StrictMode und Neu-Renders rufen den Effekt mehrfach).
  const uebernommen = useRef<Position[] | null>(null);
  useEffect(() => {
    if (
      vorbelegung === undefined ||
      uebernommen.current === vorbelegung ||
      (bedienerPflicht && anmeldung === null)
    ) {
      return;
    }
    uebernommen.current = vorbelegung;
    if (vorbelegung.length > 0) {
      setPositionen((alt) => vorbelegung.reduce((bon, p) => inBon(bon, p), alt));
    }
    onVorbelegungUebernommen?.();
  }, [vorbelegung, onVorbelegungUebernommen, bedienerPflicht, anmeldung]);

  function rabattSetzen(prozent: number | null): void {
    setPositionen((alt) => (prozent === null ? ohneRabatt(alt) : mitRabatt(alt, prozent)));
    setRabattOffen(false);
  }

  /** Einmalige Aktion direkt nach dem gespeicherten Beleg (Kassenlade bei Barzahlung). */
  const nachBeleg = useRef<((belegnummer: string) => void) | null>(null);

  /**
   * N1: Vorgang am Server als nicht gebucht abschließen (unter der Kassensperre) und lokal den
   * Abschlussnachweis setzen. true = bestätigt; false = offen lassen (Klärung über „Beleg prüfen“).
   */
  async function abschliessenNichtGebucht(
    nummer: string,
    vorgangId: string,
    grund: string,
  ): Promise<boolean> {
    try {
      // Codex-Prüfung 14: unter der Kassiersperre den aktuellen Stand prüfen – hat ein anderes
      // Fenster die Bezahlung inzwischen bestätigt (oder den Vorgang abgeschlossen), wird hier
      // nie verworfen; die Klärung läuft dort („Beleg prüfen“)
      return await mitKassiersperre(kassenId, async () => {
        const d = await ladeVorgang(geraeteSpeicher, kassenId, nummer);
        if (
          d?.vorgang.id !== vorgangId ||
          d.bezahlt !== undefined ||
          d.abgeschlossen !== undefined
        ) {
          return false;
        }
        const stand = await api.vorgangVerwerfen(kassenId, vorgangId, nummer, grund.slice(0, 300));
        if (stand.stand !== 'verworfen') {
          return false;
        }
        await schliesseVorgangAb(geraeteSpeicher, kassenId, nummer, vorgangId, {
          art: 'nicht_gebucht',
          grund,
        });
        return true;
      });
    } catch {
      return false;
    }
  }

  /**
   * N1: Nummer des zuletzt gesendeten Verkaufs, dessen Vorgangsdatensatz noch offen ist – für den
   * Offline-Puffer bei verlorener Antwort (gleiche Vorgangs-ID, gleicher Inhalt).
   */
  const versuchteNummer = useRef<string | null>(null);

  /**
   * Neuen Vorgang anlegen – nur unter der Kassiersperre aufrufen (Fehlerbericht 25.10.2026):
   * Nummer aus dem gemeinsamen Zähler aller Fenster (N1.6), Vorgangs-ID und vollständiger
   * Auftrag, dauerhaft gesichert vor dem ersten Sendeversuch (N1.1). Scheitert der
   * Gerätespeicher, entsteht kein Beleg und es wird nicht gesendet.
   */
  async function vorgangAnlegen(
    zahlungsart: 'bar' | 'karte' | 'gutschein' | 'training',
    zuZahlen: readonly Position[],
    zahlungsreferenz: string | undefined,
    gegebenCent: number | null | undefined,
  ): Promise<{
    belegnummer: string;
    vorgang: { id: string; zeitpunkt: string };
    auftrag: BelegAuftrag;
    ohneVerbindung: boolean;
  }> {
    // Codex-Prüfung 4: Beleg einer nur übernommenen Kartenzahlung – Kunde, Gutscheine, Empfänger
    // und Notiz aus der Momentaufnahme ihres Bons, nie aus dem Bon dieses Fensters
    const kontext = zahlungsreferenz !== undefined && karteFremd() ? fremdKontext.current : null;
    if (zahlungsreferenz !== undefined && karteFremd() && kontext === null) {
      throw new Error(
        'Belegdaten der Kartenzahlung fehlen in diesem Fenster – im ursprünglichen Fenster „Zahlung prüfen“ (oder Zahlung rückerstatten)',
      );
    }
    const kEmpfaenger = kontext?.empfaenger ?? empfaengerEingabe;
    const kNotiz = kontext?.notiz ?? notiz;
    const kKundeId = kontext === null ? kundeId : (kontext.kunde?.id ?? null);
    const kGutscheine =
      kontext === null
        ? gutscheinStand
        : bereinigeGutscheine(
            kontext.positionen as Position[],
            kontext.gutscheinAusgaben as GutscheinAusgabe[],
            kontext.einloesungen as GutscheinEinloesung[],
          );
    const vergabe = await vergebeBelegnummer(geraeteSpeicher, localStorage, kassenId);
    const nummer = vergabe.nummer;
    const belegnummer = zahlungsart === 'training' ? nummer.replace(/^B-/, 'T-') : nummer;
    const empfaenger = empfaengerFuer(summeBrutto(zuZahlen), kEmpfaenger).empfaenger;
    const vorgang = { id: crypto.randomUUID(), zeitpunkt: new Date().toISOString() };
    const auftrag: BelegAuftrag = {
      belegtyp: zahlungsart === 'training' ? 'TRAINING' : 'STANDARD',
      belegnummer,
      positionen: [...zuZahlen],
      ...(zahlungsart === 'training' ? {} : { zahlungsart }),
      ...(zahlungsreferenz === undefined ? {} : { zahlungsreferenz }),
      ...(empfaenger === undefined ? {} : { empfaenger }),
      ...(kNotiz.trim() === '' ? {} : { notiz: kNotiz.trim() }),
      ...(bedienerName === null ? {} : { bediener: bedienerName }),
      // Der Server prüft einen Kundenrabatt am Kundenstamm (Sicherheitspaket S3)
      ...(kKundeId === null || zahlungsart === 'training' ? {} : { kundeId: kKundeId }),
      ...(gegebenCent === undefined || gegebenCent === null ? {} : { gegebenCent }),
      ...(kGutscheine.einloesungen.length === 0
        ? {}
        : {
            gutscheine: kGutscheine.einloesungen.map((e) => ({
              code: e.code,
              betragCent: e.betragCent,
            })),
          }),
      ...(kGutscheine.ausgaben.length === 0 ? {} : { gutscheinAusgaben: kGutscheine.ausgaben }),
      vorgang,
    };
    // Ohne Verbindung wird der Auftrag gar nicht gesendet; der Datensatz hält das fest.
    const ohneVerbindung = !navigator.onLine;
    // Codex-Prüfung 11: Bon-Merker VOR dem Vorgangsdatensatz sichern – so trägt der Bon nach
    // jedem Abbruch seine Vorgangs-ID (fehlt der Datensatz, wurde nie gesendet → frei)
    const marker = {
      belegnummer,
      positionen: [...zuZahlen],
      vorgangId: vorgang.id,
      ...(zahlungsreferenz !== undefined && karteFremd() ? { uebernommen: true as const } : {}),
    };
    setOffenerBeleg(marker);
    sichereSofort({ offenerBeleg: marker });
    try {
      await sichereVorgang(geraeteSpeicher, {
        kassenId,
        belegnummer,
        vorgang,
        auftrag,
        offlineErlaubt: vergabe.offlineErlaubt,
        ...(ohneVerbindung ? { ohneVerbindung: true as const } : {}),
        sitzung: sitzungsId,
      });
    } catch (e: unknown) {
      // kein Datensatz → kein Vorgang: Merker zurück (es wird nicht gesendet)
      setOffenerBeleg(null);
      sichereSofort({ offenerBeleg: null });
      throw e;
    }
    return { belegnummer, vorgang, auftrag, ohneVerbindung };
  }

  /**
   * Fehlerbericht 25.10.2026: Ein ungeklärter Vorgang – auch aus einem anderen Fenster – wird
   * zum offenen Bon dieses Fensters; „Beleg prüfen“ klärt genau ihn, kassiert wird erst danach.
   */
  function uebernimmOffenenVorgang(d: VorgangsDatensatz): void {
    // Codex-Prüfung 4: übernommen (nicht der Bon dieses Fensters) – nach der Klärung bleibt der
    // eigene Bon unberührt
    // Codex-Prüfung 10: Vorgang dieser Browsersitzung (Abbruch vor dem Bon-Merker, Neuladen,
    // Duplikat) – sein Bon ist dieser, nach der Klärung wird er abgeschlossen
    const marker = {
      belegnummer: d.belegnummer,
      positionen: [...(d.auftrag.positionen ?? [])],
      vorgangId: d.vorgang.id,
      ...(d.sitzung === sitzungsId ? {} : { uebernommen: true as const }),
    };
    setOffenerBeleg(marker);
    sichereSofort({ offenerBeleg: marker });
  }

  /**
   * Codex-Runde 1 (K3/K4): Reservierung des eigenen Kassierwegs (Kartenstart, Lieferdienst,
   * Altbestands-Bon) – nur diese gibt dieses Fenster frei, und nur sie nimmt `belegErstellen` für
   * den Beleg der eigenen Kartenzahlung aus.
   */
  const eigeneReservierung = useRef<{ art: Kassierreservierung['art']; id: string } | null>(null);

  /**
   * Codex-Prüfung 4: Besitz einer Reservierung (Kartenstart, Lieferdienst) – der Bon dieses
   * Fensters ist der reservierte Einkauf. Belegt durch den Bon-Merker `reservierung` UND eine
   * gehaltene Web-Lock `bilanx.besitz.<Kasse>.<ID>`: Ein dupliziertes Fenster oder ein neues
   * Fenster, das den dauerhaften Merker eines noch offenen Fensters liest, bekommt die Lock nicht
   * und ist damit nie Besitzer (sein Bon ist eine Kopie und wird geleert).
   */
  const [reservierungMarker, setReservierungMarker] = useState<{ art: string; id: string } | null>(
    () => gesichert?.reservierung ?? null,
  );
  /**
   * Codex-Prüfung 6: Trägt der Bon beim Öffnen einen Reservierungs-Merker, ist bis zur
   * Besitzentscheidung offen, ob er eine Kopie ist – bis dahin schreibt dieses Fenster den
   * gemeinsamen dauerhaften Merker nicht (keine ältere Momentaufnahme über den Stand des
   * Besitzers).
   */
  const besitzGeklaert = useRef(gesichert?.reservierung == null);
  /** Reservierung eines anderen Fensters, die dieses Fenster nur klärt. */
  const [fremdeKlaerung, setFremdeKlaerung] = useState<{ art: string; id: string } | null>(
    () => gesichert?.uebernommen ?? null,
  );
  /** Belegkontext (Kunde, Gutscheine, Empfänger, Notiz) einer übernommenen Kartenzahlung. */
  const fremdKontext = useRef<GesicherterBon | null>(null);
  /** Ausgang der Kartenzahlung dieses Fensters, sobald er feststeht (für die Freigabe). */
  const kartenAusgang = useRef<ReservierungsAusgang['ergebnis'] | null>(null);
  /** Bon dieses Fensters stammt aus dem dauerhaften Merker (keine Sitzung: Neustart/neues Fenster). */
  const [ausDauerhaftem] = useState(() => {
    try {
      return (sessionStorage.getItem(`bilanx.bon.${kassenId}`) ?? '') === '';
    } catch {
      return true;
    }
  });
  const besitzFreigabe = useRef<(() => void) | null>(null);

  /** Besitz-Lock erwerben (ohne Warten); false = ein anderes Fenster besitzt die Reservierung. */
  /** Codex-Prüfung 5: Lauf der Komponente – eine Lock nach dem Abbau wird sofort freigegeben. */
  const montiert = useRef(true);
  /** ID der Reservierung, deren Besitz-Lock dieses Fenster hält. */
  const besitzId = useRef<string | null>(null);
  function besitzErwerben(id: string): Promise<boolean> {
    const locks = (globalThis.navigator as Navigator | undefined)?.locks as
      LockManager | null | undefined;
    if (locks === undefined || locks === null) {
      return Promise.resolve(false);
    }
    // schon gehalten (z. B. zweiter Lauf beim Öffnen): nicht freigeben und neu anfordern
    if (besitzId.current === id && besitzFreigabe.current !== null) {
      return Promise.resolve(true);
    }
    besitzFreigabe.current?.();
    besitzId.current = null;
    return new Promise<boolean>((ergebnis) => {
      void locks
        .request(`bilanx.besitz.${kassenId}.${id}`, { ifAvailable: true }, async (lock) => {
          if (lock === null || !montiert.current) {
            // nicht erhalten bzw. Komponente inzwischen abgebaut: Lock sofort wieder frei
            ergebnis(false);
            return;
          }
          besitzId.current = id;
          ergebnis(true);
          await new Promise<void>((frei) => {
            besitzFreigabe.current = frei;
          });
        })
        .catch(() => {
          ergebnis(false);
        });
    });
  }

  /**
   * Codex-Prüfung 5 (BLOCKER): Verkauften Einkauf abschließen und den Reservierungs-Merker in
   * EINER Sicherung entfernen – nie ein noch gefüllter Bon ohne Merker auf dem Datenträger (ein
   * Abbruch dazwischen hinterließe einen kassierbaren Bon ohne Ausgangsprüfung).
   */
  function verkauftAbschliessenOhneMarker(verkauft: readonly Position[]): void {
    const b = sicherungRef.current;
    const rest = restNachZahlung(b.positionen as Position[], verkauft);
    // Codex-Prüfung 6: beim Teilen auch den Bonierstand wie teilungAbschliessen (ohne Storno)
    const bonPos = b.positionen as Position[];
    const bonBoniert = b.boniert as BonierStand[];
    sichereSofort(
      rest.length > 0
        ? {
            positionen: rest,
            boniert: abziehen(verbuche(bonBoniert, bonierDiff(bonPos, bonBoniert)), verkauft),
            reservierung: null,
          }
        : {
            positionen: [],
            aktiverTisch: null,
            notiz: '',
            boniert: [],
            empfaenger: KEIN_EMPFAENGER,
            kundeEmail: null,
            gutscheinAusgaben: [],
            einloesungen: [],
            kunde: null,
            reservierung: null,
          },
    );
    teilungAbschliessen(verkauft);
    setzeMarker(null);
  }

  /** Bon-Merker setzen (mit Besitz-Lock) bzw. entfernen. */
  function setzeMarker(m: { art: string; id: string } | null): void {
    setReservierungMarker(m);
    sichereSofort({ reservierung: m });
    if (m === null) {
      besitzFreigabe.current?.();
      besitzFreigabe.current = null;
      besitzId.current = null;
    }
  }
  useEffect(() => {
    montiert.current = true;
    return () => {
      montiert.current = false;
      besitzFreigabe.current?.();
      besitzFreigabe.current = null;
      besitzId.current = null;
    };
  }, []);

  /** Momentaufnahme des Bons für eine Reservierung (ohne E-Mail-Adresse, ohne offene Teile). */
  function bonMomentaufnahme(zuZahlen: readonly Position[]): GesicherterBon {
    return {
      ...sicherungRef.current,
      kundeEmail: null,
      offeneZahlung: null,
      startUnklarSeit: null,
      startVorgang: null,
      offenerBeleg: null,
      reservierung: null,
      uebernommen: null,
      zahlungPositionen: [...zuZahlen],
    };
  }

  /** Eigenen Bon aus seiner Momentaufnahme wiederherstellen (nach Neustart: minimaler Bon). */
  function bonWiederherstellen(b: GesicherterBon): void {
    setPositionen(b.positionen as Position[]);
    setAktiverTisch(b.aktiverTisch);
    setNotiz(b.notiz);
    setBoniert(b.boniert as BonierStand[]);
    setEmpfaengerEingabe(b.empfaenger);
    setGutscheinAusgaben(b.gutscheinAusgaben as GutscheinAusgabe[]);
    setEinloesungen(b.einloesungen as GutscheinEinloesung[]);
    setKundeId(b.kunde?.id ?? null);
    setKundenRabatt(b.kunde?.rabattProzent ?? null);
  }

  /** Ist die Kartenzahlung in diesem Fenster nur übernommen (nicht der Bon hier)? */
  function karteFremd(): boolean {
    return sicherungRef.current.uebernommen?.art === 'karte';
  }

  /**
   * Ungeklärter Verkauf eines anderen Kassierwegs bzw. Fensters (VerkaufUngeklaert): in dieses
   * Fenster übernehmen, damit es ihn klären kann („Beleg prüfen“, „Zahlung prüfen“, „Erfassung
   * prüfen“). Kassiert wird erst danach. Codex-Prüfung 4: Nur der Besitzer behandelt den
   * Einkauf als seinen Bon; ein anderes Fenster klärt nur – sein eigener Bon bleibt unberührt.
   */
  function uebernimmUngeklaert(e: VerkaufUngeklaert, alsEigen?: boolean): void {
    if (e.offen !== null) {
      uebernimmOffenenVorgang(e.offen);
      return;
    }
    const r = e.reservierung;
    if (r === null) {
      return;
    }
    const m = sicherungRef.current.reservierung;
    const eigen = alsEigen ?? (m?.art === r.art && m.id === r.id);
    const momentaufnahme =
      r.art !== 'altbon' && r.bon !== undefined
        ? ladeBon({ getItem: () => JSON.stringify(r.bon), setItem: () => undefined }, kassenId)
        : null;
    if (eigen) {
      // Eigener Bon nach einem Neustart: vollständig aus der Momentaufnahme (Fund 3)
      if (ausDauerhaftem && momentaufnahme !== null) {
        bonWiederherstellen(momentaufnahme);
      }
      fremdKontext.current = null;
    } else {
      fremdKontext.current = momentaufnahme;
      if (r.art !== 'altbon') {
        const u = { art: r.art, id: r.id };
        setFremdeKlaerung(u);
        sichereSofort({ uebernommen: u });
      }
    }
    eigeneReservierung.current = { art: r.art, id: r.id };
    if (r.art === 'karte') {
      // Klärung über die Vorgangs-ID des Starts (P13) bzw. die Zahlungsreferenz: „Zahlung prüfen“
      if (offeneZahlung === null && startUnklarSeit === null) {
        setZahlungPositionen(r.positionen as Position[]);
        if (r.zahlungsreferenz !== undefined) {
          // Stand holt „Zahlung prüfen“ vom Server (nur die Referenz wird gebraucht)
          const z: Zahlung = {
            zahlungsreferenz: r.zahlungsreferenz,
            kassenId,
            anbieter: '',
            betragCent: 0,
            status: 'ausstehend',
            details: {},
          };
          setOffeneZahlung(z);
          sichereSofort({ offeneZahlung: z, zahlungPositionen: r.positionen });
        } else {
          setStartUnklarSeit(r.seit);
          setStartVorgang(r.ohneStartId === true ? null : r.id);
          sichereSofort({
            startUnklarSeit: r.seit,
            startVorgang: r.ohneStartId === true ? null : r.id,
            zahlungPositionen: r.positionen,
          });
        }
      }
    } else if (r.art === 'plattform') {
      setOffenePlattform(r);
    } else if (offenerBeleg === null) {
      const marker = {
        belegnummer: r.id,
        positionen: r.positionen as Position[],
        uebernommen: true as const,
      };
      setOffenerBeleg(marker);
      sichereSofort({ offenerBeleg: marker });
    }
  }

  /**
   * Nach dem Beleg den Bon abschließen. Codex-Prüfung 4: Beleg einer Kartenzahlung, die dieses
   * Fenster nur für ein anderes klärt – dessen Einkauf ist nicht der Bon hier, er bleibt
   * unberührt; der Ausgang „verkauft“ wird für die Freigabe der Reservierung gemerkt.
   */
  function abschliessenNachBeleg(zuZahlen: readonly Position[], zahlungsreferenz?: string): void {
    if (zahlungsreferenz !== undefined) {
      kartenAusgang.current = 'verkauft';
      if (karteFremd()) {
        return;
      }
    }
    teilungAbschliessen(zuZahlen);
  }

  /**
   * Beleg erstellen; bei vergebener Nummer (409) mit der nächsten erneut versuchen.
   * Die gesendete Nummer wird vorher in der Sitzung gesichert: geht die Antwort
   * verloren oder wird neu geladen, klärt die Kasse erst, ob der Beleg entstanden
   * ist, bevor wieder kassiert werden kann (sonst droht ein Doppelbeleg).
   */
  async function belegErstellen(
    zahlungsart: 'bar' | 'karte' | 'gutschein' | 'training',
    zahlungsreferenz?: string,
    gegebenCent?: number | null,
    zuZahlen: readonly Position[] = positionenZumZahlen,
  ): Promise<void> {
    if (zahlungsart === 'training' && (mitGutschein || gutscheinStand.ausgaben.length > 0)) {
      throw new Error('Trainingsbelege verkaufen oder lösen keine Gutscheine ein');
    }
    if (props.verkaufsstopp != null) {
      throw new Error(props.verkaufsstopp);
    }
    if (parkLaeuft.current) {
      throw new Error('Der Bon wird gerade geparkt – nicht kassiert');
    }
    // Codex-Prüfung 4: Trägt der Bon eine Reservierung (Karte, Lieferdienst), zuerst ihren
    // Ausgang prüfen – ein anderswo schon verkaufter Einkauf wird nie erneut kassiert. Der Beleg
    // der eigenen Kartenzahlung (mit Zahlungsreferenz) gehört zur Reservierung selbst.
    if (zahlungsreferenz === undefined && !(await pruefeEigeneReservierung())) {
      throw new Error(
        'Dieser Einkauf ist noch ungeklärt bzw. bereits verkauft – nicht erneut kassieren',
      );
    }
    // Fehlerbericht 25.10.2026: Solange ein Verkauf dieses Fensters ungeklärt ist, entsteht kein
    // neuer Vorgang – auch nicht aus einem noch offenen Dialog oder über ein Tastenkürzel.
    if (offenerBeleg !== null) {
      throw new Error(
        `Verkauf ${offenerBeleg.belegnummer} noch ungeklärt. Bitte diesen Einkauf nicht erneut kassieren. Zuerst Belegstatus prüfen.`,
      );
    }
    for (let versuch = 0; versuch < 3; versuch++) {
      let anlage: Awaited<ReturnType<typeof vorgangAnlegen>>;
      try {
        // Fehlerbericht 25.10.2026: zentrale Kassiersperre unmittelbar vor der Vorgangsanlage,
        // über alle Fenster dieser Kasse nacheinander – liegt ein ungeklärter Vorgang vor, wird
        // weder eine Nummer vergeben noch etwas übertragen.
        // Codex-Runde 1 (K3): Nur der Beleg der eigenen Kartenzahlung darf an deren
        // Reservierung vorbei – jede andere Reservierung sperrt
        const eigene =
          zahlungsreferenz !== undefined && eigeneReservierung.current?.art === 'karte'
            ? eigeneReservierung.current
            : undefined;
        anlage = await mitKassiersperre(kassenId, async () => {
          await pruefeKeinOffenerVorgang(geraeteSpeicher, kassenId, eigene);
          return vorgangAnlegen(zahlungsart, zuZahlen, zahlungsreferenz, gegebenCent);
        });
      } catch (e: unknown) {
        // Unter der Nummer liegt schon ein Datensatz bzw. Abschlussnachweis (z. B. nach
        // unsicherem Zählerstand): nie überschreiben – nächste Nummer (Codex-Runde 5)
        if (e instanceof SchluesselBelegt) {
          continue;
        }
        if (e instanceof VerkaufUngeklaert) {
          uebernimmUngeklaert(e);
        }
        throw e;
      }
      const { belegnummer, vorgang, auftrag, ohneVerbindung } = anlage;
      // Geht die Antwort verloren, puffert die Kasse genau diesen Vorgang – hat der Server ihn
      // doch gespeichert, erkennt die Synchronisation ihn an der Vorgangs-ID als vorhanden.
      versuchteNummer.current = belegnummer;
      // N1 (Codex R3-2): Merker mit Vorgangs-ID – ohne Datensatz nie über die Nummer klären
      // Codex-Prüfung 5: Beleg einer nur übernommenen Kartenzahlung – nach „Beleg prüfen“ bleibt
      // der Bon dieses Fensters unberührt
      const marker = {
        belegnummer,
        positionen: [...zuZahlen],
        vorgangId: vorgang.id,
        ...(zahlungsreferenz !== undefined && karteFremd() ? { uebernommen: true as const } : {}),
      };
      setOffenerBeleg(marker);
      sichereSofort({ offenerBeleg: marker });
      try {
        // Fehlerbericht 25.10.2026: Ohne Verbindung wird nicht gesendet – der Auftrag erreicht
        // den Server dann sicher nicht (anders als bei einer verlorenen Antwort).
        if (ohneVerbindung) {
          throw new TypeError('Keine Verbindung – Verkauf nicht übertragen');
        }
        const neu = await api.erstelleBeleg(kassenId, auftrag);
        await schliesseVorgangAb(geraeteSpeicher, kassenId, belegnummer, vorgang.id, {
          art: 'gebucht',
          beleg: neu,
        }).catch(() => undefined);
        versuchteNummer.current = null;
        // Sofort nach dem Beleg (vor dem Belegtext): z. B. Kassenlade bei Barzahlung.
        nachBeleg.current?.(belegnummer);
        nachBeleg.current = null;
        // Bon sofort abschließen – nicht erst nach dem Drucktext (Neuladen dazwischen).
        setOffenerBeleg(null);
        abschliessenNachBeleg(zuZahlen, zahlungsreferenz);
        const text = await api.druck(kassenId, neu.belegnummer).catch(() => null);
        setBeleg({ beleg: neu, text });
        return;
      } catch (e: unknown) {
        // Vergebene Nummer: nächste versuchen. „Derselbe Vorgang“ meldet der Server nur zur
        // Information – ohne dauerhafte Vorgangs-ID beweist gleicher Inhalt keine Wiederholung
        // (Sicherheitspaket S2); eine verlorene Antwort klärt „Beleg prüfen“.
        if (e instanceof ApiFehler && e.status === 409 && e.message.includes('Belegnummer')) {
          // Die Nummer gehört einem anderen Beleg – dieser Vorgang kann sie nie belegen. Abschluss
          // „nicht gebucht“ nur nach Bestätigung am Server (sonst bleibt der Datensatz offen; die
          // Wiederaufnahme schließt ihn später ab). Neue Nummer = neuer Vorgang.
          versuchteNummer.current = null;
          // Codex-Prüfung 15: nur wenn dieser Vorgang jetzt sicher „nicht gebucht“ ist, mit neuer
          // Nummer weiter. Sonst (inzwischen bezahlt bestätigt, ersetzt oder gebucht – z. B. in
          // einem anderen Fenster – bzw. Server nicht erreichbar) endet das Kassieren hier: der
          // Bon bleibt mit seinem Vorgang offen, „Beleg prüfen“ klärt ihn
          if (await abschliessenNichtGebucht(belegnummer, vorgang.id, e.message)) {
            continue;
          }
          throw new Error(
            `Verkauf ${belegnummer} noch ungeklärt. Bitte diesen Einkauf nicht erneut kassieren. Zuerst Belegstatus prüfen.`,
            { cause: e },
          );
        }
        if (e instanceof ApiFehler && e.status === 409 && e.message.includes('bereits mit Beleg')) {
          // Zahlung schon abgerechnet (z. B. Wiederholung): vorhandenen Beleg anzeigen.
          const nummer = /Beleg (\S+) abgerechnet/.exec(e.message)?.[1];
          if (nummer !== undefined) {
            setOffenerBeleg(null);
            abschliessenNachBeleg(zuZahlen, zahlungsreferenz);
            const vorhanden = await api.beleg(kassenId, nummer);
            // Dieser Vorgang ist über den vorhandenen Beleg der Zahlung abgeschlossen
            await schliesseVorgangAb(geraeteSpeicher, kassenId, belegnummer, vorgang.id, {
              art: 'gebucht',
              beleg: vorhanden,
            }).catch(() => undefined);
            const text = await api.druck(kassenId, nummer).catch(() => null);
            setBeleg({ beleg: vorhanden, text });
            return;
          }
        }
        // Preis weicht vom Artikelstamm ab (S3): Stamm neu laden, damit neue Kacheln stimmen
        if (e instanceof ApiFehler && e.message.includes('Artikelstamm')) {
          stammNeuLaden();
        }
        // Fachliche Ablehnung (4xx): der Server hat sicher nichts angelegt. Bei Netz- und
        // Serverfehlern (5xx, z. B. „Speicherergebnis unklar“) bleibt der Merker – der Beleg
        // kann trotzdem entstanden sein; „Beleg prüfen“ klärt das (Sicherheitspaket S2).
        if (!istNetzfehler(e) && !(e instanceof ApiFehler && e.status >= 500)) {
          // Erster und einziger Sendeversuch fachlich abgelehnt: erst nach Bestätigung am Server
          // („verworfen“) freigeben – sonst bleibt der Bon zur Klärung (Codex-Runde 5)
          const grund = e instanceof Error ? e.message : String(e);
          if (await abschliessenNichtGebucht(belegnummer, vorgang.id, grund)) {
            setOffenerBeleg(null);
            versuchteNummer.current = null;
          }
        }
        throw e;
      }
    }
    throw new Error('Keine freie Belegnummer gefunden');
  }

  /**
   * W-7 (Welle 2): Trainingsbeleg bzw. Gutschein-Abschluss nur einmal zugleich und nie neben
   * einem anderen Kassierweg – die Sperre greift vor dem nächsten Rendern (wie `barSperre`).
   */
  function direktBelegGesperrt(): boolean {
    return (
      direktBelegSperre.current ||
      barSperre.current ||
      laeuftRef.current ||
      parkLaeuft.current ||
      plattformLaeuft.current
    );
  }

  async function trainingsbeleg(): Promise<void> {
    if (direktBelegGesperrt()) {
      return;
    }
    direktBelegSperre.current = true;
    setLaeuft(true);
    setFehler(null);
    try {
      await belegErstellen('training');
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      direktBelegSperre.current = false;
      setLaeuft(false);
    }
  }

  /** Online bezahlte Lieferdienst-Bestellung (Modul lieferdienste): kein Beleg. */
  const [plattformDialog, setPlattformDialog] = useState(false);
  const [plattformMeldung, setPlattformMeldung] = useState<string | null>(null);
  /** Doppeltipp auf „erfassen“ darf nur eine Anfrage auslösen (State-Update kommt erst beim nächsten Rendern). */
  const plattformLaeuft = useRef(false);
  /**
   * Codex-Runde 1 (K4): Lieferdienst-Erfassung ohne Antwort (Netz, 5xx) – bis zur Klärung
   * („Erfassung prüfen“ wiederholt genau dieselbe Erfassung; am Server idempotent) wird über
   * keinen Weg kassiert, sonst entstünden für denselben Einkauf zwei Umsätze.
   */
  /** Codex-Prüfung 8: in diesem Fenster vom Server bestätigte Erfassungen (Reservierungs-IDs). */
  const plattformBestaetigt = useRef(new Set<string>());
  const [offenePlattform, setOffenePlattform] = useState<Extract<
    Kassierreservierung,
    { art: 'plattform' }
  > | null>(null);

  async function plattformErfassen(a: {
    plattform: UmsatzPlattform;
    externeId: string;
    lieferart: 'lieferung' | 'abholung';
  }): Promise<void> {
    if (plattformLaeuft.current || parkLaeuft.current || direktBelegSperre.current) {
      return;
    }
    plattformLaeuft.current = true;
    setLaeuft(true);
    setFehler(null);
    const zuZahlen = [...positionenZumZahlen];
    try {
      // Codex-Prüfung 4: trägt der Bon eine Reservierung, zuerst ihren Ausgang prüfen
      if (!(await pruefeEigeneReservierung())) {
        setPlattformDialog(false);
        return;
      }
      // Fehlerbericht 25.10.2026: auch keine Lieferdienst-Erfassung bei ungeklärtem Verkauf
      if (offenerBeleg !== null) {
        throw new Error(
          `Verkauf ${offenerBeleg.belegnummer} noch ungeklärt. Bitte diesen Einkauf nicht erneut kassieren. Zuerst Belegstatus prüfen.`,
        );
      }
      const erfassung = {
        ...a,
        positionen: zuZahlen,
        ...(bedienerName === null ? {} : { bediener: bedienerName }),
        ...(notiz.trim() === '' ? {} : { notiz: notiz.trim() }),
      };
      const reservierung = {
        kassenId,
        art: 'plattform' as const,
        id: `${a.plattform}:${a.externeId}`,
        seit: new Date().toISOString(),
        positionen: zuZahlen,
        erfassung,
        bon: bonMomentaufnahme(zuZahlen),
      };
      // Codex-Runde 1 (K4): Prüfung und dauerhafte Reservierung unter der Kassiersperre – ohne
      // gespeicherte Reservierung (und ohne Besitz) wird nicht erfasst
      try {
        await mitKassiersperre(kassenId, async () => {
          await pruefeKeinOffenerVorgang(geraeteSpeicher, kassenId);
          if (!(await besitzErwerben(reservierung.id))) {
            throw new Error('Reservierung nicht möglich (Fenstersperre) – nicht erfasst');
          }
          // Codex-Prüfung 12: Bon-Merker vor der Reservierung (ein Abbruch dazwischen lässt nie
          // einen Bon ohne Verknüpfung zu einer bestehenden Reservierung zurück)
          setzeMarker({ art: 'plattform', id: reservierung.id });
          try {
            await reserviereKassieren(geraeteSpeicher, reservierung);
          } catch (e: unknown) {
            setzeMarker(null);
            throw e;
          }
        });
      } catch (e: unknown) {
        if (e instanceof VerkaufUngeklaert) {
          setPlattformDialog(false);
          uebernimmUngeklaert(e);
        }
        throw e;
      }
      eigeneReservierung.current = { art: 'plattform', id: reservierung.id };
      await plattformSenden(reservierung, true);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      plattformLaeuft.current = false;
      setLaeuft(false);
    }
  }

  /**
   * Erfassung senden (erstmals oder als Klärung). Antwort bzw. fachliche Ablehnung → Reservierung
   * frei; ohne Antwort bleibt sie – „Erfassung prüfen“ wiederholt genau diese Nutzdaten.
   */
  async function plattformSenden(
    r: Extract<Kassierreservierung, { art: 'plattform' }>,
    erstversuch = false,
  ): Promise<void> {
    let u: { plattform: UmsatzPlattform; externeId: string; summeCent: number };
    if (plattformBestaetigt.current.has(r.id)) {
      // Codex-Prüfung 8: in diesem Fenster schon vom Server bestätigt – nur noch festhalten
      const e = r.erfassung as { plattform: UmsatzPlattform; externeId: string };
      u = {
        plattform: e.plattform,
        externeId: e.externeId,
        summeCent: summeBrutto(r.positionen as Position[]),
      };
    } else {
      try {
        u = await api.plattformumsatzErfassen(
          kassenId,
          r.erfassung as Parameters<KassenApi['plattformumsatzErfassen']>[1],
        );
      } catch (e: unknown) {
        return plattformFehler(r, e, erstversuch);
      }
      plattformBestaetigt.current.add(r.id);
    }
    // Codex-Prüfung 7: Ab hier ist die Erfassung am Server bestätigt. Ein lokaler Fehler beim
    // Festhalten darf nie „nicht verkauft“ ergeben – die Reservierung bleibt dann bestehen (der
    // Einkauf bleibt gesperrt), und „Erfassung prüfen“ wiederholt die Erfassung (am Server
    // idempotent) und das Festhalten.
    try {
      // Codex-Prüfung 4: Ausgang dauerhaft festhalten – ein anderes Fenster mit diesem Bon
      // (Neuladen, Duplikat, Neustart) schließt ihn dann ab
      const tatsaechlich = await gibReservierungFrei(
        geraeteSpeicher,
        kassenId,
        'plattform',
        r.id,
        'verkauft',
      );
      if (tatsaechlich === 'nicht_verkauft') {
        // Codex-Prüfung 10: inzwischen anderswo als nicht erfasst freigegeben, aber die Erfassung
        // ist am Server – den Bon hier trotzdem abschließen und deutlich melden
        setFehler(
          `Achtung: Lieferdienst-Bestellung ${u.externeId} ist erfasst, wurde aber zuvor als „nicht erfasst“ freigegeben – im Backoffice prüfen, ob der Einkauf zusätzlich kassiert wurde.`,
        );
      }
    } catch (e: unknown) {
      setPlattformDialog(false);
      setOffenePlattform(r);
      throw new Error(
        `Lieferdienst-Bestellung ${u.externeId} ist erfasst, aber der Gerätespeicher hat den Ausgang nicht festgehalten (${e instanceof Error ? e.message : String(e)}). Bitte diesen Einkauf nicht kassieren – „Erfassung prüfen“ wiederholen.`,
        { cause: e },
      );
    }
    plattformBestaetigt.current.delete(r.id);
    setOffenePlattform(null);
    eigeneReservierung.current = null;
    setPlattformDialog(false);
    plattformAusgangUebernehmen(r.id, 'verkauft', r.positionen as Position[]);
    setPlattformMeldung(
      `${PLATTFORM_NAME[u.plattform]}-Bestellung ${u.externeId} erfasst (${formatiereEuro(u.summeCent)} €) – online bezahlt, kein Registrierkassenbeleg`,
    );
  }

  /** Fehler der Lieferdienst-Erfassung selbst (Serveraufruf) – nie ein lokaler Speicherfehler. */
  async function plattformFehler(
    r: Extract<Kassierreservierung, { art: 'plattform' }>,
    e: unknown,
    erstversuch: boolean,
  ): Promise<never> {
    const offenLassen = (meldung: string): never => {
      setPlattformDialog(false);
      setOffenePlattform(r);
      throw new Error(meldung, { cause: e });
    };
    if (istNetzfehler(e) || (e instanceof ApiFehler && e.status >= 500)) {
      // Antwort fehlt: die Erfassung kann am Server angekommen sein – gesperrt bis zur Klärung
      return offenLassen(
        `Lieferdienst-Erfassung ${r.id} ohne Antwort. Bitte diesen Einkauf nicht erneut kassieren. Zuerst „Erfassung prüfen“.`,
      );
    }
    if (!(e instanceof ApiFehler)) {
      // Kein Serverbescheid (z. B. Programmfehler, Verkaufsstopp): unklar, nie „nicht verkauft“
      return offenLassen(
        `Lieferdienst-Erfassung ${r.id} ungeklärt (${e instanceof Error ? e.message : String(e)}). Bitte diesen Einkauf nicht erneut kassieren.`,
      );
    }
    // 409 mit vorhandener Bestellung: der Einkauf ist (online) verkauft
    let freigabeVon: string | undefined;
    // 409 mit vorhandener Bestellung: verkauft; 409 „gesperrt“ (Migration 0084): die Bestellung
    // ist am Server endgültig als nicht erfasst gesperrt – sie kann nie mehr erfasst werden
    let ergebnis: ReservierungsAusgang['ergebnis'] | null =
      e.status === 409 && e.daten?.vorhanden === true
        ? 'verkauft'
        : e.status === 409 && e.daten?.gesperrt === true
          ? 'nicht_verkauft'
          : null;
    if (ergebnis === null && erstversuch) {
      // Erster und einziger Aufruf fachlich abgelehnt: nichts erfasst
      ergebnis = 'nicht_verkauft';
    }
    if (ergebnis === null) {
      // Codex-Prüfung 8: Bei einer Wiederholung beweist eine Ablehnung (z. B. Anmeldung, Recht,
      // Version) nichts über den früheren Aufruf – Ausgang über die erfassten Bestellungen klären
      ergebnis = await plattformAusgangSuchen(r);
      // Option A (Nutzer, 25.10.2026): nur eine benannte Person nach Prüfung im Backoffice darf die
      // Bestellung als nicht erfasst abschließen – und nur über die serverseitige Sperre
      // (Migration 0084): sie ist atomar gegenüber jeder, auch verspäteten, Erfassung derselben
      // Bestellung. Ist sie inzwischen erfasst, gilt „verkauft“; scheitert die Sperre, bleibt es
      // ungeklärt. Nie automatisch.
      if (ergebnis === null) {
        const ext = r.erfassung as { plattform: UmsatzPlattform; externeId: string };
        if (
          window.confirm(
            `Lieferdienst-Bestellung ${ext.externeId}: Ausgang ungeklärt (${e.message}). Nur wenn im Backoffice unter „Lieferdienste“ sicher KEINE Erfassung dieser Bestellung besteht, mit OK endgültig als nicht erfasst sperren (danach ist keine Erfassung mehr möglich).`,
          )
        ) {
          const von = (bedienerName ?? window.prompt('Wer hat geprüft? (Name)') ?? '').trim();
          if (von !== '') {
            try {
              const gesperrt = await api.plattformumsatzSperren(kassenId, {
                plattform: ext.plattform,
                externeId: ext.externeId,
                vorgangId: r.id,
                grund: `Im Backoffice geprüft: nicht erfasst (${e.message})`.slice(0, 300),
                geprueftVon: von,
              });
              ergebnis = gesperrt.stand === 'gesperrt' ? 'nicht_verkauft' : 'verkauft';
              freigabeVon = von;
            } catch {
              // Sperre nicht bestätigt: ungeklärt lassen
            }
          }
        }
      }
      if (ergebnis === null) {
        return offenLassen(
          `Lieferdienst-Erfassung ${r.id} weiter ungeklärt (${e.message}). Bitte diesen Einkauf nicht erneut kassieren – nach Anmeldung erneut „Erfassung prüfen“.`,
        );
      }
    }
    let tatsaechlich: ReservierungsAusgang['ergebnis'] | null;
    try {
      tatsaechlich = await gibReservierungFrei(
        geraeteSpeicher,
        kassenId,
        'plattform',
        r.id,
        ergebnis,
        freigabeVon,
      );
    } catch (f: unknown) {
      // Ausgang nicht festgehalten: Reservierung (Sperre) bleibt, Klärung wiederholbar
      setPlattformDialog(false);
      setOffenePlattform(r);
      throw new Error(
        `${e.message} – Ausgang im Gerätespeicher nicht festgehalten (${f instanceof Error ? f.message : String(f)}); „Erfassung prüfen“ wiederholen`,
        { cause: f },
      );
    }
    // Codex-Prüfung 10: maßgeblich ist der tatsächlich gespeicherte Ausgang (ein anderes Fenster
    // kann inzwischen „verkauft“ festgehalten haben); ohne Ausgang bleibt es ungeklärt
    if (tatsaechlich === null) {
      return offenLassen(
        `Lieferdienst-Erfassung ${r.id} ungeklärt (Ausgang nicht festgehalten). Bitte diesen Einkauf nicht erneut kassieren.`,
      );
    }
    setOffenePlattform(null);
    eigeneReservierung.current = null;
    if (e.status === 409) {
      setPlattformDialog(false);
    }
    plattformAusgangUebernehmen(r.id, tatsaechlich, r.positionen as Position[]);
    throw e;
  }

  /**
   * Codex-Prüfung 8/9: Ist die Bestellung am Server erfasst? Sucht sie in den Lieferdienst-
   * Umsätzen vom Vortag der Reservierung bis heute. Gefunden → verkauft; sonst null (unklar) –
   * „nicht gefunden“ beweist nie „nicht erfasst“, das klärt nur die Serversperre (Migration 0084).
   */
  async function plattformAusgangSuchen(
    r: Extract<Kassierreservierung, { art: 'plattform' }>,
  ): Promise<'verkauft' | null> {
    const e = r.erfassung as { plattform: UmsatzPlattform; externeId: string };
    const tag = (d: Date) => d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Vienna' });
    // Codex-Stop-Prüfung: JEDEN Wiener Tag von der Reservierung bis heute abfragen – die Erfassung
    // kann an jedem dazwischenliegenden Tag erfolgt sein (Wiederholung über Mitternacht);
    // mehr als 8 Tage: nicht sicher klärbar → gesperrt lassen
    const tage: string[] = [];
    const heute = tag(new Date());
    for (let t = Date.parse(r.seit) - 24 * 3_600_000; tage.at(-1) !== heute; t += 12 * 3_600_000) {
      const d = tag(new Date(Math.min(t, Date.now())));
      if (tage.at(-1) !== d) {
        tage.push(d);
      }
      if (tage.length > 9) {
        return null;
      }
    }
    try {
      for (const datum of tage) {
        const { umsaetze } = await api.plattformumsaetze(kassenId, datum);
        if (
          umsaetze.some(
            (x) =>
              x.plattform === e.plattform &&
              x.externeId.toLowerCase() === e.externeId.toLowerCase() &&
              x.stornoVon === null,
          )
        ) {
          return 'verkauft';
        }
      }
    } catch {
      return null;
    }
    // Codex-Prüfung 9: „nicht gefunden“ beweist nichts (Aufruf evtl. noch in Arbeit, andere
    // Kasse, Schreibweise) – nie automatisch „nicht verkauft“
    return null;
  }

  /**
   * Ausgang einer Lieferdienst-Reservierung in diesem Fenster übernehmen: Besitzer schließt seinen
   * Bon (verkauft) bzw. gibt ihn frei; ein nur klärendes Fenster lässt seinen Bon unberührt.
   */
  function plattformAusgangUebernehmen(
    id: string,
    ergebnis: ReservierungsAusgang['ergebnis'],
    positionen: readonly Position[],
  ): void {
    const m = sicherungRef.current.reservierung;
    if (m?.art === 'plattform' && m.id === id) {
      if (ergebnis === 'verkauft') {
        verkauftAbschliessenOhneMarker(positionen);
      } else {
        setzeMarker(null);
      }
    }
    if (sicherungRef.current.uebernommen?.id === id) {
      setFremdeKlaerung(null);
      sichereSofort({ uebernommen: null });
    }
  }

  async function plattformPruefen(): Promise<void> {
    const r = offenePlattform;
    if (r === null || plattformLaeuft.current) {
      return;
    }
    plattformLaeuft.current = true;
    setLaeuft(true);
    setFehler(null);
    try {
      await plattformSenden(r);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      plattformLaeuft.current = false;
      setLaeuft(false);
    }
  }

  async function gutscheinKassieren(): Promise<void> {
    if (direktBelegGesperrt()) {
      return;
    }
    direktBelegSperre.current = true;
    setLaeuft(true);
    setFehler(null);
    try {
      await belegErstellen('gutschein');
      anzeige.current?.sende({
        typ: 'bezahlt',
        summeCent: summe,
        gutscheinCent: summe - rest,
        zahlungsart: 'gutschein',
        gegebenCent: null,
        rueckgeldCent: null,
        trinkgeldCent: 0,
      });
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      direktBelegSperre.current = false;
      setLaeuft(false);
    }
  }

  /**
   * Fehlerbericht 25.10.2026 (Zustand B/C): Entscheidung an der Kasse. „Bezahlt“ bucht genau den
   * ursprünglichen Vorgang (gleiche Vorgangs-ID, gleiche Nummer); „nicht bezahlt“ hält ihn am
   * Server verbindlich als nicht gebucht fest (Grund mit Bediener und Zeit).
   */
  async function bezahlEntscheiden(bezahlt: boolean): Promise<void> {
    if (laeuftRef.current || bezahlFrage === null) {
      return;
    }
    // Codex-Runde 1 (M7): Wer entscheidet, muss benannt sein – ohne angemeldeten Bediener
    // ausdrücklich abfragen; ohne Namen keine Entscheidung (der Verkauf bleibt gesperrt)
    const von = (
      bedienerName ??
      window.prompt('Wer hat die Bezahlung geprüft? (Name)') ??
      ''
    ).trim();
    if (von === '') {
      setFehler(
        `Ohne Namen der prüfenden Person keine Entscheidung – Verkauf ${bezahlFrage.belegnummer} bleibt ungeklärt`,
      );
      return;
    }
    const zeit = new Date().toLocaleString('de-AT', { timeZone: 'Europe/Vienna' });
    laeuftRef.current = true;
    setLaeuft(true);
    try {
      if (bezahlt && bezahlFrage.offline) {
        await offlineNachEntscheidung(bezahlFrage.belegnummer, von);
        return;
      }
      await offenenBelegKlaeren(
        bezahlt
          ? { bezahlt: true, von }
          : {
              bezahlt: false,
              von,
              grund: `Bezahlung nicht angenommen bzw. Betrag zurückgegeben – an der Kasse bestätigt von ${von} (${zeit})`,
            },
      );
    } finally {
      laeuftRef.current = false;
      setLaeuft(false);
    }
  }

  /**
   * Codex-Runde 1 (K1): Offline gepuffert wird ein Barverkauf erst, nachdem ein Mensch die
   * Bezahlung bestätigt hat – die Bestätigung (Person, Zeit) wird vor dem Puffern dauerhaft am
   * Vorgang festgehalten. Die spätere Nachsignierung beruht auf dieser Entscheidung, nie allein
   * auf der wiederhergestellten Verbindung.
   */
  async function offlineNachEntscheidung(belegnummer: string, von: string): Promise<void> {
    const d = await ladeVorgang(geraeteSpeicher, kassenId, belegnummer).catch(() => null);
    if (d === null || d.abgeschlossen !== undefined) {
      setBezahlFrage(null);
      setFehler(`Verkauf ${belegnummer} ist nicht mehr offen – „Beleg prüfen“`);
      return;
    }
    // Codex-Bestätigungsprüfung F3: Bestätigung und Pufferübergabe gemeinsam unter der
    // Kassiersperre – kein anderes Fenster bucht oder ersetzt den Vorgang dazwischen
    await mitKassiersperre(kassenId, async () => {
      // W9-A4 (OE-7, Codex R1/R2): genau eine Prüfung, unter der Sperre und VOR der
      // Bezahlbestätigung; danach folgen Bestätigung und Puffern ohne weitere Bedienung. So
      // bleibt bei einer Sperre keine wirksame Bestätigung am Vorgang zurück (sie machte ihn bei
      // Wiederverbindung ohne neue Entscheidung buchbar); „Beleg prüfen“ entscheidet später neu.
      if (!(await offlineAbschlussErlaubt())) {
        setBezahlFrage(null);
        setFehler('Kasse nicht erreichbar – dieser Verkauf ist offline nicht möglich');
        return;
      }
      try {
        await halteBezahlungFest(geraeteSpeicher, kassenId, belegnummer, d.vorgang.id, {
          am: new Date().toISOString(),
          von,
          quelle: 'offline',
        });
      } catch (e: unknown) {
        setFehler(
          `Bezahlbestätigung nicht gespeichert (${e instanceof Error ? e.message : String(e)}) – Verkauf ${belegnummer} bleibt ungeklärt`,
        );
        return;
      }
      setBezahlFrage(null);
      versuchteNummer.current = belegnummer;
      await offlinePuffern(true);
    }).catch((e: unknown) => {
      setFehler(e instanceof Error ? e.message : String(e));
    });
  }

  async function barKassieren(gegebenCent: number | null = null): Promise<void> {
    // Synchron gesperrt: Doppel-Enter, Doppeltipp oder zwei Finger vor dem nächsten Rendern
    // erzeugen sonst zwei Belege mit verschiedenen Nummern (Prüfung Barzahlung, P-Sprint).
    if (barSperre.current || laeuftRef.current || parkLaeuft.current || direktBelegSperre.current) {
      return;
    }
    // Fehlerbericht 25.10.2026: kein zweiter Vorgang aus dem noch offenen Dialog
    if (offenerBeleg !== null) {
      setFehler(
        `Verkauf ${offenerBeleg.belegnummer} noch ungeklärt. Bitte diesen Einkauf nicht erneut kassieren. Zuerst Belegstatus prüfen.`,
      );
      return;
    }
    barSperre.current = true;
    try {
      await barKassierenEinmal(gegebenCent);
    } finally {
      barSperre.current = false;
    }
  }

  async function barKassierenEinmal(gegebenCent: number | null): Promise<void> {
    if (props.verkaufsstopp != null) {
      setFehler(props.verkaufsstopp);
      return;
    }
    setLaeuft(true);
    setFehler(null);
    const wechsel = rueckgeldCent(rest, gegebenCent);
    setRueckgeld(
      gegebenCent === null || wechsel === null ? null : { gegebenCent, rueckgeldCent: wechsel },
    );
    // Kassenlade bei Barzahlung öffnen (nur mit Bridge, nie bei Karte) – sobald der Beleg
    // gespeichert ist, nicht erst nach dem Belegtext. Ein Fehler stört den Verkauf nicht, wird
    // aber angezeigt (Lade dann mit dem Schlüssel öffnen).
    setLadeHinweis(null);
    const ladeAmGeraet = props.ladeAmGeraet;
    nachBeleg.current =
      props.bridgeDruck === undefined && ladeAmGeraet === undefined
        ? null
        : (nr: string) => {
            // Nativ mit Schlüssel je Beleg (Wiederholung öffnet nicht erneut), sonst Server-Weg.
            (ladeAmGeraet !== undefined
              ? ladeAmGeraet(schluesselLadeBeleg(kassenId, nr))
              : oeffneLade(api, kassenId)
            )
              .then((r) => {
                setLadeHinweis(r.ok === true ? null : r.text);
              })
              .catch((e: unknown) => {
                setLadeHinweis(
                  `Kassenlade nicht geöffnet: ${e instanceof Error ? e.message : String(e)}`,
                );
              });
          };
    try {
      await belegErstellen('bar', undefined, gegebenCent);
      setBarDialog(false);
      anzeige.current?.sende({
        typ: 'bezahlt',
        summeCent: summe,
        gutscheinCent: summe - rest,
        zahlungsart: 'bar',
        gegebenCent,
        rueckgeldCent: wechsel,
        trinkgeldCent: 0,
      });
    } catch (e: unknown) {
      // Ungeklärte Kartenzahlung bzw. Lieferdienst-Erfassung (Reservierung): Dialog schließen –
      // geklärt wird mit „Zahlung prüfen“ bzw. „Erfassung prüfen“, nicht im Bar-Dialog
      if (e instanceof VerkaufUngeklaert && e.offen === null && e.reservierung?.art !== 'altbon') {
        setBarDialog(false);
      }
      if (istNetzfehler(e)) {
        if (mitGutschein || gutscheinStand.ausgaben.length > 0) {
          setFehler('Kasse nicht erreichbar – Gutscheine können offline nicht gebucht werden');
          return;
        }
        // P1-3/P1-8/P1-14: puffern nur bei einem puffergeeigneten Ausfall, mit dauerhafter
        // Ablage und gesichertem Bonkopf. Sonst scheitert der Verkauf mit der bisherigen
        // Meldung; der offene Beleg bleibt zur Klärung („Beleg prüfen“) – kein Doppelbeleg.
        if (
          istPuffergeeignet(e, OFFLINE_ERWEITERT) &&
          // N1.11: gepuffert wird nur bei gewährtem dauerhaftem Speicher
          offlinePufferBereit(puffer, localStorage, kassenId, props.speicherDauerhaft === true) &&
          versuchteNummer.current !== null
        ) {
          // Codex-Runde 1 (K1): erst die Bezahlentscheidung, dann puffern
          const nummer = versuchteNummer.current;
          const d = await ladeVorgang(geraeteSpeicher, kassenId, nummer).catch(() => null);
          setBarDialog(false);
          setBezahlFrage({
            belegnummer: nummer,
            summeCent: summeBrutto(d?.auftrag.positionen ?? []),
            ohneVerbindung: d?.ohneVerbindung === true,
            offline: true,
          });
          setFehler(
            `Verkauf ${nummer} noch ungeklärt. Bitte diesen Einkauf nicht erneut kassieren. Zuerst Belegstatus prüfen.`,
          );
          return;
        }
      }
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      // Ohne gespeicherten Beleg keine Ladenöffnung (Fehler, offline gepuffert).
      nachBeleg.current = null;
      setLaeuft(false);
    }
  }

  /**
   * Kasse nicht erreichbar: den eben gesendeten Barverkauf puffern und vorläufigen Beleg ausgeben.
   * N1: aus dem gesicherten Vorgangsdatensatz – gleiche Nummer, gleiche Vorgangs-ID, gleicher
   * Inhalt und ursprünglicher Zeitpunkt wie der Online-Versuch.
   */
  /**
   * W9-A4 (OE-7): Offline-Abschluss nur, wenn der Puffer bereit ist (gemeldete Dauerhaftigkeit,
   * IndexedDB, Bonkopf) **und** der Browser jetzt `persisted() === true` meldet (Codex R1: der
   * beim Start gemeldete Stand allein ist kein aktueller Nachweis).
   */
  async function offlineAbschlussErlaubt(): Promise<boolean> {
    return (
      offlinePufferBereit(puffer, localStorage, kassenId, props.speicherDauerhaft === true) &&
      (await speicherJetztDauerhaft())
    );
  }

  /**
   * @param geprueft `offlineAbschlussErlaubt()` wurde eben unter derselben Kassiersperre geprüft
   *   (vor der Bezahlbestätigung) – keine zweite Prüfung, die nach gespeicherter Bestätigung
   *   scheitern und diese wirksam zurücklassen könnte (Codex R2).
   */
  async function offlinePuffern(geprueft = false): Promise<void> {
    // W9-A4 (OE-7, Nutzer 27.10.): Offline-Verkäufe nur auf Geräten mit nachweislich dauerhaftem
    // Speicher – Prüfung unmittelbar vor dem Abschluss, nicht nur beim Anbieten der
    // Bezahlentscheidung. Sonst bleibt der Verkauf offen zur Klärung (bestehende Meldung).
    if (!geprueft && !(await offlineAbschlussErlaubt())) {
      setFehler('Kasse nicht erreichbar – dieser Verkauf ist offline nicht möglich');
      return;
    }
    const nummer = versuchteNummer.current;
    const datensatz =
      nummer === null
        ? null
        : await ladeVorgang(geraeteSpeicher, kassenId, nummer).catch(() => null);
    if (datensatz === null) {
      setFehler(
        'Kasse nicht erreichbar und kein gesicherter Verkauf zum Puffern – „Beleg prüfen“, dann weiter kassieren',
      );
      return;
    }
    const empfaenger = datensatz.auftrag.empfaenger;
    const betrag = summeBrutto(datensatz.auftrag.positionen ?? []);
    // Eine vollständige Rechnung (über 400 €) lässt sich offline nicht prüfen (UID, Merkmale):
    // nicht puffern – ohne Empfänger kassieren (Kassenbeleg) oder die Verbindung abwarten.
    if (empfaenger !== undefined && Math.abs(betrag) > KLEINBETRAG_GRENZE_CENT) {
      setFehler(
        'Kasse nicht erreichbar – eine Rechnung über 400 € ist offline nicht möglich. Ohne Empfänger kassieren (Kassenbeleg) oder Verbindung abwarten.',
      );
      return;
    }
    // N1.9: unsicherer Zählerstand bzw. noch nicht registrierte Gerätekennung – offline keine
    // Nummer (Online gilt der Serverschutz). Der offene Beleg bleibt zur Klärung.
    if (!datensatz.offlineErlaubt) {
      setFehler(new ZaehlerstandUnsicher().message);
      return;
    }
    const beleg = offlineBelegAusVorgang(datensatz, anmeldung?.sitzung);
    if (beleg === null) {
      setFehler('Kasse nicht erreichbar – dieser Verkauf ist offline nicht möglich');
      return;
    }
    try {
      if (puffer.uebernimmVorgang === undefined) {
        await puffer.speichere(beleg);
      } else {
        await puffer.uebernimmVorgang(beleg);
      }
    } catch (e: unknown) {
      setFehler(
        `Kasse nicht erreichbar und Puffer gescheitert: ${e instanceof Error ? e.message : String(e)}`,
      );
      return;
    }
    versuchteNummer.current = null;
    setOffenerBeleg(null);
    setOfflineBeleg(beleg);
    if (sicherungRef.current.offenerBeleg?.uebernommen !== true) {
      teilungAbschliessen(beleg.positionen);
    }
    props.onOffline();
  }

  /** Karte gewählt: mit Modul Trinkgeld erst fragen, sonst gleich ans Terminal. */
  function karteWaehlen(): void {
    if (modul.trinkgeld) {
      setTrinkgeldFrage(true);
    } else {
      void karteStarten(0);
    }
  }

  /** Kartenzahlung starten; `trinkgeldCent` kommt in den Terminalbetrag, nicht in den Beleg. */
  async function karteStarten(trinkgeldCent: number): Promise<void> {
    // Synchron: ein zweiter Tipp vor dem nächsten Rendern startet keine zweite Kartenzahlung
    if (laeuftRef.current || barSperre.current || parkLaeuft.current || direktBelegSperre.current) {
      return;
    }
    // N1 (Codex N1-8): kein Zahlungsstart während eines Verkaufsstopps (sonst bezahlt ohne Beleg)
    if (props.verkaufsstopp != null) {
      setFehler(props.verkaufsstopp);
      return;
    }
    // Fehlerbericht 25.10.2026: kein Kartenstart, solange ein Verkauf ungeklärt ist (auch aus
    // einem anderen Fenster) – sonst entstünde ein zweiter Verkauf für denselben Einkauf
    laeuftRef.current = true;
    // Zeitpunkt vor dem Start (mit Spielraum für abweichende Uhren): ab hier wird gesucht,
    // falls die Antwort verloren geht.
    const seit = new Date(Date.now() - 2 * 60_000).toISOString();
    const zuZahlen = [...positionenZumZahlen];
    // P13: dauerhafte Vorgangs-ID je Start – vor dem Aufruf gemerkt; „Zahlung prüfen“ findet
    // darüber genau diese Zahlung, und eine Wiederholung startet nie eine zweite.
    const vorgangId = crypto.randomUUID();
    try {
      if (offenerBeleg !== null) {
        throw new Error(
          `Verkauf ${offenerBeleg.belegnummer} noch ungeklärt. Bitte diesen Einkauf nicht erneut kassieren. Zuerst Belegstatus prüfen.`,
        );
      }
      // Codex-Prüfung 4: trägt der Bon eine Reservierung, zuerst ihren Ausgang prüfen
      if (!(await pruefeEigeneReservierung())) {
        throw new Error(
          'Dieser Einkauf ist noch ungeklärt bzw. bereits verkauft – nicht erneut kassieren',
        );
      }
      // Codex-Runde 1 (K3/K5): Prüfung und dauerhafte Kartenreservierung (über alle Fenster)
      // unter der Kassiersperre; ohne gespeicherte Reservierung (und Besitz) keine Zahlung
      await mitKassiersperre(kassenId, async () => {
        await pruefeKeinOffenerVorgang(geraeteSpeicher, kassenId);
        if (!(await besitzErwerben(vorgangId))) {
          throw new Error('Reservierung nicht möglich (Fenstersperre) – keine Kartenzahlung');
        }
        // Codex-Prüfung 12: Bon-Merker vor der Reservierung
        const momentaufnahme = bonMomentaufnahme(zuZahlen);
        setzeMarker({ art: 'karte', id: vorgangId });
        try {
          await reserviereKassieren(geraeteSpeicher, {
            kassenId,
            art: 'karte',
            id: vorgangId,
            seit,
            positionen: zuZahlen,
            bon: momentaufnahme,
          });
        } catch (e: unknown) {
          setzeMarker(null);
          throw e;
        }
      });
      eigeneReservierung.current = { art: 'karte', id: vorgangId };
      kartenAusgang.current = null;
    } catch (e: unknown) {
      laeuftRef.current = false;
      if (e instanceof VerkaufUngeklaert) {
        uebernimmUngeklaert(e);
      }
      setTrinkgeldFrage(false);
      setFehler(e instanceof Error ? e.message : String(e));
      return;
    }
    setTrinkgeldFrage(false);
    setLaeuft(true);
    setFehler(null);
    setZahlungPositionen(zuZahlen);
    setStartVorgang(vorgangId);
    // Vor dem Aufruf merken: Neuladen während des Starts → „Zahlung prüfen“ statt Doppelstart
    merkeKartenstart(kartenstartSpeicher(), kassenId, seit, vorgangId);
    laeuftRef.current = true;
    try {
      // Sicherheitspaket S3: Der Server prüft Rabatte und Preise vor dem Terminal und bindet
      // die Freigabe an diese Zahlung – sonst stünde nach einer Ablehnung eine bezahlte
      // Kartenzahlung ohne Beleg da.
      const z = await api.starteZahlung(
        kassenId,
        rest + trinkgeldCent,
        `${String(positionen.length)} Positionen`,
        trinkgeldCent,
        {
          positionen: zuZahlen,
          ...(kundeId === null ? {} : { kundeId }),
          ...(gutscheinStand.einloesungen.length === 0
            ? {}
            : {
                gutscheine: gutscheinStand.einloesungen.map((e) => ({
                  code: e.code,
                  betragCent: e.betragCent,
                })),
              }),
          ...(gutscheinStand.ausgaben.length === 0
            ? {}
            : { gutscheinAusgaben: gutscheinStand.ausgaben }),
        },
        vorgangId,
      );
      // Sofort sichern: Neuladen oder Reiterwechsel während der Terminalzahlung verliert
      // sonst die Referenz, und derselbe Bon ließe sich ein zweites Mal bezahlen.
      setOffeneZahlung(z);
      sichereSofort({ offeneZahlung: z, zahlungPositionen: zuZahlen });
      merkeKartenstart(kartenstartSpeicher(), kassenId, null);
      setZahlung(z);
    } catch (e: unknown) {
      merkeKartenstart(kartenstartSpeicher(), kassenId, null);
      // Netz- und Serverfehler (5xx) sowie „unklar“ (409): der Start kann am Terminal trotzdem
      // laufen – unklar, erst „Zahlung prüfen“
      if (
        istNetzfehler(e) ||
        (e instanceof ApiFehler &&
          (e.status >= 500 || (e.status === 409 && e.message.includes('unklar'))))
      ) {
        setStartUnklarSeit(seit);
        sichereSofort({
          startUnklarSeit: seit,
          startVorgang: vorgangId,
          zahlungPositionen: zuZahlen,
        });
        setFehler(
          'Antwort zum Kartenzahlungsstart fehlt – erst „Zahlung prüfen“, bevor erneut kassiert wird',
        );
      } else {
        // Fachliche Ablehnung (z. B. S3: Rabattrecht fehlt) – keine Zahlung gestartet
        setZahlungPositionen(null);
        await gibReservierungFrei(
          geraeteSpeicher,
          kassenId,
          'karte',
          vorgangId,
          'nicht_verkauft',
        ).catch(() => undefined);
        eigeneReservierung.current = null;
        setzeMarker(null);
        if (e instanceof ApiFehler && e.message.includes('Artikelstamm')) {
          stammNeuLaden();
        }
        setFehler(`Kartenzahlung nicht gestartet: ${e instanceof Error ? e.message : String(e)}`);
      }
    } finally {
      laeuftRef.current = false;
      setLaeuft(false);
    }
  }

  /**
   * Kartenzahlung mit unbekanntem Ergebnis (Dialog nach Störung geschlossen): Sie kann am
   * Terminal noch gelingen. Bis zur Klärung keine neue Kartenzahlung, sonst droht eine
   * Doppelbelastung; „Zahlung prüfen“ holt den Stand und erstellt dann ggf. den Beleg.
   */
  const erstattungLaeuft = useRef(false);
  /** Laufende Rückerstattung einer bezahlten Zahlung ohne Beleg (Terminal führt sie aus). */
  const [erstattung, setErstattung] = useState<{ ursprung: string; zahlung: Zahlung } | null>(null);
  const [offeneZahlung, setOffeneZahlung] = useState<Zahlung | null>(
    () => (gesichert?.offeneZahlung ?? null) as Zahlung | null,
  );
  /** Positionen der offenen Kartenzahlung (Teilzahlung beim Teilen – auch nach Neuladen). */
  const [zahlungPositionen, setZahlungPositionen] = useState<Position[] | null>(
    () => (gesichert?.zahlungPositionen ?? null) as Position[] | null,
  );
  /** Kartenstart dieses Tabs gerade unterwegs (der Merker gehört dann noch nicht zum Neuladen). */
  const laeuftRef = useRef(false);
  /** Zahlungsstart ohne Antwort: ab diesem Zeitpunkt (ISO) nach der Zahlung suchen. */
  // Auch ein Start, der beim Neuladen noch unterwegs war, gilt als unklar (Sicherheitspaket S2).
  const [startUnklarSeit, setStartUnklarSeit] = useState<string | null>(
    () => gesichert?.startUnklarSeit ?? ladeKartenstart(kartenstartSpeicher(), kassenId),
  );
  /** P13: Vorgangs-ID des (unklaren) Kartenzahlungsstarts. */
  const [startVorgang, setStartVorgang] = useState<string | null>(
    () =>
      gesichert?.startVorgang ??
      (gesichert?.startUnklarSeit == null
        ? ladeKartenstartVorgang(kartenstartSpeicher(), kassenId)
        : null),
  );
  // Der Merker ist in den Zustand übernommen – die Bonsicherung hält ihn ab jetzt.
  useEffect(() => {
    if (ladeKartenstart(kartenstartSpeicher(), kassenId) !== null && !laeuftRef.current) {
      merkeKartenstart(kartenstartSpeicher(), kassenId, null);
    }
  }, [kassenId]);
  /** Gesendeter Beleg ohne Antwort – erst klären, dann wieder kassieren. */
  const [offenerBeleg, setOffenerBeleg] = useState<{
    belegnummer: string;
    positionen: Position[];
    vorgangId?: string;
    /** aus einem anderen Fenster übernommen: nach der Klärung bleibt der Bon hier unberührt */
    uebernommen?: true;
  } | null>(
    () =>
      (gesichert?.offenerBeleg ?? null) as { belegnummer: string; positionen: Position[] } | null,
  );
  const sicherung: GesicherterBon = {
    positionen,
    aktiverTisch,
    notiz,
    boniert,
    empfaenger: empfaengerEingabe,
    kundeEmail,
    gutscheinAusgaben,
    einloesungen,
    offeneZahlung,
    zahlungPositionen,
    startUnklarSeit,
    startVorgang: startUnklarSeit === null ? null : startVorgang,
    offenerBeleg,
    kunde: kundeId === null ? null : { id: kundeId, rabattProzent: kundenRabatt },
    reservierung: reservierungMarker,
    uebernommen: fremdeKlaerung,
  };
  const sicherungRef = useRef(sicherung);
  sicherungRef.current = sicherung;
  /**
   * Ungeklärte Zahlung oder ungeklärter Beleg: kein weiteres Kassieren (auch nicht bar),
   * bis „Zahlung prüfen“ bzw. „Beleg prüfen“ den Stand geklärt hat – sonst doppelter Umsatz.
   */
  const vorgangOffen =
    offeneZahlung !== null ||
    startUnklarSeit !== null ||
    offenerBeleg !== null ||
    offenePlattform !== null;

  // Codex-Runde 1 (K3): die eigene Reservierung (Karte, Altbestands-Bon) erst freigeben, wenn in
  // diesem Fenster nichts mehr offen ist – weder Zahlung noch Start noch Beleg. Codex-Prüfung 4:
  // mit dem Ausgang der Kartenzahlung (verkauft / nicht verkauft), dauerhaft je Reservierung
  useEffect(() => {
    const r = eigeneReservierung.current;
    if (
      r === null ||
      r.art === 'plattform' ||
      laeuft ||
      laeuftRef.current ||
      zahlung !== null ||
      offeneZahlung !== null ||
      startUnklarSeit !== null ||
      offenerBeleg !== null
    ) {
      return;
    }
    eigeneReservierung.current = null;
    const ergebnis = r.art === 'karte' ? (kartenAusgang.current ?? undefined) : undefined;
    gibReservierungFrei(geraeteSpeicher, kassenId, r.art, r.id, ergebnis)
      .then(() => {
        kartenAusgang.current = null;
        const m = sicherungRef.current.reservierung;
        if (m?.art === r.art && m.id === r.id) {
          setzeMarker(null);
        }
        if (sicherungRef.current.uebernommen?.id === r.id) {
          fremdKontext.current = null;
          setFremdeKlaerung(null);
          sichereSofort({ uebernommen: null });
        }
      })
      .catch(() => {
        // bleibt bestehen: beim nächsten Wechsel erneut versuchen (im Zweifel gesperrt)
        eigeneReservierung.current = r;
      });
  }, [geraeteSpeicher, kassenId, laeuft, zahlung, offeneZahlung, startUnklarSeit, offenerBeleg]);

  /**
   * Codex-Prüfung 4: Trägt dieser Bon eine Reservierung (Merker), entscheidet ihr Ausgang, ob er
   * noch kassiert werden darf: offen → klären; „verkauft“ (z. B. in einem anderen Fenster) → Bon
   * abschließen, nie erneut kassieren; „nicht verkauft“ → frei; unbekannt → gesperrt (im
   * Backoffice prüfen, danach den Bon leeren). true = frei zum Kassieren.
   */
  async function pruefeEigeneReservierung(): Promise<boolean> {
    const m = sicherungRef.current.reservierung;
    if (m == null) {
      return true;
    }
    let aktiv: Kassierreservierung | null;
    let ausgang: ReservierungsAusgang | null;
    try {
      aktiv = await ladeReservierung(geraeteSpeicher, kassenId);
      ausgang =
        aktiv?.art === m.art && aktiv.id === m.id
          ? null
          : await ladeReservierungsAusgang(geraeteSpeicher, kassenId, m.art, m.id);
    } catch (e: unknown) {
      setFehler(
        `Gerätespeicher nicht lesbar (${e instanceof Error ? e.message : String(e)}) – nicht kassiert`,
      );
      return false;
    }
    if (aktiv?.art === m.art && aktiv.id === m.id) {
      const e = new VerkaufUngeklaert(null, aktiv);
      uebernimmUngeklaert(e, true);
      setFehler(e.message);
      return false;
    }
    const was = m.art === 'karte' ? 'der Kartenzahlung' : 'der Lieferdienst-Erfassung';
    if (ausgang === null) {
      setFehler(
        `Ausgang ${was} zu diesem Bon unbekannt. Bitte diesen Einkauf nicht erneut kassieren – im Backoffice prüfen, erst danach den Bon leeren.`,
      );
      return false;
    }
    if (ausgang.ergebnis === 'verkauft') {
      verkauftAbschliessenOhneMarker(ausgang.positionen as Position[]);
      setFehler(
        `Dieser Einkauf ist bereits verkauft (${was === 'der Kartenzahlung' ? 'Kartenzahlung' : 'Lieferdienst'}, anderes Fenster) – Bon abgeschlossen. Nicht erneut kassieren.`,
      );
      return false;
    }
    setzeMarker(null);
    return true;
  }

  /**
   * Codex-Prüfung 4: Bon dieses Fensters ist nur eine Kopie eines Einkaufs, den ein anderes, noch
   * offenes Fenster besitzt (dupliziertes Fenster; dauerhafter Merker eines offenen Fensters) –
   * hier leeren, nie hier kassieren. Der dauerhafte Merker des Besitzers bleibt unberührt.
   */
  function kopieVerwerfen(): void {
    // Codex-Prüfung 5: sofort (vor dem nächsten Rendern) als leerer Bon ohne eigenen Teil führen –
    // so schreibt bzw. leert dieses Fenster den dauerhaften Merker des Besitzers nie
    dauerhaftGeschrieben.current = false;
    sicherungRef.current = {
      ...sicherungRef.current,
      positionen: [],
      aktiverTisch: null,
      notiz: '',
      boniert: [],
      empfaenger: KEIN_EMPFAENGER,
      kundeEmail: null,
      gutscheinAusgaben: [],
      einloesungen: [],
      offeneZahlung: null,
      zahlungPositionen: null,
      startUnklarSeit: null,
      startVorgang: null,
      offenerBeleg: null,
      kunde: null,
      reservierung: null,
      uebernommen: null,
    };
    sichern(sicherungRef.current);
    bonZuruecksetzen();
    setOffeneZahlung(null);
    setStartUnklarSeit(null);
    setStartVorgang(null);
    setZahlungPositionen(null);
    setOffenerBeleg(null);
    setReservierungMarker(null);
    setFehler(
      'Dieser Einkauf wird in einem anderen Fenster bearbeitet (Kartenzahlung bzw. Lieferdienst offen) – hier nicht kassieren, dort klären.',
    );
  }

  // Codex-Runde 1 (K3/K4), Codex-Prüfung 4: Beim Öffnen den Besitz prüfen und Reservierungen
  // übernehmen – eigene (Neuladen, Neustart) als eigenen Bon, fremde nur zum Klären – bzw. einen
  // offenen Bon / eine offene Kartenzahlung einer früheren Kassenversion nachträglich reservieren.
  const mountLauf = useRef(0);
  useEffect(() => {
    // Codex-Prüfung 5: ein abgebauter bzw. abgelöster Lauf (StrictMode, schneller Wechsel) handelt
    // nach seinen Wartepunkten nicht mehr
    const lauf = ++mountLauf.current;
    const abgeloest = () => lauf !== mountLauf.current || !montiert.current;
    void (async () => {
      const s = sicherungRef.current;
      let m = s.reservierung ?? null;
      let besitz = m === null || (await besitzErwerben(m.id));
      if (!besitz && m !== null && !abgeloest()) {
        // Die Lock einer eben geschlossenen Seite bzw. eines abgelösten Laufs kann noch kurz
        // gehalten sein – einmal kurz warten, erst dann gilt dieser Bon als Kopie
        await new Promise((r) => setTimeout(r, 150));
        if (abgeloest()) {
          return;
        }
        besitz = await besitzErwerben(m.id);
      }
      if (abgeloest()) {
        return;
      }
      if (!besitz) {
        kopieVerwerfen();
        besitzGeklaert.current = true;
        // Nur klären, nie als eigenen Bon: die Reservierung gehört dem anderen Fenster
        const fremd = await ladeReservierung(geraeteSpeicher, kassenId).catch(() => null);
        if (fremd !== null && !abgeloest()) {
          uebernimmUngeklaert(new VerkaufUngeklaert(null, fremd), false);
        }
        return;
      }
      // Besitzer (bzw. kein Merker): ab jetzt darf dieses Fenster den dauerhaften Merker schreiben
      besitzGeklaert.current = true;
      sichern(sicherungRef.current);
      const r = await ladeReservierung(geraeteSpeicher, kassenId).catch(() => null);
      const karteOffen = s.offeneZahlung !== null || s.startUnklarSeit !== null;
      // Ungeklärter Verkauf eines anderen Fensters (oder vor dem Neustart): gleich zum Klären
      // anbieten („Beleg prüfen“) statt erst beim nächsten Kassierversuch
      const offenerVorgang = (await offeneVorgaenge(geraeteSpeicher, kassenId).catch(() => []))[0];
      if (abgeloest()) {
        return;
      }
      if (offenerVorgang !== undefined && !karteOffen && s.offenerBeleg === null) {
        uebernimmOffenenVorgang(offenerVorgang);
        return;
      }
      if (r !== null) {
        const eigen = m?.art === r.art && m.id === r.id;
        const fremd = s.uebernommen?.art === r.art && s.uebernommen.id === r.id;
        if (r.art === 'altbon' && s.offenerBeleg?.belegnummer === r.id) {
          eigeneReservierung.current = { art: r.art, id: r.id };
        } else if (eigen) {
          uebernimmUngeklaert(new VerkaufUngeklaert(null, r), true);
        } else if (fremd || (!karteOffen && s.offenerBeleg === null && m === null)) {
          uebernimmUngeklaert(new VerkaufUngeklaert(null, r), false);
        } else if (r.art === 'karte' && karteOffen && m === null && s.uebernommen == null) {
          // offene Kartenzahlung einer Kassenversion vor dem Merker: als eigene führen
          if ((await besitzErwerben(r.id)) && !abgeloest()) {
            setzeMarker({ art: r.art, id: r.id });
            uebernimmUngeklaert(new VerkaufUngeklaert(null, r), true);
          }
        }
        return;
      }
      if (m !== null) {
        // Reservierung schon abgeschlossen (auch in einem anderen Fenster): Ausgang übernehmen
        await pruefeEigeneReservierung();
        return;
      }
      if (s.uebernommen != null) {
        // Nur geklärt für ein anderes Fenster, dort inzwischen abgeschlossen: Klärung beenden
        setFremdeKlaerung(null);
        if (s.uebernommen.art === 'karte') {
          setOffeneZahlung(null);
          setStartUnklarSeit(null);
          setStartVorgang(null);
          setZahlungPositionen(null);
        }
        return;
      }
      const z = s.offeneZahlung as Zahlung | null;
      const nachtrag: Kassierreservierung | null = karteOffen
        ? {
            kassenId,
            art: 'karte',
            id: s.startVorgang ?? z?.zahlungsreferenz ?? `seit:${s.startUnklarSeit ?? ''}`,
            seit: s.startUnklarSeit ?? new Date().toISOString(),
            positionen: s.zahlungPositionen ?? [],
            ...(z === null ? {} : { zahlungsreferenz: z.zahlungsreferenz }),
            ...(s.startVorgang == null && z === null ? { ohneStartId: true as const } : {}),
            bon: bonMomentaufnahme((s.zahlungPositionen ?? s.positionen) as Position[]),
          }
        : s.offenerBeleg !== null && s.offenerBeleg.vorgangId === undefined
          ? {
              kassenId,
              art: 'altbon',
              id: s.offenerBeleg.belegnummer,
              seit: new Date().toISOString(),
              positionen: s.offenerBeleg.positionen,
            }
          : null;
      if (nachtrag === null) {
        return;
      }
      m = { art: nachtrag.art, id: nachtrag.id };
      if (nachtrag.art === 'karte' && !(await besitzErwerben(nachtrag.id))) {
        return;
      }
      if (abgeloest()) {
        return;
      }
      // Codex-Prüfung 7: auch innerhalb der (ggf. wartenden) Kassiersperre prüfen
      await mitKassiersperre(kassenId, async () => {
        if (abgeloest()) {
          return false;
        }
        await reserviereKassieren(geraeteSpeicher, nachtrag);
        return true;
      })
        .then((reserviert) => {
          if (!reserviert || abgeloest()) {
            return;
          }
          eigeneReservierung.current = { art: nachtrag.art, id: nachtrag.id };
          if (nachtrag.art === 'karte') {
            setzeMarker(m);
          }
        })
        .catch(() => undefined);
    })();
    return () => {
      mountLauf.current++;
    };
    // Nur beim Öffnen (der Stand stammt aus der Sitzung)
  }, [geraeteSpeicher, kassenId]);
  /**
   * Codex-Prüfung 4: Der dauerhafte Merker (P1-13, für den Browserneustart) wird nur mit dem
   * eigenen offenen Teil dieses Fensters geschrieben – und nur von dem Fenster wieder geleert,
   * das ihn geschrieben hat. Ein Fenster ohne eigenen offenen Teil lässt den Merker eines anderen
   * Fensters unberührt.
   */
  const dauerhaftGeschrieben = useRef(false);
  function sichern(bon: GesicherterBon): void {
    const eigen = eigenerVorgangsteil(bon) !== null;
    const mitDauerhaft = besitzGeklaert.current && (eigen || dauerhaftGeschrieben.current);
    meldeSpeicherfehler(
      'vorgang',
      !speichereBon(sessionStorage, kassenId, bon, mitDauerhaft ? localStorage : undefined),
    );
    dauerhaftGeschrieben.current = eigen;
  }
  /** Sofort sichern (vor einem Netzaufruf) – der Effekt unten liefe erst nach dem nächsten Rendern. */
  function sichereSofort(teil: Partial<GesicherterBon>): void {
    // Codex-Prüfung 5: Folgeaufrufe vor dem nächsten Rendern bauen auf dieser Sicherung auf
    // (sonst ginge z. B. „uebernommen“ verloren und eine fremde Zahlung würde als eigene gesichert)
    const neu = { ...sicherungRef.current, ...teil };
    sicherungRef.current = neu;
    sichern(neu);
  }
  // Bon bei jeder Änderung in der Sitzung sichern.
  useEffect(() => {
    sichern(sicherungRef.current);
  }, [
    kassenId,
    positionen,
    aktiverTisch,
    notiz,
    boniert,
    empfaengerEingabe,
    kundeEmail,
    gutscheinAusgaben,
    einloesungen,
    offeneZahlung,
    zahlungPositionen,
    startUnklarSeit,
    startVorgang,
    offenerBeleg,
    kundeId,
    kundenRabatt,
    reservierungMarker,
    fremdeKlaerung,
  ]);

  /**
   * Fehlerbericht 25.10.2026: Bezahlentscheidung für einen Verkauf, den der Server nicht kennt
   * (Zustand B/C) – bis sie fällt, bleibt das Kassieren gesperrt (Zustand D).
   */
  const [bezahlFrage, setBezahlFrage] = useState<{
    belegnummer: string;
    summeCent: number;
    ohneVerbindung: boolean;
    /** Codex-Runde 1 (K1): Offline-Puffer bereit – „bezahlt“ puffert (vorläufiger Beleg). */
    offline?: boolean;
    /** Codex-Prüfung 15: Bezahlung schon bestätigt, Buchung abgelehnt („Nein“ = zurückgegeben) */
    abgelehnt?: boolean;
  } | null>(null);

  // A5-3 (Testserver 01.11.): Die Meldung „Verkauf … noch ungeklärt“ blieb nach erfolgreicher
  // Nachsignierung stehen, bis die Seite neu geladen wurde. Nach jedem solchen Abgleich wird sie
  // entfernt, wenn dieses Fenster nichts mehr offen hat und der Gerätespeicher keinen offenen
  // Vorgang und keine Reservierung mehr kennt (sonst bleibt sie – im Zweifel gesperrt).
  const ungeklaertStand = useRef({ fehler, offenerBeleg, bezahlFrage });
  ungeklaertStand.current = { fehler, offenerBeleg, bezahlFrage };
  useEffect(() => {
    const stand = ungeklaertStand.current;
    if (
      props.abgleichNr === undefined ||
      !istUngeklaertMeldung(stand.fehler) ||
      stand.offenerBeleg !== null ||
      stand.bezahlFrage !== null
    ) {
      return;
    }
    const gezeigt = stand.fehler;
    void ungeklaertErledigt(geraeteSpeicher, kassenId).then((erledigt) => {
      const jetzt = ungeklaertStand.current;
      if (erledigt && jetzt.offenerBeleg === null && jetzt.bezahlFrage === null) {
        setFehler((f) => (f === gezeigt ? null : f));
      }
    });
  }, [props.abgleichNr, geraeteSpeicher, kassenId]);

  /** Nach Neuladen: gesendeter Beleg ohne Antwort – entstanden, gepuffert oder nicht? */
  async function offenenBelegKlaeren(entscheidung?: Bezahlentscheidung): Promise<void> {
    let m = offenerBeleg;
    if (m === null) {
      // Fehlerbericht 25.10.2026: ungeklärter Vorgang ohne Merker in diesem Fenster (anderes
      // Fenster, Neustart) – den ältesten offenen Vorgang der Kasse übernehmen und klären
      const d = (await offeneVorgaenge(geraeteSpeicher, kassenId).catch(() => []))[0];
      if (d === undefined) {
        // Codex-Runde 1 (K3/K4): sonst eine Reservierung (Karte, Lieferdienst) übernehmen
        const r = await ladeReservierung(geraeteSpeicher, kassenId).catch(() => null);
        if (r !== null) {
          uebernimmUngeklaert(new VerkaufUngeklaert(null, r));
          setFehler(new VerkaufUngeklaert(null, r).message);
        }
        return;
      }
      m = {
        belegnummer: d.belegnummer,
        positionen: [...(d.auftrag.positionen ?? [])],
        vorgangId: d.vorgang.id,
        ...(d.sitzung === sitzungsId ? {} : { uebernommen: true as const }),
      };
      uebernimmOffenenVorgang(d);
    }
    setFehler(null);
    setBezahlFrage(null);
    // N1 (Codex N1-2): genau diesen Vorgang (gleiche Vorgangs-ID) wiederholen – nie nur nach der
    // Nummer suchen; ein momentanes 404 beweist keinen Fehlschlag.
    // Codex-Runde 6: Merker ohne Vorgangs-ID (Kassenversion vor N1) nutzt nie lokale Datensätze
    // gleicher Nummer – direkt der Altbestandsweg; mit ID zählt nur genau dieser Vorgang
    // Codex-Runde 1 (M8): Klärung unter der Kassiersperre – nie zwei Fenster zugleich am selben
    // Vorgang; ohne Sperre (kein Web Locks) bleibt er unklar
    const vorgangId = m.vorgangId;
    const belegnummer = m.belegnummer;
    let k: VorgangsKlaerung =
      vorgangId === undefined
        ? { art: 'ohne_datensatz' }
        : await mitKassiersperre(kassenId, () =>
            klaereOffenenVorgang(api, geraeteSpeicher, kassenId, belegnummer, {
              verwerfen: true,
              vorgangId,
              ...(entscheidung === undefined ? {} : { entscheidung }),
              // Codex-Runde 1 (K6): bezahlter Verkauf mit vergebener Nummer → Ersatzbuchung
              neueNummer: () => vergebeBelegnummer(geraeteSpeicher, localStorage, kassenId),
              // Codex-Prüfung 3 (Fund 4): Ausweg über eine neue Gerätekennung
              nummernkreis: {
                kennung: () => aktuelleKennung(geraeteSpeicher, localStorage),
                stoerung: () => markiereZaehlerUnsicher(geraeteSpeicher, kassenId),
              },
            }),
          ).catch((e: unknown): VorgangsKlaerung => ({
            art: 'unklar',
            fehler: e instanceof Error ? e.message : String(e),
          }));
    if (k.art === 'entscheidung') {
      // Der Server kennt den Verkauf nicht: nie automatisch buchen – das Personal entscheidet
      setBezahlFrage({
        belegnummer: k.belegnummer,
        summeCent: k.summeCent,
        ohneVerbindung: k.ohneVerbindung,
        ...(k.fehler === undefined ? {} : { abgelehnt: true }),
      });
      setFehler(
        k.fehler === undefined
          ? `Verkauf ${m.belegnummer} noch ungeklärt. Bitte diesen Einkauf nicht erneut kassieren. Zuerst Belegstatus prüfen.`
          : `Verkauf ${m.belegnummer}: Bezahlung bestätigt, Buchung abgelehnt (${k.fehler}). Nicht erneut kassieren – Ursache beheben und „Ja“ erneut wählen, oder „Nein“, wenn der Betrag zurückgegeben wurde.`,
      );
      return;
    }
    if (k.art === 'ohne_datensatz' && m.vorgangId !== undefined) {
      // N1-Vorgang ohne Datensatz: offline gepuffert (Verkauf gebucht) oder schon anderswo
      // geklärt – Stand der Vorgangs-ID abfragen, nie über die Belegnummer (Codex R3-2)
      // Codex-Prüfung 12: ein Lesefehler des Puffers beweist keine Abwesenheit – unklar
      const pufferStand = await puffer.alle(kassenId).catch(() => null);
      if (pufferStand === null) {
        setFehler(
          `Verkauf ${m.belegnummer}: Offline-Puffer nicht lesbar – Ergebnis unklar, erst „Beleg prüfen“, dann weiter kassieren`,
        );
        return;
      }
      const gepuffert = pufferStand.some((b) => b.vorgang?.id === m.vorgangId);
      // Offene Klärung am Server hat Vorrang (Stop-Prüfung); ohne Verbindung gilt der Puffer
      const inKlaerung = gepuffert
        ? await api
            .vorgangStand(kassenId, m.vorgangId)
            .then((st) => st.stand === 'klaerung')
            .catch(() => false)
        : false;
      if (inKlaerung) {
        setFehler(
          `Verkauf ${m.belegnummer} ist in Klärung im Backoffice – erst nach der Entscheidung weiter`,
        );
        return;
      }
      if (gepuffert) {
        setOffenerBeleg(null);
        if (m.uebernommen !== true) {
          teilungAbschliessen(m.positionen);
        }
        return;
      }
      k = await klaereVorgangOhneDatensatz(
        api,
        geraeteSpeicher,
        kassenId,
        m.belegnummer,
        m.vorgangId,
      );
      // Codex-Prüfung 11: eigener Merker ohne Datensatz und ohne Pufferung, Server kennt den
      // Vorgang nicht – der Datensatz entsteht vor jedem Senden, also wurde nie gesendet
      // (Abbruch zwischen Merker und Datensatz): kein Umsatz, der Bon ist frei
      if (k.art === 'unklar' && m.uebernommen !== true) {
        const st = await api.vorgangStand(kassenId, m.vorgangId).catch(() => null);
        if (st?.stand === 'unbekannt') {
          k = {
            art: 'nicht_gebucht',
            fehler: 'Verkauf wurde nie übertragen (Abbruch vor dem Senden)',
          };
        }
      }
    }
    if (k.art === 'gepuffert') {
      // offline gepuffert: der Verkauf ist gebucht, die Nachsignierung folgt
      versuchteNummer.current = null;
      setOffenerBeleg(null);
      if (m.uebernommen !== true) {
        teilungAbschliessen(m.positionen);
      }
      return;
    }
    if (k.art === 'gebucht') {
      versuchteNummer.current = null;
      setOffenerBeleg(null);
      if (m.uebernommen !== true) {
        teilungAbschliessen(m.positionen);
      }
      if (k.ersatzFuer !== undefined) {
        // Codex-Runde 1 (K6): bezahlt bestätigt, unter neuer Nummer gebucht – nicht neu kassieren
        setFehler(
          `Verkauf ${k.ersatzFuer}: Nummer war bereits vergeben – als Beleg ${k.beleg.belegnummer} gebucht (Bezahlung bestätigt). Nicht erneut kassieren.`,
        );
      }
      const text = await api.druck(kassenId, k.beleg.belegnummer).catch(() => null);
      setBeleg({ beleg: k.beleg, text });
      return;
    }
    if (k.art === 'unklar') {
      // Ohne Verbindung mit bereitem Offline-Puffer: die Bezahlentscheidung erneut anbieten
      // („bezahlt“ puffert, Codex-Runde 1 K1) – sonst bliebe der Verkauf bis zur Verbindung offen
      const d = navigator.onLine
        ? null
        : await ladeVorgang(geraeteSpeicher, kassenId, m.belegnummer).catch(() => null);
      if (
        d !== null &&
        d.abgeschlossen === undefined &&
        d.vorgang.id === m.vorgangId &&
        brauchtBezahlentscheidung(d) &&
        d.offlineErlaubt &&
        offlineBelegAusVorgang(d) !== null &&
        offlinePufferBereit(puffer, localStorage, kassenId, props.speicherDauerhaft === true)
      ) {
        setBezahlFrage({
          belegnummer: d.belegnummer,
          summeCent: summeBrutto(d.auftrag.positionen ?? []),
          ohneVerbindung: d.ohneVerbindung === true,
          offline: true,
        });
      }
      setFehler(
        `Verkauf ${m.belegnummer}: Ergebnis unklar (${k.fehler}) – erst „Beleg prüfen“, dann weiter kassieren`,
      );
      return;
    }
    if (k.art === 'nicht_gebucht') {
      // Sicher kein Beleg zu diesem Vorgang – der Bon bleibt offen und kann neu kassiert werden
      versuchteNummer.current = null;
      setOffenerBeleg(null);
      setFehler(
        entscheidung?.bezahlt === false
          ? `Verkauf ${m.belegnummer} als nicht bezahlt festgehalten – kein Umsatz gebucht. Der Bon kann neu kassiert werden.`
          : `Verkauf ${m.belegnummer} wurde nicht gebucht (${k.fehler}) – bitte erneut kassieren`,
      );
      return;
    }
    // Ohne Vorgangs-ID (Merker aus einer Kassenversion vor N1): nie automatisch über die Nummer
    // zuordnen – freie Nummer am Server sperren, sonst entscheidet ein Mensch (Codex-Runde 5)
    const alt = await klaereAltbestandsBon(
      api,
      async () => (await puffer.alle(kassenId)).some((b) => b.belegnummer === m.belegnummer),
      kassenId,
      m.belegnummer,
      (b) =>
        window.confirm(
          `Unter Nummer ${b.belegnummer} liegt ein Beleg vom ${new Date(b.zeitpunkt).toLocaleString('de-AT', { timeZone: 'Europe/Vienna' })}. Gehört er zu diesem Bon? Nur nach Prüfung im Belegjournal mit OK bestätigen.`,
        )
          ? 'gehoert_dazu'
          : window.confirm(
                'Dann wurde dieser Verkauf nicht gebucht. Bon zum erneuten Kassieren freigeben?',
              )
            ? 'nicht_gebucht'
            : 'offen',
    );
    if (alt.art === 'gepuffert') {
      setOffenerBeleg(null);
      if (m.uebernommen !== true) {
        teilungAbschliessen(m.positionen);
      }
      return;
    }
    if (alt.art === 'gebucht') {
      setOffenerBeleg(null);
      if (m.uebernommen !== true) {
        teilungAbschliessen(m.positionen);
      }
      const text = await api.druck(kassenId, alt.beleg.belegnummer).catch(() => null);
      setBeleg({ beleg: alt.beleg, text });
      return;
    }
    if (alt.art === 'nicht_gebucht') {
      setOffenerBeleg(null);
      setFehler(
        `Verkauf ${m.belegnummer} wurde nicht gebucht (${alt.fehler}) – bitte erneut kassieren`,
      );
      return;
    }
    setFehler(
      `Verkauf ${m.belegnummer}: Ergebnis unklar (${alt.art === 'unklar' ? alt.fehler : 'unbekannt'}) – erst „Beleg prüfen“, dann weiter kassieren`,
    );
  }
  // Beim Öffnen des Verkaufs einen offenen Vorgang aus der Sitzung sofort klären.
  const geklaert = useRef(false);
  useEffect(() => {
    if (geklaert.current) {
      return;
    }
    geklaert.current = true;
    if (offenerBeleg !== null) {
      void offenenBelegKlaeren();
    }
    // Nur beim ersten Rendern (der Merker stammt aus der Sitzung).
  }, []);

  /**
   * W-9 (Welle 2): „Zahlung prüfen“ läuft (synchron, unabhängig vom Rendern) – ein zweiter Klick
   * bzw. die Taste in der Anzeige K7 starten keine zweite, parallele Prüfung (keine doppelten
   * Rückfragen oder Leseaufrufe). Die Taste ist während der Prüfung deaktiviert.
   */
  const pruefSperre = useRef(false);
  const [pruefungLaeuft, setPruefungLaeuft] = useState(false);

  async function offeneZahlungPruefen(): Promise<void> {
    if (pruefSperre.current) {
      return;
    }
    pruefSperre.current = true;
    setPruefungLaeuft(true);
    try {
      await offeneZahlungPruefenEinmal();
    } finally {
      pruefSperre.current = false;
      setPruefungLaeuft(false);
    }
  }

  async function offeneZahlungPruefenEinmal(): Promise<void> {
    let offen = offeneZahlung;
    setFehler(null);
    try {
      if (offen === null && startUnklarSeit !== null && startVorgang !== null) {
        // P13: über die Vorgangs-ID exakt klären statt über Zeit und Betrag.
        let v = await api.zahlungVorgang(kassenId, startVorgang);
        if (v.stand === 'unbekannt') {
          // „unbekannt“ beweist nichts – die Startanfrage kann noch unterwegs sein. Erst das
          // Verwerfen am Server schließt den Vorgang so ab, dass er nie mehr startet.
          const w = await api.zahlungVorgangVerwerfen(kassenId, startVorgang);
          v =
            w.stand === 'verworfen'
              ? { stand: 'verworfen' }
              : await api.zahlungVorgang(kassenId, startVorgang);
        }
        if (v.stand === 'gestartet' && v.zahlung !== undefined) {
          if (v.belegnummer != null) {
            // Codex-Bestätigungsprüfung F2: Die Zahlung ist schon mit Beleg abgerechnet (z. B. in
            // einem anderen Fenster) – der Bon dieser Zahlung ist damit verkauft: abschließen und
            // den vorhandenen Beleg zeigen, nie zum erneuten Kassieren stehen lassen
            const nummer = v.belegnummer;
            const bezahlt = zahlungPositionen ?? [];
            setStartUnklarSeit(null);
            setStartVorgang(null);
            setZahlungPositionen(null);
            kartenAusgang.current = 'verkauft';
            // Nur der eigene Bon wird abgeschlossen; ein nur klärendes Fenster bleibt unberührt
            if (!karteFremd()) {
              teilungAbschliessen(bezahlt);
            }
            setFehler(
              `Kartenzahlung bereits mit Beleg ${nummer} abgerechnet – nicht erneut kassieren`,
            );
            const vorhanden = await api.beleg(kassenId, nummer).catch(() => null);
            if (vorhanden !== null) {
              const text = await api.druck(kassenId, nummer).catch(() => null);
              setBeleg({ beleg: vorhanden, text });
            }
            return;
          }
          setStartUnklarSeit(null);
          setStartVorgang(null);
          offen = v.zahlung;
          setOffeneZahlung(offen);
        } else if (
          v.stand === 'verworfen' ||
          v.stand === 'nicht_gestartet' ||
          v.stand === 'manuell_geklaert'
        ) {
          // Der Vorgang ist am Server abgeschlossen und startet nie mehr – erneut kassieren ist möglich.
          setStartUnklarSeit(null);
          setStartVorgang(null);
          setZahlungPositionen(null);
          kartenAusgang.current = 'nicht_verkauft';
          setFehler(
            v.stand === 'manuell_geklaert'
              ? 'Kartenzahlungsvorgang wurde manuell abgeschlossen (kein Nachweis „nicht gestartet“) – im Tagesabschluss mit der Anbieterabrechnung abgleichen; erneut kassieren ist möglich'
              : 'Kartenzahlung wurde nicht gestartet – erneut kassieren ist möglich',
          );
          return;
        }
        // „reserviert“: Der Start ist am Server angekommen, das Ergebnis aber offen. Mit
        // Vorgangs-ID gibt es keine Freigabe über Zeit und Betrag – nur der Server darf den
        // Vorgang abschließen (nach Wartezeit, ohne Journaleintrag).
        if (v.stand === 'reserviert') {
          if (
            !window.confirm(
              'Der Kartenzahlungsstart ist am Server angekommen, sein Ergebnis ist offen. Zeigt das Terminal sicher KEINE laufende oder abgeschlossene Zahlung? Nur dann mit OK abschließen.',
            )
          ) {
            setFehler('Kartenzahlung weiter ungeklärt – später erneut „Zahlung prüfen“');
            return;
          }
          // Wer bestätigt, muss benannt sein – ohne angemeldeten Bediener ausdrücklich abfragen.
          const bestaetigtVon = (
            bedienerName ??
            window.prompt('Wer hat am Terminal geprüft? (Name)') ??
            ''
          ).trim();
          if (bestaetigtVon === '') {
            setFehler('Ohne Namen der prüfenden Person kein Abschluss – Zahlung bleibt ungeklärt');
            return;
          }
          const a = await api.zahlungVorgangAbschliessen(kassenId, startVorgang, bestaetigtVon);
          if (
            a.stand === 'nicht_gestartet' ||
            a.stand === 'verworfen' ||
            a.stand === 'manuell_geklaert'
          ) {
            setStartUnklarSeit(null);
            setStartVorgang(null);
            setZahlungPositionen(null);
            kartenAusgang.current = 'nicht_verkauft';
            setFehler(
              a.stand === 'manuell_geklaert'
                ? 'Kartenzahlungsvorgang manuell abgeschlossen – im Tagesabschluss mit der Anbieterabrechnung abgleichen; erneut kassieren ist möglich'
                : 'Kartenzahlungsvorgang abgeschlossen – erneut kassieren ist möglich',
            );
            return;
          }
          // inzwischen gestartet (Journal) → beim nächsten „Zahlung prüfen“ übernehmen
          setFehler('Kartenzahlung inzwischen gefunden – bitte erneut „Zahlung prüfen“');
          return;
        }
        // Nur „gestartet“ geht weiter (offene Zahlung übernommen); nie zeitbasierter Rückfall.
        if (offen === null) {
          return;
        }
      }
      if (offen === null && startUnklarSeit !== null) {
        // Startantwort verloren: die unbelegte Zahlung dieser Kasse seit dem Start suchen.
        // Z-2: ohne Beleg vollständig rückerstattete Zahlungen sind kein Kandidat mehr
        const kandidaten = zahlungsKandidaten(await api.zahlungenSeit(kassenId, startUnklarSeit));
        // Ohne dauerhafte Vorgangs-ID beweisen Zeit und Betrag keine Zuordnung
        // (Sicherheitspaket S2): mehrere Kandidaten nie automatisch, einer nur bestätigt.
        if (kandidaten.length > 1) {
          setFehler(
            `Mehrere ungeklärte Kartenzahlungen (${kandidaten.map((k) => k.zahlung.zahlungsreferenz).join(', ')}) – am Terminal und im Backoffice klären, bevor kassiert wird`,
          );
          return;
        }
        const kandidat = kandidaten[0];
        if (
          kandidat !== undefined &&
          !window.confirm(
            `Kartenzahlung ${kandidat.zahlung.zahlungsreferenz} über ${formatiereEuro(kandidat.zahlung.betragCent)} € (${kandidat.zahlung.status}) gefunden. Gehört sie zu diesem Bon? Nur mit OK zuordnen.`,
          )
        ) {
          setFehler('Kartenzahlung nicht zugeordnet – Vorgang bleibt ungeklärt');
          return;
        }
        if (kandidat === undefined) {
          // Kein Journaleintrag beweist nicht, dass keine Zahlung lief (Start kann am Terminal
          // noch laufen oder die Anbieterantwort fehlt). Freigeben nur nach Bestätigung am
          // Terminal (Sicherheitspaket S2) – sonst bleibt der Vorgang klärungsbedürftig.
          const freigeben = window.confirm(
            'Im Journal ist keine Kartenzahlung zu diesem Bon. Bitte am Terminal prüfen: Nur wenn dort KEINE Zahlung gelaufen ist (oder sie abgebrochen wurde), mit OK freigeben.',
          );
          if (!freigeben) {
            setFehler(
              'Kartenzahlung weiter ungeklärt – am Terminal prüfen und später erneut „Zahlung prüfen“',
            );
            return;
          }
          setStartUnklarSeit(null);
          setZahlungPositionen(null);
          kartenAusgang.current = 'nicht_verkauft';
          setFehler('Am Terminal bestätigt: keine Kartenzahlung – erneut kassieren ist möglich');
          return;
        }
        setStartUnklarSeit(null);
        offen = kandidat.zahlung;
        setOffeneZahlung(offen);
      }
      if (offen === null) {
        return;
      }
      const z = await api.zahlung(kassenId, offen.zahlungsreferenz);
      if (z.status === 'ausstehend') {
        // Weiter am Terminal: Dialog wieder öffnen (fragt den Stand regelmäßig ab).
        setZahlung(z);
        return;
      }
      await zahlungAbgeschlossen(z);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  /**
   * Bezahlt, aber kein Beleg möglich (z. B. Artikel inzwischen gesperrt): die Zahlung direkt
   * vollständig rückerstatten. Es entsteht kein Beleg; der Bon bleibt zum Korrigieren stehen.
   */
  async function bezahlteZahlungErstatten(): Promise<void> {
    const offen = offeneZahlung;
    // Nur ohne offenen Belegvorgang (sonst kann der Beleg schon gespeichert sein) und nie doppelt.
    if (offen?.status !== 'bezahlt' || offenerBeleg !== null || erstattungLaeuft.current) {
      return;
    }
    if (
      !window.confirm(
        `Kartenzahlung ${offen.zahlungsreferenz} über ${formatiereEuro(offen.betragCent)} € hat keinen Beleg. Vollständig rückerstatten? Der Bon bleibt zum Korrigieren stehen.`,
      )
    ) {
      return;
    }
    setFehler(null);
    erstattungLaeuft.current = true;
    try {
      const r = await api.erstatteZahlung(
        kassenId,
        offen.zahlungsreferenz,
        undefined,
        anmeldung?.sitzung,
        true,
      );
      if (r.status === 'ausstehend') {
        setErstattung({ ursprung: offen.zahlungsreferenz, zahlung: r });
        return;
      }
      erstattungAbgeschlossen(offen.zahlungsreferenz, r);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      erstattungLaeuft.current = false;
    }
  }

  function erstattungAbgeschlossen(ursprung: string, r: Zahlung): void {
    setErstattung(null);
    if (r.status !== 'erstattet') {
      setFehler(`Rückerstattung ${r.status} – die Kartenzahlung ${ursprung} bleibt offen`);
      return;
    }
    verarbeitet.current.add(ursprung);
    setOffeneZahlung(null);
    setZahlungPositionen(null);
    setKartenStand(null);
    kartenAusgang.current = 'nicht_verkauft';
    setFehler(
      `Kartenzahlung ${ursprung} rückerstattet – kein Beleg. Bon korrigieren und neu kassieren.`,
    );
  }

  /**
   * ZD-V2-F1: zuletzt abgeschlossene Kartenzahlung – nur für die Anzeige (Betrag, Referenz) in
   * der V2-Hülle; gesetzt beim Abschluss, auch über „Zahlung prüfen“ ohne Kartendialog
   * (Codex-Prüfung A2, Runde 1).
   */
  const [letzteKarte, setLetzteKarte] = useState<Zahlung | null>(null);
  async function zahlungAbgeschlossen(z: Zahlung): Promise<void> {
    if (verarbeitet.current.has(z.zahlungsreferenz)) {
      return;
    }
    // Nur Anzeige (V2-Ergebnis); ändert keinen Ablauf
    setLetzteKarte(z);
    if (z.status === 'ausstehend') {
      setZahlung(null);
      setOffeneZahlung(z);
      setFehler(
        `Ergebnis der Kartenzahlung ${z.zahlungsreferenz} unbekannt – erst „Zahlung prüfen“, bevor erneut kassiert wird`,
      );
      return;
    }
    verarbeitet.current.add(z.zahlungsreferenz);
    setZahlung(null);
    if (z.status !== 'bezahlt') {
      setOffeneZahlung(null);
      setZahlungPositionen(null);
      setKartenStand(z.status === 'storniert' ? 'abgebrochen' : 'abgelehnt');
      kartenAusgang.current = 'nicht_verkauft';
      setFehler(
        z.status === 'storniert'
          ? 'Kartenzahlung abgebrochen – kein Beleg erstellt'
          : `Kartenzahlung ${z.status} – kein Beleg erstellt`,
      );
      return;
    }
    setLaeuft(true);
    setKartenStand('beleg');
    const zuZahlen = zahlungPositionen ?? positionenZumZahlen;
    try {
      await belegErstellen('karte', z.zahlungsreferenz, undefined, zuZahlen);
      setOffeneZahlung(null);
      setZahlungPositionen(null);
      const belegsumme = summeBrutto(zuZahlen);
      anzeige.current?.sende({
        typ: 'bezahlt',
        summeCent: belegsumme,
        // Mit Karte bezahlt wurde der Terminalbetrag ohne Trinkgeld; der Rest ist Gutschein.
        gutscheinCent: Math.max(0, belegsumme - (z.betragCent - (z.trinkgeldCent ?? 0))),
        zahlungsart: 'karte',
        gegebenCent: null,
        rueckgeldCent: null,
        trinkgeldCent: z.trinkgeldCent ?? 0,
      });
    } catch (e: unknown) {
      // Bezahlt, aber ohne Beleg: offen lassen – „Zahlung prüfen“ erstellt ihn erneut.
      verarbeitet.current.delete(z.zahlungsreferenz);
      setOffeneZahlung(z);
      // S3: fehlt das Rabattrecht (z. B. Preis inzwischen geändert), kann ein berechtigter
      // Bediener den Beleg freigeben – sonst Rückerstattung der Zahlung.
      const rechtFehlt =
        e instanceof ApiFehler && e.status === 403 && e.message.includes('Rabattrecht');
      // Zahlung rückerstattet: sie deckt keinen Beleg mehr. Nur bei VOLLSTÄNDIG erstatteter
      // Zahlung ist der Vorgang erledigt (Bon bleibt zum Korrigieren stehen); läuft die
      // Rückerstattung noch oder ist sie nur teilweise erfolgt, bleibt die Zahlung offen.
      if (e instanceof ApiFehler && e.status === 409 && e.message.includes('rückerstattet')) {
        if (e.message.includes('vollständig rückerstattet')) {
          kartenAusgang.current = 'nicht_verkauft';
          verarbeitet.current.add(z.zahlungsreferenz);
          setOffeneZahlung(null);
          setZahlungPositionen(null);
          setFehler(`${e.message}. Bon korrigieren und neu kassieren.`);
        } else {
          setFehler(
            `${e.message}. Ergebnis der Rückerstattung abwarten bzw. im Backoffice klären, dann „Zahlung prüfen“.`,
          );
        }
        return;
      }
      // Artikel nach dem Kartenstart gesperrt/deaktiviert: kein Beleg – Zahlung rückerstatten
      const nichtVerkaufbar =
        e instanceof ApiFehler && e.status === 422 && e.message.includes('nicht verkaufbar');
      setFehler(
        `Zahlung ${z.zahlungsreferenz} ist bezahlt, aber der Beleg fehlt noch: ${e instanceof Error ? e.message : String(e)} – ${nichtVerkaufbar ? 'Artikel ist nicht mehr verkaufbar – „Zahlung rückerstatten“ tippen' : rechtFehlt ? 'Bediener mit Rabattrecht anmelden und „Zahlung prüfen“ (oder Zahlung rückerstatten)' : '„Zahlung prüfen“ erstellt ihn erneut'}`,
      );
    } finally {
      setLaeuft(false);
      setKartenStand((k) => (k === 'beleg' ? null : k));
    }
  }

  /**
   * ZD-V2-F1 (nur mit Schalter `bilanx.zahlungsdialogV2`, beim Öffnen des Verkaufs gelesen):
   * Kartenzustände vor und nach dem Kartendialog in der V2-Hülle. Reine Anzeige, abgeleitet aus
   * dem bestehenden Zustand – `karteStarten`, `zahlungAbgeschlossen` und „Zahlung prüfen“
   * bleiben unverändert. Ergebnis- und Unklar-Anzeige lassen sich schließen; das ändert nichts
   * am Vorgang (Sperren, „Zahlung prüfen“ und Fehlerzeile bleiben).
   */
  const kartenStatusAn = useMemo(() => zahlungsdialogV2Aktiv(kassenId), [kassenId]);
  const [kartenErgebnisZu, setKartenErgebnisZu] = useState(false);
  const [startUnklarZu, setStartUnklarZu] = useState(false);
  // Jedes neue Kartenergebnis (neue abgeschlossene Zahlung) zeigt sich wieder, auch bei gleichem
  // `kartenStand` (z. B. zweimal abgelehnt über „Zahlung prüfen“; Codex-Prüfung A2, Runde 2)
  useEffect(() => {
    setKartenErgebnisZu(false);
  }, [kartenStand, letzteKarte]);
  useEffect(() => {
    setStartUnklarZu(false);
  }, [startUnklarSeit]);
  useEffect(() => {
    // Neuer Versuch über F3 (Trinkgeldfrage): das alte Ergebnis nicht wieder einblenden
    if (trinkgeldFrage) {
      setKartenErgebnisZu(true);
    }
  }, [trinkgeldFrage]);
  /** ZD-V2-F4: Fokusfalle der Trinkgeldfrage und der Bezahlentscheidung (Reiter dahinter). */
  const trinkgeldDialog = useRef<HTMLDivElement | null>(null);
  useFokusfalleAmFenster(trinkgeldDialog, trinkgeldFrage);
  const bezahlDialog = useRef<HTMLDivElement | null>(null);
  useFokusfalleAmFenster(bezahlDialog, bezahlFrage !== null);
  /** D6: Fokusfalle des Empfängerdialogs (Codex Sprint 01.11.). */
  const empfaengerDialog = useRef<HTMLDivElement | null>(null);
  useFokusfalleAmFenster(empfaengerDialog, empfaengerSichtbar);
  /** BT2-D6/BT3-D6: Tastatur des Empfängerdialogs; ohne gewähltes Feld „Kunde suchen“ (R1). */
  const btEmpf = useBildschirmtastatur({ standardZiel: modul.kunden ? 'kunde' : 'name' });
  const btEmpfSchliessen = btEmpf.schliessen;
  useEffect(() => {
    if (!empfaengerSichtbar) {
      btEmpfSchliessen('keiner'); // BT-E2: nächster Dialog startet geschlossen und ohne Ziel
    }
  }, [empfaengerSichtbar, btEmpfSchliessen]);
  // Beim Schließen Fokus zurück an den Auslöser (sonst landete er auf body). Der Auslöser wird
  // im ersten Rendern mit offenem Dialog gemerkt – vor dem Commit, also bevor autoFocus bzw.
  // die Fokusfalle den Fokus in den Dialog ziehen.
  const empfaengerAusloeser = useRef<{ offen: boolean; el: HTMLElement | null }>({
    offen: false,
    el: null,
  });
  if (empfaengerSichtbar && !empfaengerAusloeser.current.offen) {
    const a = document.activeElement;
    empfaengerAusloeser.current = {
      offen: true,
      el: a instanceof HTMLElement && a !== document.body ? a : null,
    };
  }
  useEffect(() => {
    if (empfaengerSichtbar) {
      return;
    }
    const { offen, el } = empfaengerAusloeser.current;
    empfaengerAusloeser.current = { offen: false, el: null };
    if (
      offen &&
      el?.isConnected === true &&
      (document.activeElement === null || document.activeElement === document.body)
    ) {
      el.focus();
    }
  }, [empfaengerSichtbar]);
  /** D3 v2: „Anderer Betrag …“ – Startfokus auf die freie Eingabe, sonst gingen Ziffern verloren. */
  const trinkgeldFreiFeld = useRef<HTMLDivElement | null>(null);
  const trinkgeldFreiOffen = trinkgeldFrei !== null;
  useEffect(() => {
    if (trinkgeldFreiOffen) {
      trinkgeldFreiFeld.current?.focus();
    }
  }, [trinkgeldFreiOffen]);
  /** Startaufruf der Kartenzahlung unterwegs (zwischen Reservierung und Antwort). */
  const kartenStartLaeuft =
    laeuft &&
    zahlung === null &&
    zahlungPositionen !== null &&
    offeneZahlung === null &&
    startUnklarSeit === null &&
    kartenStand !== 'beleg';
  const andererDialogOffen =
    beleg !== null ||
    offlineBeleg !== null ||
    barDialog ||
    trinkgeldFrage ||
    rueckfrage !== null ||
    gutscheinDialog !== null ||
    zahlung !== null ||
    erstattung !== null ||
    bezahlFrage !== null ||
    pfandDialog !== null ||
    plattformDialog ||
    tischplanOffen ||
    anmeldeDialog ||
    scanAuswahl !== null ||
    wiegeAnfrage !== null;
  const kartenStatus: KartenStatusArt | null =
    !kartenStatusAn || andererDialogOffen
      ? null
      : kartenStand === 'beleg'
        ? 'beleg'
        : kartenStartLaeuft
          ? 'start'
          : laeuft
            ? null
            : startUnklarSeit !== null && !startUnklarZu
              ? 'start_unklar'
              : (kartenStand === 'abgelehnt' || kartenStand === 'abgebrochen') && !kartenErgebnisZu
                ? kartenStand
                : null;

  // BT-E2/R1: Dialog, Zahlung, Sperre oder Bedienerwechsel – Tastatur der Verkaufsfläche zu,
  // keine alte Feldzuordnung übernehmen
  const btGesperrt =
    andererDialogOffen ||
    empfaengerSichtbar ||
    ziffernOffen ||
    kartenStatus !== null ||
    (bedienerPflicht && anmeldung === null);
  const btBediener = anmeldung?.name ?? bediener.aktiv ?? '';
  const btSchliessen = bt.schliessen;
  useEffect(() => {
    if (btGesperrt) {
      btSchliessen('keiner');
    }
  }, [btGesperrt, btSchliessen]);
  const btBedienerVorher = useRef(btBediener);
  useEffect(() => {
    if (btBedienerVorher.current !== btBediener) {
      btBedienerVorher.current = btBediener;
      btSchliessen('keiner');
    }
  }, [btBediener, btSchliessen]);

  // Getippte Ziffer außerhalb von Eingabefeldern: Ziffernblock einblenden und die Ziffer
  // übernehmen – so bleibt „Zahl tippen, dann Kachel = Stückzahl“ auch ohne sichtbaren
  // Ziffernblock erhalten. Der Scanner hält schnelle Ziffernfolgen vorher selbst zurück.
  const ziffernSperre = useRef(false);
  ziffernSperre.current =
    beleg !== null ||
    offlineBeleg !== null ||
    (bedienerPflicht && anmeldung === null) ||
    barDialog ||
    trinkgeldFrage ||
    rueckfrage !== null ||
    gutscheinDialog !== null ||
    zahlung !== null ||
    pfandDialog !== null ||
    plattformDialog ||
    tischplanOffen ||
    anmeldeDialog ||
    scanAuswahl !== null ||
    wiegeAnfrage !== null ||
    kartenStatus !== null ||
    // D6 (Codex Sprint 01.11., Runde 2): keine zweite modale Eingabe über dem Empfängerdialog
    empfaengerSichtbar;
  // Scan verarbeiten: erst Sperre (Zahlung läuft, Beleg offen …), dann Code-Suche. Die
  // Sperre wird nach der (künftig ggf. asynchronen) Suche erneut geprüft.
  const scanZustand = {
    laeuft,
    kartenzahlung: zahlung !== null,
    trinkgeldFrage,
    barDialog,
    vorgangOffen,
    positionenFest: zahlungPositionen !== null,
    belegOffen: beleg !== null || offlineBeleg !== null,
  };
  const scanZustandRef = useRef(scanZustand);
  scanZustandRef.current = scanZustand;
  const scanAuswahlRef = useRef(scanAuswahl);
  scanAuswahlRef.current = scanAuswahl;
  const scanSperreJetzt = (): string | null =>
    scanAuswahlRef.current !== null
      ? 'Artikelauswahl offen'
      : scanSperre({
          ...scanZustandRef.current,
          // Synchron beim Kartenstart gesetzt – greift schon vor dem nächsten Rendern.
          kartenzahlung: scanZustandRef.current.kartenzahlung || laeuftRef.current,
        });
  /** Dialog vor dem Bon: Scans hinter dem Dialog nicht in den Bon (Felder darin bleiben frei). */
  const dialogOffen =
    gutscheinDialog !== null ||
    pfandDialog !== null ||
    plattformDialog ||
    tischplanOffen ||
    anmeldeDialog ||
    notizOffen ||
    kundenSuche !== null ||
    // W-14 (Welle 2): Kartenstatus in der V2-Hülle (Start, Beleg, K6 Abgelehnt/Abgebrochen,
    // K7 Start unklar) sowie Rückerstattung und Bezahlentscheidung – ein Scan liefe sonst in
    // den Bon hinter der Anzeige (der abgelehnte Betrag im Band stimmte nicht mehr)
    kartenStatus !== null ||
    erstattung !== null ||
    bezahlFrage !== null ||
    // D11/D6 (Codex Sprint 01.11., HOCH): offene Rückfrage (Entfernen, Leeren) bzw.
    // Empfängerdialog – ein Scan veränderte sonst den Bon hinter der Rückfrage, die Bestätigung
    // verwürfe dann auch die neu gescannte Ware
    rueckfrage !== null ||
    empfaengerSichtbar ||
    // Wiegeartikel: kein Scan in den Bon hinter dem Gewichtsdialog (auch kein zweiter Wiegescan)
    wiegeAnfrageRef.current !== null;
  const dialogOffenRef = useRef(dialogOffen);
  dialogOffenRef.current = dialogOffen;
  /** Abgebrochener Präfix-Scan im Feld: Feld auf den Wert vor dem Präfix, Hinweis. */
  const feldPraefixVerwerfen = (
    feld: HTMLInputElement | HTMLTextAreaElement,
    v: FeldVerworfen,
  ): void => {
    letzterScanZeit.current = performance.now();
    setzeFeldwert(feld, v.wertVorher);
    setFehler(
      `Scan${v.code === '' ? '' : ` ${v.code}`} unvollständig – nichts übernommen, bitte erneut scannen`,
    );
  };
  /** Läuft im Feld ein Präfix-Scan, verwirft ihn ein Timer nach der Frist (samt Feldinhalt). */
  const feldPraefixUeberwachen = (feld: HTMLInputElement | HTMLTextAreaElement): void => {
    if (feldPraefixTimer.current !== null) {
      clearTimeout(feldPraefixTimer.current);
      feldPraefixTimer.current = null;
    }
    if (!feldScan.current.praefixLaeuft()) {
      return;
    }
    feldPraefixTimer.current = setTimeout(() => {
      feldPraefixTimer.current = null;
      const v = feldScan.current.ablauf(performance.now());
      if (v !== null) {
        feldPraefixVerwerfen(feld, v);
      } else {
        feldPraefixUeberwachen(feld);
      }
    }, PRAEFIX_ABSTAND_MS + 20);
  };
  feldScanRef.current = (e, feld) => {
    const jetzt = tastenZeit(e);
    // Nachlauf (CR+LF) eines Scans, der den Fokus in ein Feld verschoben hat: verwerfen.
    if (!feldScan.current.hatZeichen(jetzt) && istNachlauf(e.key, jetzt, letzterScanZeit.current)) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const treffer = feldScan.current.taste(
      e.key,
      jetzt,
      feld.value,
      ladeScannerPraefix(localStorage),
    );
    feldPraefixUeberwachen(feld);
    if (treffer === null) {
      return;
    }
    if (treffer === 'praefix' || treffer === 'schlucken') {
      // Scannerpräfix (PB-1) bzw. Rest eines verworfenen Präfix-Scans: nie als Zeichen ins
      // Feld, nie als Abschluss (kein Kassieren mit einem Bruchstück)
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (typeof treffer === 'object' && 'restVerworfen' in treffer) {
      // Abschluss eines verworfenen Scan-Rests (Runde 2): Taste verwerfen, Verlust melden –
      // ein in den Rest gefallener neuer Scan geht nie still verloren
      e.preventDefault();
      e.stopPropagation();
      letzterScanZeit.current = performance.now();
      setFehler(
        `Scan${treffer.restVerworfen === '' ? '' : ` ${treffer.restVerworfen}`} unvollständig – nichts übernommen, bitte erneut scannen`,
      );
      return;
    }
    if (typeof treffer === 'object' && 'verworfen' in treffer) {
      // Präfix-Scan abgebrochen (Frist, fremde Taste): Feld zurück, Taste verwerfen
      e.preventDefault();
      e.stopPropagation();
      feldPraefixVerwerfen(feld, treffer);
      return;
    }
    // H4: Betragsfelder in Dialogen (data-scansperre) sind auch ohne Zahlungssperre gesperrt
    const sperre = scanSperreJetzt() ?? feldScanSperre(feld);
    const suchfeld = feld.dataset.scanfeld !== undefined;
    if (treffer === 'nachlauf') {
      // Zweites Abschlusszeichen (CR+LF) eines verworfenen bzw. übernommenen Scans
      if (sperre !== null || suchfeld) {
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }
    // Ungesperrt: kurze schnelle Folgen (1–3 Zeichen ohne Präfix) bleiben Eingabe – im
    // Suchfeld übernimmt Enter die Handsuche (Artikelcodes laufen dort ebenfalls als Scan);
    // nach einer schnellen Folge nie einen Bezeichnungs- bzw. Anfangstreffer automatisch.
    if (sperre === null && treffer.kurz) {
      if (suchfeld) {
        schnellesSuchEnter.current = e;
      }
      return;
    }
    letzterScanZeit.current = jetzt;
    if (sperre !== null && treffer.kurz) {
      // Gesperrtes Feld (z. B. „Gegeben“): kurze schnelle Folge mit sofortigem Enter kann ein
      // Scan ohne Präfix sein. Scan und getippte Ziffern lassen sich nicht sicher trennen –
      // Enter verwerfen und das Feld leeren, damit kein Betrag aus einem Code entsteht.
      e.preventDefault();
      e.stopPropagation();
      setzeFeldwert(feld, '');
      setFehler(
        `Eingabe ${treffer.code} verworfen (${sperre}) – möglicher Scan; Betrag bitte neu eingeben`,
      );
      return;
    }
    if (sperre !== null) {
      // Abschlusszeichen verwerfen (kein Kassieren mit einem Barcode als Betrag), Feld zurück.
      e.preventDefault();
      e.stopPropagation();
      setzeFeldwert(feld, treffer.wertVorher);
      setFehler(gesperrterScan(treffer.code, sperre));
      return;
    }
    if (suchfeld) {
      // Scan ins Suchfeld (Enter- oder Tab-Suffix): wie ein Scan verarbeiten.
      e.preventDefault();
      e.stopPropagation();
      setzeFeldwert(feld, treffer.wertVorher);
      if (!doppelFilterRef.current.annehmen(treffer.code, jetzt)) {
        return; // doppelt gemeldeter Scan (HID und Feld): nicht zweimal in den Bon
      }
      scanVerarbeitenRef.current(treffer.code);
    }
  };
  feldEingabeRef.current = (e, feld) => {
    const daten = feldEingabeDaten(e);
    if (daten === null) {
      return;
    }
    // Jedes noch nicht per keydown gezählte Zeichen läuft als Taste durch die Felderkennung;
    // Verhindern/Anhalten wirkt auf das Einfügen (wie bei keydown)
    const zeichen = feldScan.current.eingabeZeichen(daten, tastenZeit(e));
    for (const key of zeichen) {
      feldScanRef.current(
        {
          key,
          timeStamp: e.timeStamp,
          preventDefault: () => {
            e.preventDefault();
          },
          stopPropagation: () => {
            e.stopPropagation();
          },
        } as KeyboardEvent,
        feld,
      );
    }
    feldScan.current.eingabeGezaehlt();
  };
  /** Kachel aus Scan bzw. Auswahl in den Bon – Stückzahl aus dem Ziffernblock wie bei der Suche. */
  function scanUebernehmen(k: Schnellwahl): void {
    const menge = vorgabeMenge(zifferCent, zifferMenge);
    artikelWaehlen(k, menge);
    setZifferCent(() => 0);
    setZifferMenge(1);
    // D8: die getippte Zahl ist als Stückzahl verbraucht – Fenster „Menge oder Betrag“ schließen
    setZiffernOffen(false);
  }
  const scanUebernehmenRef = useRef(scanUebernehmen);
  scanUebernehmenRef.current = scanUebernehmen;
  const konfigRef = useRef(konfig);
  konfigRef.current = konfig;
  kurzcodeRef.current = (code, grund): void => {
    letzterScanZeit.current = performance.now();
    setFehler(
      grund === 'kurz'
        ? kurzcodeHinweis(code)
        : `Scan${code === '' ? '' : ` ${code}`} unvollständig – nichts übernommen, bitte erneut scannen`,
    );
  };
  scanVerarbeitenRef.current = (code: string): void => {
    letzterScanZeit.current = performance.now();
    const vorher =
      scanSperreJetzt() ?? (dialogOffenRef.current ? 'Dialog offen – erst schließen' : null);
    if (vorher !== null) {
      setFehler(gesperrterScan(code, vorher));
      return;
    }
    void Promise.resolve(stammCodeSuche(konfigRef.current)(code)).then((ergebnis) => {
      // W-14: nach der (ggf. asynchronen) Suche auch einen inzwischen geöffneten Dialog beachten
      const sperre =
        scanSperreJetzt() ?? (dialogOffenRef.current ? 'Dialog offen – erst schließen' : null);
      if (sperre !== null) {
        setFehler(gesperrterScan(code, sperre));
        return;
      }
      switch (ergebnis.art) {
        case 'gutschein':
          // Gutscheincode (QR auf der Gutscheinkarte) → Einlösen mit Vorbelegung
          setScanCode(ergebnis.code);
          setGutscheinDialog('einloesen');
          return;
        case 'unbekannt':
        case 'gesperrt':
          // gesperrt: nur gesperrte Artikel – Meldung mit Sperrgrund, nichts in den Bon
          setFehler(ergebnis.hinweis);
          return;
        case 'artikel': {
          const [erster, ...weitere] = ergebnis.treffer;
          const gesperrt = ergebnis.gesperrt ?? [];
          if (erster === undefined) {
            return;
          }
          if (weitere.length === 0 && gesperrt.length === 0) {
            scanUebernehmenRef.current(erster);
          } else {
            // PB-1: mehrdeutig (auch verkaufbar + gesperrt) – nie still der erste
            setScanAuswahl({ code: ergebnis.code, treffer: ergebnis.treffer, gesperrt });
          }
        }
      }
    });
  };
  // Zahlungsstand für die Kundenanzeige, aus dem Ablauf abgeleitet (auch „nichts offen“
  // nach Abbruch des Bar-Dialogs). Abgelehnt/abgebrochen gilt bis zum nächsten Versuch.
  const anzeigeZahlung: Zahlungsanzeige =
    zahlung !== null
      ? 'karte'
      : kartenStand === 'beleg'
        ? 'beleg'
        : vorgangOffen
          ? 'pruefung'
          : barDialog
            ? 'bar'
            : (kartenStand ?? 'keine');
  const anzeigeBetrag = zahlung === null ? rest : zahlung.betragCent;
  useEffect(() => {
    if (anzeigeZahlung === 'karte' || anzeigeZahlung === 'bar') {
      setKartenStand(null);
    }
    anzeige.current?.sende({ typ: 'zahlung', status: anzeigeZahlung, betragCent: anzeigeBetrag });
  }, [anzeigeZahlung, anzeigeBetrag]);
  const ziffernFeld = useRef<HTMLDivElement | null>(null);
  // D8: Auslöser vor der Fokusfalle merken und beim Schließen (Esc, Abbrechen, Vormerken)
  // wieder fokussieren, sofern der Fokus nicht schon woanders liegt
  useEffect(() => {
    if (!ziffernOffen) {
      return;
    }
    const a = document.activeElement;
    const ausloeser = a instanceof HTMLElement && a !== document.body ? a : null;
    return () => {
      const jetzt = document.activeElement;
      if (ausloeser?.isConnected === true && (jetzt === null || jetzt === document.body)) {
        ausloeser.focus();
      }
    };
  }, [ziffernOffen]);
  // D8: Fokusfalle des Fensters „Menge oder Betrag“ (Tab erreicht den Verkauf dahinter nicht)
  useFokusfalleAmFenster(ziffernFeld, ziffernOffen);
  const [ziffernFokus, setZiffernFokus] = useState(0);
  useEffect(() => {
    const beiTaste = (e: KeyboardEvent): void => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || ziffernSperre.current) {
        return;
      }
      const ziel = e.target;
      if (
        ziel instanceof HTMLInputElement ||
        ziel instanceof HTMLTextAreaElement ||
        ziel instanceof HTMLSelectElement ||
        (ziel instanceof Element && ziel.closest('.ziffernblock') !== null)
      ) {
        return;
      }
      const t = /^[0-9]$/.test(e.key) ? tastaturZuZiffer(e.key) : null;
      if (t === null) {
        return;
      }
      e.preventDefault();
      setZiffernOffen(true);
      setZifferCent((c) => ziffernTaste(c, t));
      setZiffernFokus((n) => n + 1);
    };
    window.addEventListener('keydown', beiTaste);
    return () => {
      window.removeEventListener('keydown', beiTaste);
    };
  }, []);
  useEffect(() => {
    if (ziffernFokus > 0) {
      ziffernFeld.current?.querySelector<HTMLElement>('.ziffernblock')?.focus();
    }
  }, [ziffernFokus]);
  function ziffernUmschalten(): void {
    if (ziffernOffen) {
      // Beim Schließen keine unsichtbare Vormerkmenge zurücklassen
      setZiffernOffen(false);
      setZifferCent(() => 0);
      setZifferMenge(1);
    } else {
      setZiffernOffen(true);
      setZiffernFokus((n) => n + 1);
    }
  }

  /** Ohne Empfänger: Empfängerdaten und Kundenrabatt vom Bon nehmen. */
  function ohneEmpfaenger(): void {
    setEmpfaengerOffen(false);
    setKundenSuche(null);
    setEmpfaengerEingabe(KEIN_EMPFAENGER);
    setKundeEmail(null);
    setKundeId(null);
    // Ohne Kunde kein Kundenrabatt mehr
    setPositionen((alt) => [...nachKundenwechsel(alt, kundenRabatt, null, gutscheineImBon)]);
    setKundenRabatt(null);
  }

  if (offlineBeleg !== null) {
    return (
      <div className="karte">
        <p className="warnung">
          Kasse nicht erreichbar – Beleg gepuffert und wird nach Wiederverbindung nachsigniert.
        </p>
        <pre className="bon">{offlineBelegText(offlineBeleg, props.unternehmer).join('\n')}</pre>
        <div className="zeile kein-druck">
          <button
            className="taste zweit"
            onClick={() => {
              window.print();
            }}
          >
            Drucken
          </button>
          <button
            className="taste gross"
            onClick={() => {
              setOfflineBeleg(null);
            }}
          >
            Neuer Verkauf
          </button>
        </div>
      </div>
    );
  }

  if (beleg !== null) {
    return (
      <>
        {rueckgeld !== null && (
          <div className="karte kein-druck rueckgeld-anzeige">
            Gegeben {formatiereEuro(rueckgeld.gegebenCent)} € · Rückgeld{' '}
            <strong>{formatiereEuro(rueckgeld.rueckgeldCent)} €</strong>
          </div>
        )}
        {ladeHinweis !== null && (
          <p className="warnung kein-druck" role="alert">
            {ladeHinweis}
          </p>
        )}
        <Belegansicht
          beleg={beleg.beleg}
          text={beleg.text}
          autoDruck={ladeAutoDruck(localStorage, kassenId)}
          {...(props.bridgeDruck === undefined ? {} : { bridgeDruck: props.bridgeDruck })}
          {...(props.emailSenden === undefined ? {} : { emailSenden: props.emailSenden })}
          details={() => api.beleg(kassenId, beleg.beleg.belegnummer)}
          unternehmer={props.unternehmer}
          emailVorschlag={kundeEmail}
          aktionen={
            <button
              className="taste gross"
              onClick={() => {
                setBeleg(null);
                setRueckgeld(null);
                setLadeHinweis(null);
              }}
            >
              Neuer Verkauf
            </button>
          }
        />
      </>
    );
  }

  // Bediener-Verwaltung aktiv und niemand angemeldet: nur die Anmeldeseite.
  if (bedienerPflicht && anmeldung === null) {
    return (
      <BedienerAnmeldung
        seite
        bediener={verwaltung.bediener}
        laeuft={anmeldeLaeuft}
        fehler={anmeldeFehler}
        onAnmelden={anmelden}
        hinweis={verwaltung.geladen ? undefined : 'Bediener werden geladen …'}
      />
    );
  }

  const bonPositionen = ohneRabatt(positionen);
  // Gewichtszeilen zählen als ein Stück (ihre Menge ist kg)
  const stueck = bonPositionen.reduce((n, p) => n + (istGewichtsPosition(p) ? 1 : p.menge), 0);
  const gewaehlteZeile = gewaehlt === null || teilen ? undefined : positionen[gewaehlt.index];
  const gewaehltIndex =
    gewaehlt !== null &&
    gewaehlteZeile !== undefined &&
    zeilenKennung(gewaehlteZeile) === gewaehlt.kennung
      ? gewaehlt.index
      : -1;
  // Hervorgehoben wird nur die erste passende Zeile – dorthin legt inBon gleiche Artikel.
  const zuletztIndex =
    zuletzt === null ? -1 : positionen.findIndex((p) => zeilenSchluessel(p) === zuletzt.schluessel);
  const zeigeKundenzeile = !empfaengerSichtbar && empfaengerEingabe.name.trim() !== '';
  const zeigeBediener = bedienerPflicht || (modul.bediener && bediener.liste.length > 0);
  const mehrAktiv = ausserHaus || training || notizOffen || notiz.trim() !== '' || bedienerOffen;

  /** Enter in der Artikelsuche bzw. „Suchen ↵“ der Bildschirmtastatur (R2). */
  function sucheBestaetigen(schnell: boolean): void {
    // Artikelcode (voller GTIN-Code oder Barcode/EAN/Artikelnummer im Stamm, auch
    // kurz oder nicht numerisch – PB-1): wie ein Scan – gemeinsame Code-Suche,
    // Mehrfachtreffer-Auswahl, Sperrgrund, Zahlungssperre.
    if (istArtikelcodeImStamm(konfig, suche)) {
      scanVerarbeitenRef.current(suche.trim());
      setSuche('');
      return;
    }
    // Genau ein Treffer über die Bezeichnung: direkt in den Bon. Reine Ziffern ohne
    // Code-Treffer (Anfangstreffer) nur anzeigen, nie automatisch übernehmen – ein
    // unbekannter Scan legte sonst still einen anderen Artikel in den Bon (R1, M2).
    // Ebenso nach einer schnellen Folge (Scan, z. B. „AB“ + Enter): nur anzeigen.
    const treffer = sucheKacheln(konfig.kacheln, suche);
    if (
      !schnell &&
      treffer.length === 1 &&
      treffer[0] !== undefined &&
      !/^\d+$/.test(suche.trim())
    ) {
      const menge = vorgabeMenge(zifferCent, zifferMenge);
      artikelWaehlen(treffer[0], menge);
      setZifferCent(() => 0);
      setZifferMenge(1);
      setSuche('');
    }
  }

  /** R6: Warnungen der Verkaufsfläche in heutiger Anzeigereihenfolge (für die Warnzeile). */
  const verkaufWarnungen = verdichteWarnungen([
    training ? 'TRAINING – es entsteht kein Barumsatz' : '',
    fehler ?? '',
    bonierMeldung ?? '',
    nachsendeOffen > 0
      ? `${String(nachsendeOffen)} Küchenbon(s) noch nicht angekommen – werden automatisch nachgesendet`
      : '',
  ]);
  const warnungenVerdichtet = bt.tastatur !== null && verkaufWarnungen !== null;

  return (
    <>
      <div className="verkauf-raster" ref={verkaufRaster}>
        {bt.tastatur !== null && (
          <VerkaufsTastatur
            steuerung={bt.tastatur}
            wurzel={verkaufRaster}
            zielElement={bt.zielElement}
            warnzeile={
              <TastaturWarnzeile
                warnungen={verkaufWarnungen}
                onAlleAnzeigen={() => {
                  // R6: schließt zustandserhaltend (Text, Cursor, Ziel); „Tastatur“ öffnet wieder
                  bt.schliessen();
                }}
              />
            }
          />
        )}
        <nav className="funktionen" aria-label="Funktionen">
          <FunktionsTaste
            icon="kunde"
            text={modul.kunden ? 'Kunde' : 'Empfänger'}
            aktiv={empfaengerSichtbar}
            title="Rechnungsempfänger aus dem Kundenstamm wählen oder eingeben (Rechnung für Firmenkunden)"
            onClick={() => {
              if (empfaengerOffen) {
                setEmpfaengerOffen(false);
                setKundenSuche(null);
              } else {
                setEmpfaengerOffen(true);
                setKundenSuche('');
              }
            }}
          />
          <FunktionsTaste
            icon="rabatt"
            text={rabatt === null ? 'Rabatt' : `Rabatt ${String(rabatt)} %`}
            aktiv={rabattOffen || rabatt !== null}
            disabled={
              ohneRabatt(positionen).length === 0 ||
              gutscheinStand.ausgaben.length > 0 ||
              mitGutschein ||
              !rechte.rabatt
            }
            title={
              !rechte.rabatt
                ? 'Kein Rabattrecht für diesen Bediener'
                : gutscheinStand.ausgaben.length > 0 || mitGutschein
                  ? 'Kein Rabatt auf Bons mit Gutscheinen'
                  : undefined
            }
            onClick={() => {
              setRabattOffen(!rabattOffen);
            }}
          />
          {modul.pfand && (
            <FunktionsTaste
              icon="pfand"
              text="Pfand"
              disabled={laeuft || training}
              onClick={() => {
                setPfandDialog('pfand');
              }}
            />
          )}
          {modul.pfand && (
            <FunktionsTaste
              icon="leergut"
              text="Leergut"
              disabled={laeuft || training}
              title="Leergut zurücknehmen – wird vom Bon abgezogen"
              onClick={() => {
                setPfandDialog('leergut');
              }}
            />
          )}
          {modul.tische && (
            <FunktionsTaste
              icon="tische"
              text="Tische"
              zahl={alleTische.length}
              aktiv={tischeOffen}
              disabled={alleTische.length === 0 && !hatTischplan}
              onClick={() => {
                void serverTischeLaden();
                if (hatTischplan) {
                  setTischplanOffen(true);
                } else {
                  setTischeOffen(!tischeOffen);
                }
              }}
            />
          )}
          {modul.tische && (
            <FunktionsTaste
              icon="parken"
              text={aktiverTisch === null ? 'Parken' : 'Tisch parken'}
              disabled={positionen.length === 0 || laeuft || bonierLaeuft || mitGutschein}
              title={
                mitGutschein
                  ? 'Mit eingelöstem Gutschein kann der Bon nicht geparkt werden'
                  : aktiverTisch === null
                    ? undefined
                    : `${aktiverTisch} parken`
              }
              onClick={() => void tischParken()}
            />
          )}
          {modul.kuechenbons && (
            <FunktionsTaste
              icon="bonieren"
              text="Bonieren"
              zahl={zuBonieren.length}
              aktiv={zuBonieren.length > 0}
              disabled={laeuft || bonierLaeuft || training || zuBonieren.length === 0}
              title="Neu Bestelltes an Küche und Schank senden, Abbestelltes stornieren"
              onClick={() => void bonierenJetzt()}
            />
          )}
          {modul.rechnungTeilen && (
            <FunktionsTaste
              icon="teilen"
              text="Teilen"
              aktiv={teilen}
              disabled={laeuft || mitGutschein || (!teilen && !teilbar(positionen))}
              title="Getrennt zahlen: nur die gewählten Mengen jetzt kassieren, der Rest bleibt im Bon"
              onClick={() => {
                setTeilen((t) => !t);
                setAuswahl(new Map());
              }}
            />
          )}
          <div className="mehr-bereich">
            <FunktionsTaste
              icon="mehr"
              text="Mehr"
              aktiv={mehrOffen || mehrAktiv}
              menue={{ offen: mehrOffen }}
              title="Weitere Funktionen"
              onClick={() => {
                setMehrOffen(!mehrOffen);
              }}
            />
            {mehrOffen && (
              <div className="mehr-menue" role="menu" aria-label="Weitere Funktionen">
                {modul.ausserHaus && (
                  <button
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={ausserHaus}
                    title="Mitnahme/Lieferung: Artikel mit abweichendem Steuersatz werden damit gebucht"
                    onClick={() => {
                      setAusserHaus(!ausserHaus);
                      setMehrOffen(false);
                    }}
                  >
                    <FunktionsSymbol icon="ausserhaus" />
                    <span>Außer Haus</span>
                    {ausserHaus && <em className="an">an</em>}
                  </button>
                )}
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={notizOffen || notiz.trim() !== ''}
                  onClick={() => {
                    setNotizOffen(!notizOffen);
                    setMehrOffen(false);
                  }}
                >
                  <FunktionsSymbol icon="notiz" />
                  <span>Notiz</span>
                  {notiz.trim() !== '' && <em className="an">gesetzt</em>}
                </button>
                {modul.gutscheine && (
                  <button
                    type="button"
                    role="menuitem"
                    disabled={!navigator.onLine || laeuft}
                    onClick={() => {
                      setGutscheinDialog('verkauf');
                      setMehrOffen(false);
                    }}
                  >
                    <FunktionsSymbol icon="gutschein" />
                    <span>Gutschein verkaufen</span>
                  </button>
                )}
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={training}
                  onClick={() => {
                    setTraining(!training);
                    setMehrOffen(false);
                  }}
                >
                  <FunktionsSymbol icon="training" />
                  <span>Training</span>
                  {training && <em className="an">an</em>}
                </button>
                {zeigeBediener && (
                  <button
                    type="button"
                    role="menuitem"
                    title="Bediener wechseln"
                    onClick={() => {
                      setBedienerOffen(!bedienerOffen);
                      setMehrOffen(false);
                    }}
                  >
                    <FunktionsSymbol icon="bediener" />
                    <span>{bedienerName ?? 'Bediener'}</span>
                  </button>
                )}
                {(props.bridgeDruck !== undefined || props.ladeAmGeraet !== undefined) && (
                  <button
                    type="button"
                    role="menuitem"
                    title="Kassenlade öffnen (ohne Verkauf)"
                    onClick={() => {
                      setMehrOffen(false);
                      // Nativ: jeder Klick ein neuer Vorgang (neuer Schlüssel); sonst Server-Weg.
                      (props.ladeAmGeraet !== undefined
                        ? props.ladeAmGeraet(schluesselLadeManuell(kassenId))
                        : oeffneLade(api, kassenId)
                      )
                        .then((r) => {
                          if (r.ok !== true) {
                            setFehler(r.text);
                          }
                        })
                        .catch((e: unknown) => {
                          setFehler(e instanceof Error ? e.message : String(e));
                        });
                    }}
                  >
                    <FunktionsSymbol icon="schublade" />
                    <span>Schublade</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </nav>
        <aside className="karte bon-panel" aria-label="Aktueller Bon">
          <div className="bon-kopf">
            <h2>
              {/* D9: beim Teilen heißt der Bon „Teilen“ */}
              {teilen ? 'Teilen' : 'Aktueller Bon'}
              {aktiverTisch !== null && (teilen ? ` · ${aktiverTisch}` : ` – ${aktiverTisch}`)}
              <small>
                {bonPositionen.length} Pos. · {stueck} Stück
              </small>
            </h2>
            <button
              type="button"
              className="bon-leeren"
              disabled={positionen.length === 0 || laeuft || bonierLaeuft || teilen}
              onClick={bonLeerenFragen}
            >
              <FunktionsSymbol icon="leeren" groesse={20} />
              Leeren
            </button>
          </div>
          {(training || ausserHaus || notiz.trim() !== '') && (
            <div className="bon-modi">
              {training && <span className="modus warnung">TRAINING</span>}
              {ausserHaus && <span className="modus">Außer Haus</span>}
              {notiz.trim() !== '' && <span className="modus">Notiz: {notiz.trim()}</span>}
            </div>
          )}
          {zeigeKundenzeile && (
            <div className="kunden-zeile">
              <button
                type="button"
                className="kunde-oeffnen"
                title="Empfängerdaten ändern"
                onClick={() => {
                  setEmpfaengerOffen(true);
                }}
              >
                <span className="initialen" aria-hidden="true">
                  {initialen(empfaengerEingabe.name)}
                </span>
                <span className="kunde-text">
                  <strong>{empfaengerEingabe.name}</strong>
                  {empfaengerEingabe.anschrift.trim() !== '' && (
                    <small>{empfaengerEingabe.anschrift}</small>
                  )}
                </span>
              </button>
              {kundenRabatt !== null && (
                <span className="rabatt-badge" title="Fester Kundenrabatt">
                  −{kundenRabatt} %
                </span>
              )}
              <button type="button" className="kunde-entfernen" onClick={ohneEmpfaenger}>
                <span className="unsichtbar">Ohne Empfänger</span>
                <span aria-hidden="true">×</span>
              </button>
            </div>
          )}
          <div className="bon-inhalt" ref={bonInhalt}>
            <ul className="bon-liste">
              {positionen.map((p, i) => {
                const hervor = i === zuletztIndex;
                const gewichtZeile = gewichtsText(p);
                const einzelText = `${gewichtZeile ?? `${formatiereEuro(Math.round(einzelpreis(p)))} €`} · ${p.steuersatz} %`;
                const mengeText = gewichtZeile ?? `${String(p.menge)}×`;
                if (istRabatt(p)) {
                  return (
                    <li key={`${String(i)}-${p.bezeichnung}`} className="rabatt-zeile">
                      <span className="menge-pille" aria-hidden="true">
                        %
                      </span>
                      <span className="bez">
                        {kundenRabatt === rabatt
                          ? `Kunden${p.bezeichnung.toLowerCase()}`
                          : p.bezeichnung}
                        <span className="einzel">{p.steuersatz} % USt</span>
                      </span>
                      <span className="betrag">{formatiereEuro(p.bruttoCent)}</span>
                      <button
                        type="button"
                        className="zeile-x"
                        aria-label="Rabatt entfernen"
                        onClick={() => {
                          rabattSetzen(null);
                        }}
                      >
                        ✕
                      </button>
                    </li>
                  );
                }
                if (teilen) {
                  return (
                    // POS V2 Phase 2, Rahmen D9 `17:3444`: je Zeile „jetzt x / im Bon y“
                    <li key={`${String(i)}-${p.bezeichnung}`} className="teilen-zeile">
                      <span className="bez">
                        {p.bezeichnung}
                        <span className="einzel">
                          {einzelText} · im Bon{' '}
                          {istGewichtsPosition(p) ? 'ganz (Wägung)' : `${String(p.menge)}×`}
                          {(auswahl.get(i) ?? 0) > 0 &&
                            ` · jetzt ${formatiereEuro(anteilCent(p, auswahl.get(i) ?? 0))} €`}
                        </span>
                      </span>
                      <span className="zeile-aktionen">
                        <span className="stepper" title="Davon jetzt bezahlen">
                          <button
                            type="button"
                            aria-label="Weniger jetzt bezahlen"
                            onClick={() => {
                              setAuswahl((a) =>
                                auswahlSetzen(a, positionen, i, (a.get(i) ?? 0) - 1),
                              );
                            }}
                          >
                            −
                          </button>
                          <span>
                            {istGewichtsPosition(p)
                              ? `${(auswahl.get(i) ?? 0) > 0 ? '1' : '0'}/1`
                              : `${String(auswahl.get(i) ?? 0)}/${String(p.menge)}`}
                          </span>
                          <button
                            type="button"
                            aria-label="Mehr jetzt bezahlen"
                            onClick={() => {
                              setAuswahl((a) =>
                                auswahlSetzen(a, positionen, i, (a.get(i) ?? 0) + 1),
                              );
                            }}
                          >
                            +
                          </button>
                        </span>
                      </span>
                    </li>
                  );
                }
                const offen = i === gewaehltIndex;
                return (
                  <li
                    key={`${String(i)}-${p.bezeichnung}`}
                    className={`${offen ? 'gewaehlt' : ''}${hervor ? ' zuletzt' : ''}`}
                  >
                    <button
                      type="button"
                      className="zeile-kopf"
                      aria-expanded={offen}
                      aria-label={`${gewichtZeile === null ? `${String(p.menge)} ×` : `${gewichtZeile},`} ${p.bezeichnung}, ${formatiereEuro(p.bruttoCent)} € – Zeile bearbeiten`}
                      onClick={() => {
                        setGewaehlt(offen ? null : { index: i, kennung: zeilenKennung(p) });
                      }}
                    >
                      <span className="menge-pille">
                        {gewichtZeile === null ? `${String(p.menge)}×` : 'kg'}
                      </span>
                      <span className="bez">
                        {p.bezeichnung}
                        <span className="einzel">{einzelText}</span>
                      </span>
                      <span className="betrag">{formatiereEuro(p.bruttoCent)}</span>
                    </button>
                    {offen && (
                      <span className="zeile-aktionen">
                        {/* Gewichtszeilen: keine Stückmenge ± und kein Stückpreis – nur Entfernen */}
                        {gewichtZeile === null && (
                          <span className="stepper">
                            <button
                              type="button"
                              aria-label="Menge verringern"
                              onClick={() => {
                                // Bei Menge 0 verschwindet die Zeile – dann keine Auswahl halten
                                zeilenAktion.current = p.menge > 1;
                                setPositionen((alt) =>
                                  aendereMitRabatt(alt, (b) => mengeSetzen(b, i, p.menge - 1)),
                                );
                              }}
                            >
                              −
                            </button>
                            <span>{p.menge}</span>
                            <button
                              type="button"
                              aria-label="Menge erhöhen"
                              onClick={() => {
                                zeilenAktion.current = true;
                                setPositionen((alt) =>
                                  aendereMitRabatt(alt, (b) => mengeSetzen(b, i, p.menge + 1)),
                                );
                              }}
                            >
                              +
                            </button>
                          </span>
                        )}
                        {rechte.rabatt && gewichtZeile === null && (
                          <button
                            type="button"
                            className="taste zweit"
                            title="Neuer Einzelpreis (3,50), Abzug (-1,50) oder Rabatt (10%)"
                            onClick={() => {
                              const eingabe = window.prompt(
                                `${p.bezeichnung}: neuer Einzelpreis (z. B. 3,50), Abzug (-1,50) oder Rabatt (10%)`,
                                '',
                              );
                              if (eingabe === null || eingabe.trim() === '') {
                                return;
                              }
                              try {
                                const neu = passePositionAn(p, eingabe);
                                zeilenAktion.current = true;
                                setPositionen((alt) =>
                                  aendereMitRabatt(alt, (b) =>
                                    b.map((x, j) => (j === i ? neu : x)),
                                  ),
                                );
                                setGewaehlt({ index: i, kennung: zeilenKennung(neu) });
                                setFehler(null);
                              } catch (err: unknown) {
                                setFehler(err instanceof Error ? err.message : String(err));
                              }
                            }}
                          >
                            Preis ändern
                          </button>
                        )}
                        <button
                          type="button"
                          className="taste zweit gefahr"
                          onClick={() => {
                            // D11: Rückfrage vor dem Entfernen (Menge ± fragt nie)
                            setRueckfrage({
                              titel: 'Position entfernen?',
                              icon: 'leeren',
                              text: `${p.bezeichnung} (${mengeText}, ${formatiereEuro(p.bruttoCent)} €) wird aus dem Bon genommen. Kein Beleg, kein Storno.`,
                              zurueck: 'Behalten',
                              bestaetigen: 'Entfernen',
                              aktion: () => {
                                setPositionen((alt) =>
                                  aendereMitRabatt(alt, (b) => mengeSetzen(b, i, 0)),
                                );
                                setGewaehlt(null);
                              },
                            });
                          }}
                        >
                          Entfernen
                        </button>
                      </span>
                    )}
                  </li>
                );
              })}
              {positionen.length === 0 && (
                <li className="bon-leer">
                  Noch keine Positionen – Kachel antippen, Artikel suchen oder scannen.
                </li>
              )}
            </ul>
          </div>
          {(gutscheinStand.einloesungen.length > 0 ||
            training ||
            empfaengerSichtbar ||
            fehler !== null ||
            bonierMeldung !== null ||
            plattformMeldung !== null ||
            nachsendeOffen > 0 ||
            rabattOffen ||
            notizOffen ||
            (modul.tische && tischeOffen) ||
            bedienerOffen) && (
            <div className="bon-werkzeuge">
              {gutscheinStand.einloesungen.length > 0 && (
                <div className="chips" role="group" aria-label="Eingelöste Gutscheine">
                  {gutscheinStand.einloesungen.map((e) => (
                    <button
                      key={e.code}
                      type="button"
                      className="aktiv"
                      title="Gutschein vom Bon entfernen"
                      onClick={() => {
                        einloesungEntfernen(e.code);
                      }}
                    >
                      {e.code} · {formatiereEuro(e.betragCent)} € ×
                    </button>
                  ))}
                </div>
              )}
              {/* R6: bei offener Tastatur stehen die Warnungen nur verdichtet in deren Warnzeile */}
              {!warnungenVerdichtet && training && (
                <p className="warnung">TRAINING – es entsteht kein Barumsatz</p>
              )}
              {empfaengerSichtbar && (
                // POS V2 Phase 2, Rahmen D6 `16:2483`: Empfänger als Dialog (Regeln aus rechnung.ts
                // unverändert, Eingaben wirken wie bisher sofort am Bon)
                <div
                  ref={empfaengerDialog}
                  className="zahlung-dialog"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="empfaenger-titel"
                  tabIndex={-1}
                  style={{ outline: 'none' }}
                  onKeyDown={(e) => {
                    // Esc schließt wie „Übernehmen“; die Eingaben wirken wie bisher sofort am Bon.
                    // R5: erstes Esc bei offener Tastatur schließt nur sie.
                    if (e.key === 'Escape' && !istTastaturEsc(e)) {
                      e.preventDefault();
                      e.stopPropagation();
                      setEmpfaengerOffen(false);
                      setKundenSuche(null);
                    }
                  }}
                >
                  <div
                    className={`karte dlg empfaenger-dialog${btEmpf.tastatur === null ? '' : ' mit-tastatur'}`}
                  >
                    <div className="dlg-kopf">
                      <h2 id="empfaenger-titel">Rechnungsempfänger</h2>
                      <span className="dlg-meta">Zu zahlen {formatiereEuro(summe)} €</span>
                      {/* BT2-D6: ein Knopf im Dialogkopf für das zuletzt aktive Feld (R1) */}
                      <TastaturKnopf steuerung={btEmpf.knopf()} klein />
                    </div>
                    <div className="dlg-inhalt">
                      <div
                        className={`dlg-info${Math.abs(summe) > KLEINBETRAG_GRENZE_CENT ? ' dlg-warnung' : ''}`}
                      >
                        <p>
                          Rechnung mit Empfänger (§ 11 UStG) – ohne Empfänger gilt der Kassenbeleg
                          {Math.abs(summe) > KLEINBETRAG_GRENZE_CENT &&
                            ' · über 400 €: Name und Anschrift nötig (außer Kleinunternehmer)'}
                          {Math.abs(summe) > UID_EMPFAENGER_GRENZE_CENT &&
                            ' · über 10.000 €: UID des Empfängers angeben'}
                        </p>
                      </div>
                      {modul.kunden && (
                        <label className="dlg-feld">
                          <span className="dlg-feldname">Kunde suchen</span>
                          <input
                            data-scansperre="Kundensuche"
                            value={kundenSuche ?? ''}
                            placeholder="Name, Ort oder UID"
                            autoFocus
                            onChange={(e) => {
                              setKundenSuche(e.target.value);
                            }}
                            {...btEmpf.feld('kunde', {
                              name: 'Kunde suchen',
                              setzen: setKundenSuche,
                              beimFokus: () => {
                                if (kundenSuche === null) {
                                  setKundenSuche('');
                                }
                              },
                            })}
                          />
                        </label>
                      )}
                      {modul.kunden && kundenSuche !== null && kundenTreffer.length > 0 && (
                        <ul className="kunden-treffer" aria-label="Kunden">
                          {kundenTreffer.map((k) => (
                            <li key={k.id}>
                              <button
                                type="button"
                                onClick={() => {
                                  kundeUebernehmen(k);
                                }}
                              >
                                <strong>{k.name}</strong> · {k.anschrift}
                                {k.uid !== null && ` · ${k.uid}`}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="empfaenger-felder">
                        <label className="dlg-feld">
                          <span className="dlg-feldname">Empfänger (Name)</span>
                          <input
                            data-scansperre="Empfängerfeld"
                            value={empfaengerEingabe.name}
                            {...(modul.kunden ? {} : { autoFocus: true })}
                            onChange={(e) => {
                              setEmpfaengerEingabe({ ...empfaengerEingabe, name: e.target.value });
                            }}
                            {...btEmpf.feld('name', {
                              name: 'Empfänger (Name)',
                              setzen: (v) => {
                                setEmpfaengerEingabe((alt) => ({ ...alt, name: v }));
                              },
                            })}
                          />
                        </label>
                        <label className="dlg-feld">
                          <span className="dlg-feldname">UID (Pflicht ab 10.000 €)</span>
                          <input
                            data-scansperre="Empfängerfeld"
                            value={empfaengerEingabe.uid}
                            onChange={(e) => {
                              setEmpfaengerEingabe({ ...empfaengerEingabe, uid: e.target.value });
                            }}
                            {...btEmpf.feld('uid', {
                              name: 'UID',
                              setzen: (v) => {
                                setEmpfaengerEingabe((alt) => ({ ...alt, uid: v }));
                              },
                            })}
                          />
                        </label>
                        <label className="dlg-feld empfaenger-anschrift">
                          <span className="dlg-feldname">Anschrift</span>
                          <input
                            data-scansperre="Empfängerfeld"
                            value={empfaengerEingabe.anschrift}
                            onChange={(e) => {
                              setEmpfaengerEingabe({
                                ...empfaengerEingabe,
                                anschrift: e.target.value,
                              });
                            }}
                            {...btEmpf.feld('anschrift', {
                              name: 'Anschrift',
                              setzen: (v) => {
                                setEmpfaengerEingabe((alt) => ({ ...alt, anschrift: v }));
                              },
                            })}
                          />
                        </label>
                      </div>
                      {rechnungsempfaenger.fehler !== null && (
                        <p className="warnung" role="alert">
                          {rechnungsempfaenger.fehler}
                        </p>
                      )}
                    </div>
                    {btEmpf.tastatur !== null && <Bildschirmtastatur steuerung={btEmpf.tastatur} />}
                    <div className="dlg-fuss">
                      <button type="button" className="taste zweit" onClick={ohneEmpfaenger}>
                        Ohne Empfänger
                      </button>
                      <button
                        type="button"
                        className="taste"
                        disabled={empfaengerEingabe.name.trim() === ''}
                        onClick={() => {
                          setEmpfaengerOffen(false);
                          setKundenSuche(null);
                        }}
                      >
                        Übernehmen
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {!warnungenVerdichtet && fehler !== null && <p className="fehler">{fehler}</p>}
              {!warnungenVerdichtet && bonierMeldung !== null && (
                <p className="hinweis">{bonierMeldung}</p>
              )}
              {plattformMeldung !== null && <p className="ok">{plattformMeldung}</p>}
              {!warnungenVerdichtet && nachsendeOffen > 0 && (
                <p className="warnung">
                  {nachsendeOffen} Küchenbon(s) noch nicht angekommen – werden automatisch
                  nachgesendet
                </p>
              )}
              {rabattOffen && (
                <div className="chips rabatt-chips" role="group" aria-label="Rabatt auf den Bon">
                  {RABATT_STUFEN.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={rabatt === p ? 'aktiv' : ''}
                      onClick={() => {
                        rabattSetzen(p);
                      }}
                    >
                      {p} %
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={rabatt === null}
                    onClick={() => {
                      rabattSetzen(null);
                    }}
                  >
                    kein Rabatt
                  </button>
                </div>
              )}
              {notizOffen && (
                // BT2-NZ / BT3-TQ-NZ: Knopf „Tastatur“ neben dem Feld, Zähler bis 200 Zeichen
                <div className="notiz-feld">
                  <label htmlFor="belegnotiz">
                    Notiz auf dem Beleg <span className="dlg-feldname">{notiz.length}/200</span>
                  </label>
                  <div className="feld-mit-knopf">
                    <input
                      id="belegnotiz"
                      // wa3: ein Scan bei offener Notiz wird verworfen, nie Text im Feld
                      data-scansperre="Belegnotiz"
                      value={notiz}
                      maxLength={200}
                      autoFocus
                      placeholder="z. B. Tisch 3, ohne Zwiebel"
                      onChange={(e) => {
                        setNotiz(e.target.value);
                      }}
                      {...bt.feld('notiz', { name: 'Belegnotiz', setzen: setNotiz })}
                    />
                    <TastaturKnopf
                      steuerung={bt.knopf('notiz')}
                      klein
                      beschriftung="Tastatur für die Belegnotiz"
                    />
                  </div>
                </div>
              )}
              {modul.tische && tischeOffen && (
                <div className="chips rabatt-chips" role="group" aria-label="Offene Tische">
                  {serverTische.map((t) => (
                    <button
                      key={`s-${t.name}`}
                      type="button"
                      onClick={() => {
                        void tischAufrufen(t.name, 'server');
                      }}
                      title={`${String(t.positionen.length)} Positionen, ${formatiereEuro(summeBrutto(t.positionen))} € · geparkt an ${t.kassenId}${t.bediener === null ? '' : ` von ${t.bediener}`}`}
                    >
                      {t.name} · {formatiereEuro(summeBrutto(t.positionen))} €
                    </button>
                  ))}
                  {tische.map((t) => (
                    <button
                      key={`l-${t.name}`}
                      type="button"
                      onClick={() => {
                        void tischAufrufen(t.name, 'lokal');
                      }}
                      title={`${String(t.positionen.length)} Positionen, ${formatiereEuro(summeBrutto(t.positionen))} € · nur auf diesem Gerät`}
                    >
                      {t.name} · {formatiereEuro(summeBrutto(t.positionen))} € (lokal)
                    </button>
                  ))}
                  {alleTische.length === 0 && (
                    <span className="hinweis">Keine offenen Tische.</span>
                  )}
                </div>
              )}
              {bedienerOffen && bedienerPflicht && (
                <div className="zeile" style={{ alignItems: 'center' }}>
                  <button
                    type="button"
                    className={`taste zweit${anmeldung === null ? ' aktiv' : ''}`}
                    title="Bediener mit PIN anmelden (Backoffice-Verwaltung)"
                    onClick={() => {
                      setAnmeldeFehler(null);
                      setAnmeldeDialog(true);
                    }}
                  >
                    {anmeldung === null ? 'Bediener anmelden' : `Bediener: ${anmeldung.name}`}
                  </button>
                  {anmeldung !== null && (
                    <button type="button" className="taste zweit" onClick={abmelden}>
                      Abmelden
                    </button>
                  )}
                  {anmeldung === null && (
                    <span className="hinweis">Zum Kassieren zuerst anmelden.</span>
                  )}
                </div>
              )}
              {bedienerOffen && !bedienerPflicht && modul.bediener && bediener.liste.length > 0 && (
                <label className="notiz-feld">
                  Bediener
                  <select
                    value={bediener.aktiv ?? ''}
                    onChange={(e) => {
                      bedienerWechseln(e.target.value);
                    }}
                  >
                    <option value="">– kein Bediener –</option>
                    {bediener.liste.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}
          <div className="bon-summen">
            {(rabatt !== null || gutscheinCent > 0 || kundenRabatt !== null) && (
              <dl className="zahlstand">
                {rabatt !== null && (
                  <>
                    <dt>Zwischensumme</dt>
                    <dd>{formatiereEuro(summeBrutto(ohneRabatt(positionenZumZahlen)))} €</dd>
                    <dt>
                      {kundenRabatt === rabatt ? 'Kundenrabatt' : 'Rabatt'} {rabatt} %
                    </dt>
                    <dd className="abzug">
                      −{formatiereEuro(summeBrutto(ohneRabatt(positionenZumZahlen)) - summe)} €
                    </dd>
                  </>
                )}
                {rabatt === null && kundenRabatt !== null && (
                  <>
                    <dt>Kundenrabatt</dt>
                    <dd>{kundenRabatt} % (wird mit dem Artikel gesetzt)</dd>
                  </>
                )}
                {gutscheinCent > 0 && (
                  <>
                    <dt>Summe</dt>
                    <dd>{formatiereEuro(summe)} €</dd>
                    <dt>Bezahlt (Gutschein)</dt>
                    <dd className="abzug">−{formatiereEuro(gutscheinCent)} €</dd>
                  </>
                )}
              </dl>
            )}
            {teilen && (
              <>
                <dl className="zahlstand">
                  <dt>Bon gesamt</dt>
                  <dd>{formatiereEuro(summeBrutto(positionen))} €</dd>
                  <dt>Bleibt im Bon</dt>
                  <dd>{formatiereEuro(summeBrutto(positionen) - summe)} €</dd>
                </dl>
                <p className="hinweis">Teilzahlung – je Zeile „jetzt / im Bon“ wählen</p>
              </>
            )}
            <div className="zu-zahlen">
              <span>{teilen ? 'Jetzt bezahlen' : gutscheinCent > 0 ? 'Offen' : 'Zu zahlen'}</span>
              <strong>{formatiereEuro(rest)} €</strong>
            </div>
          </div>
        </aside>

        <section className="artikel-bereich" aria-label="Artikel">
          <div className="suche-leiste">
            <div className="suche-feld">
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                <path
                  d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM20 20l-4-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
              <input
                type="search"
                data-scanfeld=""
                value={suche}
                placeholder="Artikel suchen oder EAN scannen"
                aria-label="Artikel oder EAN suchen"
                onChange={(e) => {
                  setSuche(e.target.value);
                }}
                {...bt.feld('suche', {
                  name: 'Artikelsuche',
                  setzen: setSuche,
                  // R2: „Suchen ↵“ trifft dieselbe Entscheidung wie Enter (nie ein Scan)
                  bestaetigen: {
                    text: 'Suchen ↵',
                    aktion: () => {
                      sucheBestaetigen(false);
                    },
                  },
                })}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    // R5: erstes Esc schließt nur die Bildschirmtastatur, der Suchtext bleibt
                    if (!istTastaturEsc(e)) {
                      setSuche('');
                    }
                    return;
                  }
                  if (e.key !== 'Enter') {
                    return;
                  }
                  const schnell = schnellesSuchEnter.current === e.nativeEvent;
                  schnellesSuchEnter.current = null;
                  sucheBestaetigen(schnell);
                }}
              />
              {suche !== '' && (
                <button
                  type="button"
                  className="suche-loeschen"
                  aria-label="Suche löschen"
                  onClick={() => {
                    setSuche('');
                  }}
                >
                  ✕
                </button>
              )}
            </div>
            <TastaturKnopf
              steuerung={bt.knopf('suche')}
              beschriftung="Tastatur für die Artikelsuche"
            />
            <button
              type="button"
              className={`taste zweit menge-betrag${ziffernOffen ? ' gedrueckt' : ''}`}
              aria-pressed={ziffernOffen}
              title="Ziffernblock: Stückzahl für die nächste Kachel oder freier Betrag"
              onClick={ziffernUmschalten}
            >
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                <path
                  d="M4 4h4v4H4zM10 4h4v4h-4zM16 4h4v4h-4zM4 10h4v4H4zM10 10h4v4h-4zM16 10h4v4h-4zM4 16h4v4H4zM10 16h4v4h-4zM16 16h4v4h-4z"
                  fill="currentColor"
                />
              </svg>
              Menge / Betrag
              {!ziffernOffen && zifferMenge > 1 && <em className="vormerk">{zifferMenge}×</em>}
            </button>
          </div>

          <WarengruppenLeiste gruppen={konfig.gruppen} aktiv={gruppe} onWahl={setGruppe} />

          <div className="artikel-inhalt">
            <div className="kachel-feld">
              <SchnellwahlKacheln
                konfig={konfig}
                gruppe={gruppe}
                suche={suche}
                imBon={(k) => mengeImBon(positionen, k)}
                onWahl={(k) => {
                  // Vorher getippte Zahl bzw. Mengenzähler = Stückzahl (5, dann „Zucker“ = 5 × Zucker).
                  const menge = vorgabeMenge(zifferCent, zifferMenge);
                  artikelWaehlen(k, menge);
                  setZifferCent(() => 0);
                  setZifferMenge(1);
                }}
              />
            </div>
            {ziffernOffen && (
              // POS V2 Phase 2, Rahmen D8 `17:3252`: Fenster „Menge oder Betrag“
              <div
                className="zahlung-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="menge-betrag-titel"
                tabIndex={-1}
                style={{ outline: 'none' }}
                ref={ziffernFeld}
              >
                <div className="karte dlg menge-betrag-karte">
                  <Ziffernblock
                    onHinzufuegen={(pos) => {
                      hinzufuegen(pos);
                      setZiffernOffen(false);
                    }}
                    cent={zifferCent}
                    setCent={setZifferCent}
                    menge={zifferMenge}
                    setMenge={setZifferMenge}
                    onVormerken={(n) => {
                      setZifferMenge(n);
                      setZifferCent(() => 0);
                      setZiffernOffen(false);
                    }}
                    onAbbrechen={ziffernUmschalten}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="zahl-leiste">
            <span className="zahl-summe" aria-hidden="true">
              {formatiereEuro(rest)} €
            </span>
            {offenerBeleg !== null && (
              <button
                className="taste zweit aktiv"
                title={`Verkauf ${offenerBeleg.belegnummer}: Antwort der Kasse fehlt`}
                onClick={() => void offenenBelegKlaeren()}
              >
                Beleg prüfen
              </button>
            )}
            {offenePlattform !== null && (
              <button
                className="taste zweit aktiv"
                title={`Lieferdienst-Erfassung ${offenePlattform.id}: Antwort fehlt`}
                disabled={laeuft}
                onClick={() => void plattformPruefen()}
              >
                Erfassung prüfen
              </button>
            )}
            {training ? (
              <button
                className="taste gross"
                disabled={!kassierbar || vorgangOffen}
                onClick={() => {
                  void trainingsbeleg();
                }}
              >
                Trainingsbeleg
              </button>
            ) : nurGutschein ? (
              <button
                className="taste gross"
                disabled={!kassierbar || vorgangOffen}
                onClick={() => {
                  void gutscheinKassieren();
                }}
              >
                Abschließen (Gutschein)
              </button>
            ) : (
              <>
                {modul.gutscheine && (
                  <button
                    className="taste zweit zahl-gutschein"
                    disabled={positionen.length === 0 || laeuft || !navigator.onLine || summe <= 0}
                    title={navigator.onLine ? undefined : 'Gutscheine sind offline nicht einlösbar'}
                    onClick={() => {
                      setGutscheinDialog('einloesen');
                    }}
                  >
                    <FunktionsSymbol icon="gutschein" groesse={22} />
                    Gutschein
                  </button>
                )}
                {modul.lieferdienste && (
                  <div className="weitere-bereich">
                    <button
                      type="button"
                      className="taste zweit weitere"
                      aria-label="Weitere Zahlarten"
                      aria-haspopup="menu"
                      aria-expanded={weitereOffen}
                      onClick={() => {
                        setWeitereOffen(!weitereOffen);
                      }}
                    >
                      ⋯
                    </button>
                    {weitereOffen && (
                      <div className="weitere-menue" role="menu" aria-label="Weitere Zahlarten">
                        <button
                          type="button"
                          role="menuitem"
                          disabled={
                            !kassierbar ||
                            vorgangOffen ||
                            !navigator.onLine ||
                            mitGutschein ||
                            gutscheinStand.ausgaben.length > 0
                          }
                          title="Bei Lieferando, Foodora oder im Webshop bereits online bezahlt – kein Beleg"
                          onClick={() => {
                            setWeitereOffen(false);
                            setPlattformMeldung(null);
                            setPlattformDialog(true);
                          }}
                        >
                          <FunktionsSymbol icon="online" />
                          <span>Online bezahlt</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {(offeneZahlung !== null || startUnklarSeit !== null) && (
                  <button
                    className="taste zweit aktiv"
                    title={
                      offeneZahlung === null
                        ? 'Kartenzahlung gestartet, Antwort fehlt'
                        : `Kartenzahlung ${offeneZahlung.zahlungsreferenz}: Ergebnis offen`
                    }
                    disabled={pruefungLaeuft}
                    aria-busy={pruefungLaeuft}
                    onClick={() => void offeneZahlungPruefen()}
                  >
                    Zahlung prüfen
                  </button>
                )}
                {offeneZahlung?.status === 'bezahlt' && offenerBeleg === null && (
                  <button
                    className="taste zweit"
                    title="Bezahlt, aber kein Beleg möglich – Zahlung vollständig rückerstatten"
                    onClick={() => void bezahlteZahlungErstatten()}
                  >
                    Zahlung rückerstatten
                  </button>
                )}
                <button
                  className="taste gross bar"
                  disabled={!kassierbar || vorgangOffen}
                  title={vorgangOffen ? 'Erst den offenen Vorgang prüfen' : undefined}
                  onClick={() => {
                    setBarDialog(true);
                  }}
                >
                  <FunktionsSymbol icon="bar" groesse={26} />
                  {mitGutschein ? `Rest bar ${formatiereEuro(rest)} €` : 'Bar'}
                  <small className="kuerzel">F2</small>
                </button>
                {modul.kartenzahlung && (
                  <button
                    className="taste gross karte"
                    disabled={!kassierbar || !navigator.onLine || vorgangOffen}
                    title={
                      vorgangOffen
                        ? 'Erst den offenen Vorgang prüfen'
                        : navigator.onLine
                          ? undefined
                          : 'Kartenzahlung ist offline nicht möglich'
                    }
                    onClick={karteWaehlen}
                  >
                    <FunktionsSymbol icon="karte" groesse={26} />
                    {mitGutschein ? `Rest Karte ${formatiereEuro(rest)} €` : 'Karte'}
                    <small className="kuerzel">F3</small>
                  </button>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      {anmeldeDialog && (
        <BedienerAnmeldung
          bediener={verwaltung.bediener}
          laeuft={anmeldeLaeuft}
          fehler={anmeldeFehler}
          onAnmelden={anmelden}
          onAbbrechen={() => {
            setAnmeldeDialog(false);
          }}
        />
      )}
      {scanAuswahl !== null && (
        <ScanAuswahlDialog
          code={scanAuswahl.code}
          treffer={scanAuswahl.treffer}
          gesperrt={scanAuswahl.gesperrt}
          onWahl={(k) => {
            setScanAuswahl(null);
            if (k === null) {
              return;
            }
            const sperre = scanSperre(scanZustand);
            if (sperre === null) {
              scanUebernehmen(k);
            } else {
              setFehler(gesperrterScan(scanAuswahl.code, sperre));
            }
          }}
        />
      )}
      {wiegeAnfrage !== null && (
        <Gewichtsdialog
          // neuer Artikel = neue Eingabe (kein Gewicht des vorigen übernehmen)
          key={`${String(wiegeAnfrage.kachel.artikelId ?? '')}-${wiegeAnfrage.kachel.bezeichnung}`}
          anfrage={wiegeAnfrage}
          onHinzufuegen={(p) => {
            wiegeAnfrageSchliessen();
            hinzufuegen(p);
          }}
          onAbbrechen={wiegeAnfrageSchliessen}
        />
      )}
      {pfandDialog !== null && (
        <PfandDialog
          leergut={pfandDialog === 'leergut'}
          onFertig={(p) => {
            setPfandDialog(null);
            if (p !== null) {
              hinzufuegen(p);
            }
          }}
        />
      )}
      {plattformDialog && (
        <PlattformDialog
          summeCent={summe}
          laeuft={laeuft}
          onErfassen={(a) => void plattformErfassen(a)}
          onAbbrechen={() => {
            setPlattformDialog(false);
          }}
        />
      )}
      {barDialog && (
        <BarDialog
          kassenId={kassenId}
          info={[
            empfaengerEingabe.name.trim(),
            `${String(ohneRabatt(positionenZumZahlen).length)} Position(en)`,
          ]
            .filter((t) => t !== '')
            .join(' · ')}
          summeCent={offenerBeleg === null ? rest : summeBrutto(offenerBeleg.positionen)}
          laeuft={laeuft}
          ungeklaert={offenerBeleg?.belegnummer ?? null}
          onBelegPruefen={() => {
            setBarDialog(false);
            void offenenBelegKlaeren();
          }}
          onKassieren={(gegeben) => {
            void barKassieren(gegeben);
          }}
          onAbbrechen={() => {
            setBarDialog(false);
          }}
        />
      )}
      {bezahlFrage !== null && (
        <div
          ref={bezahlDialog}
          className="zahlung-dialog"
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          style={{ outline: 'none' }}
        >
          <div className="karte bar-dialog">
            <h2>Verkauf noch ungeklärt</h2>
            <p className="warnung" role="alert">
              Verkauf noch ungeklärt. Bitte diesen Einkauf nicht erneut kassieren. Zuerst
              Belegstatus prüfen.
            </p>
            <p>
              Verkauf <strong>{bezahlFrage.belegnummer}</strong> über{' '}
              {formatiereEuro(bezahlFrage.summeCent)} € ist an der Kasse{' '}
              {bezahlFrage.ohneVerbindung
                ? 'nicht übertragen worden (keine Verbindung).'
                : 'nicht gebucht (die Antwort fehlte).'}{' '}
              Wurde der Betrag vom Kunden bezahlt?
              {bezahlFrage.offline === true &&
                ' Mit „Ja“ wird er offline gebucht (vorläufiger Beleg, Nachsignierung folgt).'}
            </p>
            <div className="zeile" style={{ justifyContent: 'center' }}>
              <button
                className="taste gross"
                disabled={laeuft}
                onClick={() => {
                  void bezahlEntscheiden(true);
                }}
              >
                {bezahlFrage.offline === true
                  ? 'Ja, bezahlt – offline buchen'
                  : 'Ja, bezahlt – Verkauf buchen'}
              </button>
              <button
                className="taste zweit"
                disabled={laeuft}
                onClick={() => {
                  void bezahlEntscheiden(false);
                }}
              >
                {bezahlFrage.abgelehnt === true
                  ? 'Nein, Betrag zurückgegeben – nicht buchen'
                  : 'Nein, nicht bezahlt – nicht buchen'}
              </button>
              <button
                className="taste zweit"
                disabled={laeuft}
                onClick={() => {
                  setBezahlFrage(null);
                }}
              >
                Später prüfen
              </button>
            </div>
          </div>
        </div>
      )}
      {tischplanOffen && tischplan !== null && (
        <TischplanAnsicht
          plan={tischplan.plan}
          raster={tischplan.raster}
          belegung={belegung}
          aktiverTisch={aktiverTisch}
          onWahl={(name) => void tischGewaehlt(name)}
          onSchliessen={() => {
            setTischplanOffen(false);
          }}
        />
      )}
      {gutscheinDialog === 'verkauf' && <GutscheinVerkaufDialog onFertig={gutscheinVerkaufen} />}
      {gutscheinDialog === 'einloesen' && (
        <GutscheinEinloesenDialog
          api={api}
          kassenId={kassenId}
          offenCent={rest}
          {...(scanCode === null ? {} : { startCode: scanCode })}
          onFertig={gutscheinEinloesen}
          einloesungen={gutscheinStand.einloesungen}
          onEntfernen={einloesungEntfernen}
        />
      )}
      {rueckfrage !== null && (
        <Rueckfrage
          titel={rueckfrage.titel}
          icon={rueckfrage.icon}
          text={rueckfrage.text}
          zurueck={rueckfrage.zurueck}
          bestaetigen={rueckfrage.bestaetigen}
          onZurueck={() => {
            setRueckfrage(null);
          }}
          onBestaetigen={() => {
            const aktion = rueckfrage.aktion;
            setRueckfrage(null);
            aktion();
          }}
        />
      )}
      {trinkgeldFrage && (
        // POS V2 Phase 2, Rahmen D3 v2 `25:5168`: „Kein Trinkgeld“ vorne mit Fokus (Enter), ohne
        // Hervorhebung; „Anderer Betrag …“ mit Ziffernblock statt Browser-Eingabe (N9)
        <div
          ref={trinkgeldDialog}
          className="zahlung-dialog"
          role="dialog"
          aria-modal="true"
          aria-label="Trinkgeld"
          tabIndex={-1}
          style={{ outline: 'none' }}
        >
          <div className="karte dlg trinkgeld-dialog">
            <div className="dlg-kopf">
              <h2>Trinkgeld?</h2>
              <span className="dlg-meta">Karte · {formatiereEuro(rest)} €</span>
            </div>
            <div className="dlg-info">
              <p>
                <strong>Betrag {formatiereEuro(rest)} €</strong>
              </p>
              <p className="hinweis">
                Das Trinkgeld kommt auf den Terminalbetrag, nicht auf den Beleg (kein Umsatz).
              </p>
            </div>
            {trinkgeldFrei === null ? (
              <>
                <div className="dlg-wahl">
                  {trinkgeldVorschlaege(rest).map((v, i) => (
                    <button
                      key={v.trinkgeldCent}
                      className="taste zweit"
                      autoFocus={i === 0}
                      {...(i === 0 ? { 'data-kuerzel': 'Enter' } : {})}
                      onClick={() => {
                        void karteStarten(v.trinkgeldCent);
                      }}
                    >
                      {v.text}
                    </button>
                  ))}
                </div>
                <div className="dlg-fuss">
                  <button
                    className="taste zweit dlg-leise"
                    onClick={() => {
                      setTrinkgeldFrei(0);
                    }}
                  >
                    Anderer Betrag …
                  </button>
                  <button
                    className="taste zweit"
                    data-kuerzel="Esc"
                    onClick={() => {
                      setTrinkgeldFrage(false);
                    }}
                  >
                    Abbrechen
                  </button>
                </div>
              </>
            ) : (
              <div
                ref={trinkgeldFreiFeld}
                className="trinkgeld-frei"
                tabIndex={-1}
                style={{ outline: 'none' }}
                onKeyDown={(e) => {
                  // Enter bestätigt wie früher der Browser-Dialog – auf dem Feld und auf den
                  // Zifferntasten; „Zurück“ und „zahlen“ behalten ihre eigene Enter-Aktivierung.
                  const ziel = e.target as Element;
                  if (
                    e.key === 'Enter' &&
                    (ziel === e.currentTarget || ziel.closest('.ziffern-tasten') !== null)
                  ) {
                    e.preventDefault();
                    if (!e.repeat) void karteStarten(trinkgeldFrei);
                    return;
                  }
                  const t = tastaturZuZiffer(e.key);
                  if (t !== null && !(e.target instanceof HTMLInputElement)) {
                    e.preventDefault();
                    setTrinkgeldFrei((c) => ziffernTaste(c ?? 0, t));
                  }
                }}
              >
                <label className="dlg-feldname" htmlFor="trinkgeld-anzeige">
                  Trinkgeld (Eingabe von rechts: 1 5 0 → 1,50 €)
                </label>
                <output id="trinkgeld-anzeige" className="ziffern-anzeige" aria-live="polite">
                  {formatiereEuro(trinkgeldFrei)} €
                </output>
                <ZiffernTasten
                  beschriftung="Trinkgeld"
                  onTaste={(t) => {
                    setTrinkgeldFrei((c) => ziffernTaste(c ?? 0, t));
                  }}
                />
                <div className="dlg-fuss">
                  <button
                    className="taste zweit"
                    onClick={() => {
                      setTrinkgeldFrei(null);
                    }}
                  >
                    Zurück
                  </button>
                  <button
                    className="taste"
                    onClick={() => {
                      void karteStarten(trinkgeldFrei);
                    }}
                  >
                    Mit {formatiereEuro(trinkgeldFrei)} € Trinkgeld zahlen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {zahlung !== null && (
        <ZahlungDialog
          api={api}
          kassenId={kassenId}
          zahlung={zahlung}
          onFertig={(z) => {
            void zahlungAbgeschlossen(z);
          }}
        />
      )}
      {kartenStatus !== null && (
        <KartenStatusV2
          art={kartenStatus}
          betragCent={
            (kartenStatus === 'beleg' ||
              kartenStatus === 'abgelehnt' ||
              kartenStatus === 'abgebrochen') &&
            letzteKarte !== null
              ? letzteKarte.betragCent
              : rest
          }
          info={
            (kartenStatus === 'beleg' ||
              kartenStatus === 'abgelehnt' ||
              kartenStatus === 'abgebrochen') &&
            letzteKarte !== null
              ? `${letzteKarte.anbieter === 'zvt' ? 'Terminal im Laden' : letzteKarte.anbieter} · Ref. ${letzteKarte.zahlungsreferenz}`
              : undefined
          }
          // Gleiche Bedingungen und Aufrufe wie „Karte“ bzw. „Bar“ in der Zahlleiste
          karte={{
            sichtbar: modul.kartenzahlung && !training && !nurGutschein,
            bedienbar: kassierbar && navigator.onLine && !vorgangOffen,
            onKlick: () => {
              setKartenErgebnisZu(true);
              karteWaehlen();
            },
          }}
          bar={{
            sichtbar: !training && !nurGutschein,
            bedienbar: kassierbar && !vorgangOffen,
            onKlick: () => {
              setKartenErgebnisZu(true);
              setBarDialog(true);
            },
          }}
          onPruefen={() => {
            setStartUnklarZu(true);
            void offeneZahlungPruefen();
          }}
          onSchliessen={() => {
            if (kartenStatus === 'start_unklar') {
              setStartUnklarZu(true);
            } else {
              setKartenErgebnisZu(true);
            }
          }}
        />
      )}
      {erstattung !== null && (
        <ZahlungDialog
          api={api}
          kassenId={kassenId}
          zahlung={erstattung.zahlung}
          titel="Rückerstattung"
          onFertig={(r) => {
            erstattungAbgeschlossen(erstattung.ursprung, r);
          }}
        />
      )}
    </>
  );
}
